/**
 * RESTORATIVE ALIGNMENT PILLOW - Enhanced PDP Landing Page
 * 
 * UI Refinements:
 * - Pillow images fill available space
 * - Redesigned pillowcase bonus as adjacent glued box
 * - Emotional testimonials with human touches
 * - New "cut the middlemen" savings section with comparison bar graphs
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { 
  Star, Check, Moon, Thermometer, Shield, MapPin, ChevronDown, ChevronUp, BadgeCheck,
  Clock, Sparkles, Droplets, Wind, RefreshCw, Layers, Gift, Store, Truck, Tag, X,
  ChevronLeft, ChevronRight, Heart, Bed
} from "lucide-react";
import FluffLogo from "@/components/FluffLogo";
import PressLogos from "@/components/PressLogos";
import OrderPopup from "@/components/OrderPopup";
import { StockInventoryBar } from "@/components/StockInventoryBar";
import { EventClosedPage } from "@/components/EventClosedPage";
import { ProfileResultsBanner } from '@/components/ProfileResultsBanner';
import { QuizMiniSection } from '@/components/QuizMiniSection';
import { QuizModal } from '@/components/QuizModal';
import { trackViewContent, getProductDataForTracking, trackAddToCart } from "@/lib/tracking";
import { useSharedCountdown } from "@/hooks/useSharedCountdown";
import { trackViewItem, createMainProductItem, trackAddToCart as trackGA4AddToCart } from "@/lib/ga4-tracking";
import { CustomerCount } from "@/components/CustomerCount";
import { ShippingMessage } from "@/components/ShippingMessage";
import { useGeolocation, isUSVisitor, useNonUsBadgeText, useNonUsAlternativeUsp } from "@/hooks/useGeolocation";
import { useLocalizedTestimonials, useLocalizedLocation } from "@/hooks/useLocalizedTestimonials";
import { useLocaleTexts } from "@/hooks/useLocaleTexts";
import { AnimatedPlanet } from "@/components/AnimatedPlanet";
import { useLocalePricing } from "@/hooks/useLocalePricing";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useDefaultCta } from "@/hooks/useDefaultCta";
import { trpc } from "@/lib/trpc";
import { BlurImage } from "@/components/BlurImage";
import { ImageCarousel, type CarouselImage } from "@/components/ImageCarousel";
import { LazyVideo } from "@/components/LazyVideo";
import ExpertPillowSlide, { ExpertPillowThumbnail } from "@/components/ExpertPillowSlide";
import CoupleSleepSlide, { CoupleSleepThumbnail } from "@/components/CoupleSleepSlide";
import DeliverySlide, { DeliveryThumbnail } from "@/components/DeliverySlide";
import SleepCompanionSlide, { SleepCompanionThumbnail } from "@/components/SleepCompanionSlide";
import EthicalMakingSlide, { EthicalMakingThumbnail } from "@/components/EthicalMakingSlide";
import VialArgumentsSlide, { VialArgumentsThumbnail } from "@/components/VialArgumentsSlide";
import VideoSlide, { VideoSlideThumbnail } from "@/components/VideoSlide";
import ScienceResultsSlide, { ScienceResultsThumbnail } from "@/components/ScienceResultsSlide";
import IngredientsSlide, { IngredientsThumbnail } from "@/components/IngredientsSlide";
import { StepsGallerySlide, StepsGalleryThumbnail } from "@/components/StepsGallerySlide";
import TestimonialsGallerySlide, { TestimonialsGalleryThumbnail } from "@/components/TestimonialsGallerySlide";
import { AwardBadges } from "@/components/gallery/GalleryOverlays";
import { DermatologicallyTestedBadge } from "@/components/DermatologicallyTestedBadge";
import { ProductTabs } from "@/components/ProductTabs";


import { BlackFridayUrgencySection } from "@/components/BlackFridayUrgencySection";

/**
 * Helper function to convert PNG path to WebP with fallback
 * Returns WebP path if browser supports it, otherwise PNG
 */
const supportsWebP = typeof window !== 'undefined' && 
  document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0;

const toWebP = (pngPath: string): string => {
  if (!supportsWebP) return pngPath;
  // Only convert optimized PNG images
  if (!pngPath.includes('/optimized/') || !pngPath.endsWith('.png')) return pngPath;
  return pngPath.replace('.png', '.webp');
};


/**
 * Comparison Slide Component - Fourth gallery image
 * Recreates the "Ruined Sleep vs VIP Slumber" comparison using page design system
 * Adapted for "Restorative Alignment" narrative targeting 45+ audience
 */
function ComparisonSlide() {
  const negativePoints = [
    { emoji: "🧴", text: "Surface-only application" },
    { emoji: "⏳", text: "6+ months for minimal results" },
    { emoji: "💊", text: "Prescription side effects" },
    { emoji: "💸", text: "$150+/month recurring cost" },
    { emoji: "🔬", text: "No clinical proof of efficacy" },
    { emoji: "🚫", text: "Hair loss resumes when stopped" }
  ];
  
  const positivePoints = [
    { emoji: "💉", text: "Deep micro-needle delivery" },
    { emoji: "⚡", text: "Visible results in 4 weeks" },
    { emoji: "🌿", text: "Zero side effects, no Rx" },
    { emoji: "💰", text: "From $23/month" },
    { emoji: "📊", text: "93% clinical success rate" },
    { emoji: "🔒", text: "Lasting results after treatment" }
  ];

  return (
    <div className="w-full h-full flex overflow-hidden relative">
      {/* Left Side - Others */}
      <div className="w-1/2 h-full bg-[#f0f0f0] flex flex-col relative">
        <div className="p-3 lg:p-5 flex flex-col h-full">
          {/* Header with generic icon */}
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-xs lg:text-sm">🧴</span>
            </div>
            <div>
              <h3 className="text-sm lg:text-base font-bold text-[#1a1a1a] leading-tight">Others</h3>
              <p className="text-[8px] lg:text-[10px] text-gray-400">Generic Treatments</p>
            </div>
          </div>
          
          {/* Negative Points */}
          <div className="space-y-1.5 lg:space-y-2 mt-2">
            {negativePoints.map((point, idx) => (
              <div key={idx} className="flex items-center gap-1.5 lg:gap-2">
                <span className="text-xs lg:text-sm flex-shrink-0">{point.emoji}</span>
                <span className="text-[9px] lg:text-xs text-[#666] leading-tight">{point.text}</span>
              </div>
            ))}
          </div>
          
          {/* Bottom verdict */}
          <div className="mt-auto pt-2">
            <div className="bg-red-100 rounded-lg px-2 py-1 text-center">
              <span className="text-[8px] lg:text-[10px] font-bold text-red-600">👎 NOT RECOMMENDED</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* VS Divider */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white flex items-center justify-center shadow-lg border-2 border-gray-200">
          <span className="text-[9px] lg:text-xs font-black text-[#1a1a1a]">VS</span>
        </div>
      </div>
      
      {/* Right Side - Colagea */}
      <div className="w-1/2 h-full bg-[#0a1628] flex flex-col relative">
        <div className="p-3 lg:p-5 flex flex-col h-full">
          {/* Header with Colagea icon */}
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-[#0066CC] flex items-center justify-center">
              <span className="text-xs lg:text-sm">💎</span>
            </div>
            <div>
              <h3 className="text-sm lg:text-base font-bold text-white leading-tight">Colagea</h3>
              <p className="text-[8px] lg:text-[10px] text-white/40">Micro-Infusion System</p>
            </div>
          </div>
          
          {/* Positive Points */}
          <div className="space-y-1.5 lg:space-y-2 mt-2">
            {positivePoints.map((point, idx) => (
              <div key={idx} className="flex items-center gap-1.5 lg:gap-2">
                <span className="text-xs lg:text-sm flex-shrink-0">{point.emoji}</span>
                <span className="text-[9px] lg:text-xs text-white/80 leading-tight">{point.text}</span>
              </div>
            ))}
          </div>
          
          {/* Bottom verdict */}
          <div className="mt-auto pt-2">
            <div className="bg-[#0066CC]/30 rounded-lg px-2 py-1 text-center">
              <span className="text-[8px] lg:text-[10px] font-bold text-[#4da6ff]">👍 RECOMMENDED BY EXPERTS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hotel Comparison Slide Component - Compares FluffCo vs luxury hotel brands
 * Shows price, savings, and feature comparison table
 * Logos rotate with fade in/out animation across all columns
 */
function HotelComparisonSlide({ fluffPrice = "$69.90" }: { fluffPrice?: string; hotelPriceRange?: string }) {
  const features = [
    { name: "Delivery Method", colagea: "Micro-Needle", others: "Surface Only" },
    { name: "Patented Formula", colagea: true, others: false },
    { name: "Clinical Efficacy", colagea: "93%", others: "~30%" },
    { name: "Time to Results", colagea: "4 Weeks", others: "6+ Months" },
    { name: "Side Effects", colagea: "None", others: "Common" },
    { name: "Prescription Needed", colagea: false, others: true },
    { name: "DHT Blocker Free", colagea: true, others: false },
    { name: "Price / Month", colagea: fluffPrice, others: "$150+" },
    { name: "60-Day Guarantee", colagea: true, others: false },
  ];

  return (
    <div className="w-full h-full bg-[#0a1628] flex flex-col p-3 lg:p-5 relative overflow-hidden">
      {/* Title */}
      <div className="text-center mb-2 lg:mb-3 pt-1">
        <h3 className="text-white text-xs lg:text-sm font-bold uppercase tracking-wider">Colagea vs. Others</h3>
        <p className="text-white/30 text-[7px] lg:text-[8px] mt-0.5">See why 93% of users switch to Colagea</p>
      </div>
      
      {/* Comparison table */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        {/* Headers */}
        <div className="flex mb-1.5 lg:mb-2">
          <div className="w-[35%] lg:w-[38%] flex-shrink-0" />
          <div className="flex-1 flex items-center justify-center">
            <div className="bg-white rounded-lg px-2 py-1 lg:px-3 lg:py-1.5 shadow-lg">
              <FluffLogo className="h-4 lg:h-6 w-auto max-w-[55px] lg:max-w-[70px] object-contain" />
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-white/50 text-[8px] lg:text-[10px] font-medium">Generic Brands</span>
          </div>
        </div>
        
        {/* Rows */}
        {features.map((feature, idx) => (
          <div 
            key={idx} 
            className={`flex items-center py-1 lg:py-1.5 ${idx < features.length - 1 ? 'border-b border-white/10' : ''}`}
            style={{ animation: `fadeSlideIn 0.3s ease-out ${0.1 + idx * 0.06}s both` }}
          >
            <div className="w-[35%] lg:w-[38%] flex-shrink-0 text-[8px] lg:text-[10px] font-medium text-white/80 pr-2">
              {feature.name}
            </div>
            <div className="flex-1 flex items-center justify-center">
              {feature.colagea === true ? (
                <div className="w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-[#0066CC] flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-white" />
                </div>
              ) : feature.colagea === false ? (
                <span className="text-[8px] lg:text-[10px] font-medium text-[#4da6ff]">No</span>
              ) : (
                <span className="text-[9px] lg:text-[11px] font-bold text-[#4da6ff]">{feature.colagea}</span>
              )}
            </div>
            <div className="flex-1 flex items-center justify-center">
              {feature.others === false ? (
                <div className="w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-red-500/30 flex items-center justify-center">
                  <X className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-red-400" />
                </div>
              ) : feature.others === true ? (
                <span className="text-[8px] lg:text-[10px] text-red-400">Yes</span>
              ) : (
                <span className="text-[9px] lg:text-[11px] text-white/50">{feature.others}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom CTA hint */}
      <div className="text-center mt-1.5 lg:mt-2 pb-1">
        <span className="text-[7px] lg:text-[9px] text-white/40">Based on clinical trial data and market analysis</span>
      </div>
    </div>
  );
}

/**
 * Hotel Comparison Thumbnail - Shows mini table preview
 */
function HotelComparisonThumbnail() {
  return (
    <div className="w-full h-full bg-[#0a1628] flex flex-col items-center justify-center p-1">
      <div className="text-[5px] font-bold text-white/80 uppercase tracking-wider mb-1">Colagea vs Others</div>
      <div className="flex gap-1 mb-0.5">
        <div className="w-2 h-2 rounded-full bg-[#0066CC] flex items-center justify-center">
          <Check className="w-1.5 h-1.5 text-white" />
        </div>
        <div className="w-2 h-2 rounded-full bg-red-500/30 flex items-center justify-center">
          <X className="w-1.5 h-1.5 text-red-400" />
        </div>
      </div>
      <div className="flex gap-0.5">
        {[1,2,3].map(i => <div key={i} className="w-4 h-[1px] bg-white/20" />)}
      </div>
    </div>
  );
}

/**
 * Comparison Thumbnail - Shows mini version of comparison slide with X vs Check icons
 */
function ComparisonThumbnail() {
  return (
    <div className="w-full h-full flex relative">
      {/* Left side - light with thumbs down */}
      <div className="w-1/2 h-full bg-[#f0f0f0] flex flex-col items-center justify-center gap-0.5">
        <span className="text-xl leading-none">👎</span>
        <span className="text-[5px] font-bold text-gray-500 uppercase tracking-wider">Others</span>
      </div>
      {/* Right side - dark navy with thumbs up */}
      <div className="w-1/2 h-full bg-[#0a1628] flex flex-col items-center justify-center gap-0.5">
        <span className="text-xl leading-none">👍</span>
        <span className="text-[5px] font-bold text-white/80 uppercase tracking-wider">Colagea</span>
      </div>
      {/* VS badge in center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-md border border-gray-200">
        <span className="text-[5px] font-black text-[#1a1a1a]">VS</span>
      </div>
    </div>
  );
}

/**
 * Second Image Thumbnail - Shows preview with subtle overlay indicator
 */
function SecondImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/sbJxxEWpfZlzjeLo.png")} 
        alt="" 
        className="w-full h-full object-cover"
        decoding="async"
      />
      {/* Subtle corner indicator showing this image has overlays */}
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">why</span>
      </div>
    </div>
  );
}

/**
 * Third Image Thumbnail - Shows preview with "3 layers" indicator (same style as 2nd)
 */
function ThirdImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_3ea51b01.png")} 
        alt="" 
        className="w-full h-full object-cover"
        decoding="async"
      />
      {/* Subtle corner indicator showing this image has overlays - same style as 2nd */}
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">3 layers</span>
      </div>
    </div>
  );
}

/**
 * First Image Thumbnail - Shows "5-star" indicator
 */
function FirstImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("/images/optimized/cards/pillow-hero.png")} 
        alt="" 
        className="w-full h-full object-cover"
        decoding="async"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">5-star</span>
      </div>
    </div>
  );
}

/**
 * Fifth Image Thumbnail - Shows "3 care" indicator (washing machine)
 */
function FifthImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/RVxCLjqishtuSISc.png")} 
        alt="" 
        className="w-full h-full object-cover"
        decoding="async"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">care</span>
      </div>
    </div>
  );
}

/**
 * Sixth Image Thumbnail - Shows "hotel" indicator (packaging)
 */
function SixthImageThumbnail() {
  return (
    <div className="w-full h-full relative">
      <img 
        src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging_0d931499.png")} 
        alt="" 
        className="w-full h-full object-cover"
        decoding="async"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">hotel</span>
      </div>
    </div>
  );
}

import type { LandingPageConfig } from '@/config/types';
import { useLandingPageConfig } from '@/contexts/LandingPageContext';
import { baseConfig } from '@/config/base.config';
import TrustpilotStars from "@/components/TrustpilotStars";

interface HomeProps {
  configOverride?: LandingPageConfig;
  // Allow any additional props from wouter routing
  [key: string]: any;
}

export default function Home(props: HomeProps) {
  const { configOverride } = props || {};
  
  // Get config from context or use override/default
  const contextConfig = useLandingPageConfig();
  const config = configOverride || contextConfig || baseConfig;
  
  // Get site settings from database
  const { settings: siteSettings, getAngleCTA } = useSiteSettings();
  
  // Get default CTA button from database
  const { cta: defaultCta } = useDefaultCta();
  
  // Badge display: Dermatologically Tested badge on all pages
  const badgeVariant = config.id === 'down-alternative' ? 'both_badges' : 'control';
  
  // Get landing page settings for this specific page
  const { data: landingPageSettings } = trpc.landingPages.get.useQuery({ pageId: config.id });
  console.log('[DEBUG] landingPageSettings for', config.id, ':', JSON.stringify(landingPageSettings, null, 2));
  
  // Get all CTA buttons to resolve ctaButtonId
  const { data: allCtaButtons } = trpc.ctaButtons.getAll.useQuery();
  
  // Helper function to get CTA button group with priority logic:
  // Returns object with primary and secondary text
  function getPageCTAGroup(): { primary: string; secondary: string } {
    console.log('[DEBUG getPageCTAGroup] landingPageSettings FULL:', JSON.stringify(landingPageSettings, null, 2));
    console.log('[DEBUG getPageCTAGroup] landingPageSettings.ctaButtonId:', landingPageSettings?.ctaButtonId);
    console.log('[DEBUG getPageCTAGroup] allCtaButtons:', allCtaButtons);
    // Priority 1: Selected CTA button for this page
    if (landingPageSettings?.ctaButtonId && allCtaButtons) {
      console.log('[DEBUG] Looking for button with ID:', landingPageSettings.ctaButtonId);
      console.log('[DEBUG] Available button IDs:', allCtaButtons.map((btn: any) => btn.id));
      const selectedButton = allCtaButtons.find((btn: any) => btn.id === landingPageSettings.ctaButtonId);
      console.log('[DEBUG] selectedButton found:', selectedButton);
      if (selectedButton) {
        return {
          primary: selectedButton.text || "Order Now",
          secondary: selectedButton.secondaryText || "& Save {discount}%"
        };
      }
    }
    
    // Priority 2: Default CTA button
    if (defaultCta.text) {
      return {
        primary: defaultCta.text,
        secondary: (defaultCta as any).secondaryText || "& Save {discount}%"
      };
    }
    
    // Priority 3: Hardcoded fallback
    return {
      primary: "Order Now",
      secondary: "& Save {discount}%"
    };
  };
  
  // Helper to get simple primary CTA text (backward compatibility)
  const getPageCTA = (isMobile: boolean = false): string => {
    const primaryText = getPageCTAGroup().primary;
    
    // Use shorter text on mobile to prevent overflow
    if (isMobile && primaryText === "End Your Pillow Battle") {
      return "Claim Deal";
    }
    
    return primaryText;
  };
  
  // Helper to get CTA with discount (uses secondary text template)
  const getPageCTAWithDiscount = (discount: number): string => {
    const group = getPageCTAGroup();
    
    // If primary text already contains a percentage or "Off", it's a complete CTA - don't append secondary
    if (group.primary.includes('%') || group.primary.toLowerCase().includes('off')) {
      return group.primary;
    }
    
    // If secondary text has {discount} placeholder, replace it
    if (group.secondary.includes('{discount}')) {
      return `${group.primary} ${group.secondary.replace('{discount}', discount.toString())}`;
    }
    // Otherwise just append the secondary text
    return `${group.primary} ${group.secondary}`;
  };
  
  // Use config defaults for size and quantity
  const defaultSize = config.defaultSize === 'king' ? 'King' : 'Standard';
  const defaultQuantity = config.defaultQuantity || 4;
  const quantityOptions = config.quantityOptions || [1, 2, 4];
  
  // Read ?qty= URL param to pre-select quantity (e.g., from compare page CTA)
  const qtyFromUrl = (() => {
    const params = new URLSearchParams(window.location.search);
    const qtyParam = params.get('qty');
    if (qtyParam) {
      const parsed = parseInt(qtyParam, 10);
      if ([1, 2, 4].includes(parsed) && quantityOptions.includes(parsed)) return parsed;
    }
    return null;
  })();
  
  const [selectedSize, setSelectedSize] = useState<"Standard" | "King">(defaultSize);
  const [selectedQuantity, setSelectedQuantity] = useState(qtyFromUrl || defaultQuantity);
  const [hoveredQuantity, setHoveredQuantity] = useState<number | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [viewerCount, setViewerCount] = useState(47);
  const [displayedViewerCount, setDisplayedViewerCount] = useState(47);
  const [isViewerCountAnimating, setIsViewerCountAnimating] = useState(false);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  const [showAllBenefitCards, setShowAllBenefitCards] = useState(false);
  const [pricingSectionSize, setPricingSectionSize] = useState<"Standard" | "King">(defaultSize);
  const [savingsQuantity, setSavingsQuantity] = useState<1 | 2 | 4>(4);
  const [isPricingSectionAnimating, setIsPricingSectionAnimating] = useState(false);
  const [prevPricingSectionSize, setPrevPricingSectionSize] = useState<"Standard" | "King">("Standard");
  const [expertGalleryIndex, setExpertGalleryIndex] = useState(0);
  const [expandedExpertAccordion, setExpandedExpertAccordion] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(true); // Track if current gallery image is loaded
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());
  
  // Haptic feedback utility for mobile devices
  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      // Light tap feedback (10ms)
      navigator.vibrate(10);
    }
  };
  
  // Gallery carousel ref for programmatic scrolling
  const galleryScrollRef = useRef<HTMLDivElement | null>(null);
  const [awardIndex, setAwardIndex] = useState(0); // Award carousel index
  const [currentToast, setCurrentToast] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showWhatsIncluded, setShowWhatsIncluded] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [stickyBarExpanded, setStickyBarExpanded] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [purchasedToday, setPurchasedToday] = useState(0);
  const [displayedPurchasedToday, setDisplayedPurchasedToday] = useState(0);
  const [isPurchasedAnimating, setIsPurchasedAnimating] = useState(false);
  const [isHeroPriceAnimating, setIsHeroPriceAnimating] = useState(false);
  const [prevPrice, setPrevPrice] = useState<number | null>(null);
  const [stockLevel, setStockLevel] = useState(13); // Starts at 13%, decreases 1% per minute, min 7%
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [oekoTexExpanded, setOekoTexExpanded] = useState(false);
  
  // Quiz results state - for showing ProfileResultsBanner after quiz completion
  const [quizResultProfileId, setQuizResultProfileId] = useState<string | null>(null);
  const [quizResultTags, setQuizResultTags] = useState<string[]>([]);
  const [quizResultLabels, setQuizResultLabels] = useState<string[]>([]);
  const [quizResultCureQuantity, setQuizResultCureQuantity] = useState<1 | 2 | 4>(2);
  const oekoTexRef = useRef<HTMLButtonElement>(null);
  
  // Geo-detection for conditional shipping display
  const { countryCode } = useGeolocation();
  const isUS = countryCode ? isUSVisitor(countryCode) : true; // Default to US if unknown
  const nonUsBadgeText = useNonUsBadgeText(); // Configurable badge for non-US visitors
  const nonUsAlternativeUsp = useNonUsAlternativeUsp(); // Replacement for "Made in USA" for non-US visitors
  
  // FAQ micro-modal state
  const [showFaqMicroModal, setShowFaqMicroModal] = useState(false);
  const [faqMicroModalDismissed, setFaqMicroModalDismissed] = useState(false);
  
  // Checkout exit recovery modal state
  const [showExitRecoveryModal, setShowExitRecoveryModal] = useState(false);
  const [exitRecoveryShownThisSession, setExitRecoveryShownThisSession] = useState(false);
  const [exitRecoveryEmail, setExitRecoveryEmail] = useState('');
  const [exitRecoverySubmitted, setExitRecoverySubmitted] = useState(false);
  
  // Trust badges gradient visibility state
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true); // Default to true as design element
  const trustBadgesRef = useRef<HTMLDivElement | null>(null);
  
  // Contextual carousel state for quiz results
  const [contextualCarouselIndex, setContextualCarouselIndex] = useState(0);
  const [contextualCardsCount, setContextualCardsCount] = useState(0);
  
  // Trust badges scroll detection for gradient visibility
  useEffect(() => {
    const container = trustBadgesRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const isMobile = window.innerWidth < 768;
      
      // Check if content actually overflows
      const hasOverflow = scrollWidth > clientWidth;
      
      // Show left gradient if scrolled away from start (only when there's overflow)
      setShowLeftGradient(hasOverflow && scrollLeft > 5);
      
      // Show right gradient:
      // - On mobile: always show as design element (visual scroll indicator)
      // - On desktop: only show if there's overflow and not scrolled to end
      if (isMobile) {
        // On mobile, always show right gradient unless scrolled to end
        setShowRightGradient(hasOverflow ? scrollLeft < scrollWidth - clientWidth - 5 : true);
      } else {
        // On desktop, only show if there's actual overflow
        setShowRightGradient(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
      }
    };
    
    // Initial check
    handleScroll();
    
    // Add scroll listener
    container.addEventListener('scroll', handleScroll);
    
    // Add resize listener to re-check on viewport changes
    window.addEventListener('resize', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);
  
  // Gallery pills gradient visibility - initial check on mount
  useEffect(() => {
    const checkGalleryGradients = () => {
      const container = document.querySelector('.flex.gap-2.overflow-x-auto.pb-2.scrollbar-hide');
      const leftGradient = document.getElementById('thumb-gradient-left');
      const rightGradient = document.getElementById('thumb-gradient-right');
      
      if (!container || !leftGradient || !rightGradient) return;
      
      const hasOverflow = container.scrollWidth > container.clientWidth;
      
      if (!hasOverflow) {
        // No overflow - hide both gradients
        leftGradient.style.opacity = '0';
        rightGradient.style.opacity = '0';
      } else {
        // Has overflow - show right gradient initially (at scroll start)
        leftGradient.style.opacity = '0';
        rightGradient.style.opacity = '1';
      }
    };
    
    // Check after a short delay to ensure DOM is ready
    setTimeout(checkGalleryGradients, 100);
    
    // Also check on window resize
    window.addEventListener('resize', checkGalleryGradients);
    
    return () => {
      window.removeEventListener('resize', checkGalleryGradients);
    };
  }, []);
  
  // Swipe/drag state for carousel
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragCurrentX, setDragCurrentX] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const wheelAccumulatorRef = useRef(0);
  const lastWheelTimeRef = useRef(0);
  const swipeCooldownRef = useRef(false);
  

  
  // Scroll to top when arriving from quiz (ensures page starts at top)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromQuiz = urlParams.get('from') === 'quiz';
    if (fromQuiz) {
      // Force scroll to top immediately
      window.scrollTo(0, 0);
      // Also set after a small delay to ensure it happens after any browser restoration
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, []);
  
  // Auto-switch contextual carousel every 6 seconds (one card at a time)
  useEffect(() => {
    if (contextualCardsCount <= 3) return; // Only auto-switch if more than 3 cards
    const interval = setInterval(() => {
      setContextualCarouselIndex((prev) => (prev + 1) % contextualCardsCount);
    }, 10000); // 10 seconds - much slower auto-switch
    return () => clearInterval(interval);
  }, [contextualCardsCount]);
  
  // Dynamic title and meta description
  useEffect(() => {
    const pageTitle = config.name || 'The Same Pillows Used in 5-Star Hotels - For Less';
    const pageDescription = 'Sleep like you\'re on vacation every night. Enjoy the same high-end luxury quality pillows used in Four Seasons and Ritz Carlton at just a fraction of the price!';
    
    document.title = `${pageTitle} | Colagea`;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', pageDescription);
      document.head.appendChild(metaDescription);
    }
  }, [config]);
  

  
  // Click outside to close OEKO-TEX badge
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (oekoTexRef.current && !oekoTexRef.current.contains(event.target as Node)) {
        setOekoTexExpanded(false);
      }
    };
    if (oekoTexExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [oekoTexExpanded]);
  
  // Event page IDs that have date-based availability
  const eventPageIds = ['valentine-gift', 'mothers-day', 'fathers-day', 'black-friday'];
  const isEventPage = eventPageIds.includes(config.id);
  
  // Event names for display
  const eventNames: Record<string, string> = {
    'valentine-gift': "Valentine's Day",
    'mothers-day': "Mother's Day",
    'fathers-day': "Father's Day",
    'black-friday': "Black Friday"
  };
  
  // Get selected marketing angle from localStorage (synced from LP Manager)
  const getSelectedAngle = (): string => {
    if (!isEventPage) return 'gift-focused';
    try {
      const stored = localStorage.getItem('skyvane_event_angles');
      if (stored) {
        const angles = JSON.parse(stored);
        return angles[config.id] || 'gift-focused';
      }
    } catch (e) {
      console.error('Error reading angle:', e);
    }
    // Default angles for event pages
    if (config.id === 'mothers-day') return 'neck-pain';
    if (config.id === 'black-friday') return 'black-friday';
    return 'gift-focused';
  };
  
  const selectedAngle = getSelectedAngle();
  
  // Angle-based content modifiers for event pages
  const angleModifiers: Record<string, {
    heroEmphasis?: string;
    benefitHighlight?: string;
    ctaText?: string;
    trustBadge?: string;
  }> = {
    '5-star-hotel': {
      heroEmphasis: 'Same pillows used in luxury hotels like Four Seasons and Ritz-Carlton.',
      benefitHighlight: 'hotel-quality',
      ctaText: 'Get Hotel-Quality Sleep',
      trustBadge: 'As seen in 5-star hotels'
    },
    'neck-pain': {
      heroEmphasis: 'Designed for proper neck alignment to help reduce morning stiffness and discomfort.',
      benefitHighlight: 'alignment',
      ctaText: 'Get Relief Tonight',
      trustBadge: 'Recommended for neck support'
    },
    'gift-focused': {
      heroEmphasis: 'The perfect gift that shows you care about their comfort and well-being.',
      benefitHighlight: 'gifting',
      ctaText: 'Give the Gift of Sleep',
      trustBadge: 'Premium gift packaging'
    },
    'restorative': {
      heroEmphasis: 'Wake up feeling truly refreshed with sleep that actually restores your body.',
      benefitHighlight: 'restoration',
      ctaText: 'Start Sleeping Better',
      trustBadge: 'For restorative rest'
    },
    'black-friday': {
      heroEmphasis: 'Limited time Black Friday pricing on hotel-quality pillows that hold their shape for years.',
      benefitHighlight: 'sale',
      ctaText: 'Claim Black Friday Deal',
      trustBadge: 'Up to 66% off'
    }
  };
  
  const currentAngleModifier = angleModifiers[selectedAngle] || angleModifiers['gift-focused'];
  
  // Event-specific content for the gift section
  const eventContent: Record<string, {
    headline: string;
    subheadline: string;
    description: string;
    image: string;
    features: { icon: 'gift' | 'check' | 'heart'; text: string }[];
    cards: { icon: 'moon' | 'gift' | 'sparkles'; title: string; description: string; image: string }[];
  }> = {
    'valentine-gift': {
      headline: "Better Than Flowers.",
      subheadline: "Softer Than Roses.",
      description: "Because rest is one of the most personal ways to show you care. Give a gift they'll enjoy every night, not just once.",
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/valentine-unboxing_b4511f36.png',
      features: [
        { icon: 'gift', text: 'Premium Packaging' },
        { icon: 'check', text: 'No Wrapping Needed' },
        { icon: 'heart', text: '60-Day Guarantee' }
      ],
      cards: [
        { icon: 'moon', title: 'Enjoyed Every Night', description: "Unlike flowers that wilt or chocolates that disappear, this gift becomes part of their nightly routine.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/valentine-couple-bed_849a5d51.png' },
        { icon: 'gift', title: 'Arrives Ready to Gift', description: "Premium packaging means no wrapping required. Just add a card and watch them unwrap something special.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/valentine-unboxing_b4511f36.png' },
        { icon: 'sparkles', title: 'For Someone Special', description: "The perfect choice for the person who's hard to shop for. Everyone sleeps, and everyone deserves better sleep.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/valentine-hero-pillow_50ab259a.png' }
      ]
    },
    'mothers-day': {
      headline: "More Than a Gift.",
      subheadline: "A Thank You She'll Feel Every Night.",
      description: "Mom spends every day taking care of others. This Mother's Day, give her the rest she truly deserves with hotel-quality comfort.",
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/mothers-day-unboxing_b93a799b.png',
      features: [
        { icon: 'gift', text: 'Gift-Ready Packaging' },
        { icon: 'check', text: 'Neck & Back Support' },
        { icon: 'heart', text: '60-Day Guarantee' }
      ],
      cards: [
        { icon: 'moon', title: 'Rest She Deserves', description: "After years of early mornings and late nights caring for everyone else, give mom the gift of truly restorative sleep.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/mothers-day-comfort_4424042c.png' },
        { icon: 'gift', title: 'Thoughtful & Practical', description: "Not another gift that sits in a drawer. This is something she'll use and appreciate every single night.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/mothers-day-unboxing_b93a799b.png' },
        { icon: 'sparkles', title: 'Supports Her Comfort', description: "Designed for proper neck alignment, especially helpful for the aches and tension that come with years of caregiving.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/mothers-day-hero_a8f9dd7b.png' }
      ]
    },
    'fathers-day': {
      headline: "More Than a Gift.",
      subheadline: "Relief He'll Feel Every Morning.",
      description: "Dad never complains, but you know he wakes up stiff. This Father's Day, give him proper neck alignment and the rest he's earned.",
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/fathers-day-unboxing-new_212f2985.png',
      features: [
        { icon: 'gift', text: 'Gift-Ready Packaging' },
        { icon: 'check', text: 'Neck Pain Relief' },
        { icon: 'heart', text: '60-Day Guarantee' }
      ],
      cards: [
        { icon: 'moon', title: 'Relief He Deserves', description: "After years of hard work and poor pillows, give dad the gift of waking up without the morning stiffness.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/fathers-day-sleep-comfort_6f15ce0d.png' },
        { icon: 'gift', title: 'Practical & Appreciated', description: "Not another tie or gadget. This is something he'll use and appreciate every single night.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/fathers-day-unboxing-new_212f2985.png' },
        { icon: 'sparkles', title: 'Supports His Comfort', description: "Designed for proper neck alignment, especially helpful for the aches that come with years of hard work.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/fathers-day-hero-pillow_9b14a432.png' }
      ]
    },
    'black-friday': {
      headline: "Black Friday Sale.",
      subheadline: "Premium Comfort at Unbeatable Prices.",
      description: "This Black Friday, experience hotel-quality sleep at prices that won't come around again. Limited time, limited stock.",
      image: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/OOtRBgHLZECUSwVP.png',
      features: [
        { icon: 'gift', text: 'Up to 66% Off' },
        { icon: 'check', text: 'Free Shipping' },
        { icon: 'heart', text: '60-Day Guarantee' }
      ],
      cards: [
        { icon: 'moon', title: 'Hotel-Quality Sleep', description: "The same premium pillows used in 5-star hotels, now at Black Friday prices you won't see again.", image: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/zrrIKlcUCWgKmCCN.png' },
        { icon: 'gift', title: 'Limited Time Only', description: "Once Black Friday ends, these prices disappear. Stock up now and upgrade every pillow in your home.", image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' },
        { icon: 'sparkles', title: 'Clinically Proven Results', description: "Dermatologically tested, clinically validated. Premium hair care backed by science.", image: 'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/mqJDnFMoaMnTVstI.png' }
      ]
    }
  };
  
  // California timezone (PST/PDT) utility
  const CALIFORNIA_TIMEZONE = 'America/Los_Angeles';
  
  // Get current time in California timezone
  const getCaliforniaTime = () => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: CALIFORNIA_TIMEZONE }));
  };
  
  // Parse a date string and interpret it as California time
  const parseAsCaliforniaTime = (dateStr: string, endOfDay: boolean = false) => {
    const date = new Date(dateStr);
    // Create a date string in California timezone format
    const caDateStr = date.toLocaleDateString('en-US', { timeZone: CALIFORNIA_TIMEZONE });
    const caDate = new Date(caDateStr);
    if (endOfDay) {
      caDate.setHours(23, 59, 59, 999);
    } else {
      caDate.setHours(0, 0, 0, 0);
    }
    return caDate;
  };

  // Get event dates from localStorage (synced from LP Manager)
  // All dates are interpreted in California timezone
  const getEventDates = () => {
    if (!isEventPage) return null;
    try {
      const storedDates = localStorage.getItem('skyvane_event_dates');
      if (storedDates) {
        const dates = JSON.parse(storedDates);
        if (dates[config.id]) {
          const startDate = dates[config.id].startDate ? parseAsCaliforniaTime(dates[config.id].startDate, false) : null;
          const endDate = dates[config.id].endDate ? parseAsCaliforniaTime(dates[config.id].endDate, true) : null;
          return { startDate, endDate };
        }
      }
    } catch (e) {
      console.error('Error parsing event dates:', e);
    }
    // Default dates based on event type (California timezone)
    // For testing: all event pages accessible Jan 1 to May 15
    // In production, LP Manager will set actual dates
    const year = new Date().getFullYear();
    return {
      startDate: new Date(year, 0, 1, 0, 0, 0, 0), // Jan 1 (for testing)
      endDate: new Date(year, 4, 15, 23, 59, 59, 999) // May 15 (for testing)
    };
  };

  // Check event status (using California timezone)
  const getEventStatus = () => {
    const dates = getEventDates();
    if (!dates) return { status: 'active' as const, startDate: null, endDate: null };
    
    const now = getCaliforniaTime();
    const { startDate, endDate } = dates;
    
    if (startDate && now < startDate) {
      return { status: 'not-started' as const, startDate, endDate };
    }
    if (endDate && now > endDate) {
      return { status: 'ended' as const, startDate, endDate };
    }
    return { status: 'active' as const, startDate, endDate };
  };

  const eventStatus = getEventStatus();
  
  // Check for LP Manager auth (logged in users can access all event pages)
  const bypassActive = typeof window !== 'undefined' && sessionStorage.getItem('skyvane_lp_auth') === 'authenticated';

  // Fetch color schemes from backend
  const { data: backendColorSchemes } = trpc.colorSchemes.getAll.useQuery();
  
  // Hardcoded fallback color schemes (always available immediately)
  const hardcodedColorSchemes: Record<string, { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean }> = {
    'valentine-gift': {
      primary: '#c41e3a',
      secondary: '#8b1a2d',
      accent: '#fff5f5',
      accentDark: '#ffe8e8',
      background: '#fff8f8',
      secondaryLight: '#fce8eb'
    },
    'mothers-day': {
      primary: '#9b6b6b',
      secondary: '#7a5454',
      accent: '#fff9f9',
      accentDark: '#f5e6e6',
      background: '#fffafa',
      textOnLight: '#7a5454',
      secondaryLight: '#f5eaea'
    },
    'fathers-day': {
      primary: '#2c5282',
      secondary: '#1a365d',
      accent: '#f0f5ff',
      accentDark: '#e2e8f0',
      background: '#f8fafc',
      secondaryLight: '#e8eef8'
    },
    'black-friday': {
      primary: '#d4a574',
      secondary: '#b8935f',
      accent: '#fff9f0',
      accentDark: '#ffe8cc',
      background: '#fffbf5',
      secondaryLight: '#fff3e0',
      isDark: false
    }
  };
  
  // Build event color schemes from backend data, with hardcoded fallback per event
  const eventColorSchemes: Record<string, { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean }> = 
    backendColorSchemes?.reduce((acc, scheme) => {
      acc[scheme.slug] = {
        primary: scheme.primaryColor,
        secondary: scheme.secondaryColor,
        accent: scheme.accentColor,
        accentDark: scheme.accentDarkColor,
        background: scheme.backgroundColor,
        secondaryLight: scheme.secondaryLightColor,
        // textOnLight is optional, use secondaryColor as fallback
        textOnLight: scheme.secondaryColor,
        isDark: scheme.isDark === 1
      };
      return acc;
    }, { ...hardcodedColorSchemes } as Record<string, { primary: string; secondary: string; accent: string; accentDark: string; background: string; textOnLight?: string; secondaryLight: string; isDark?: boolean }>) || hardcodedColorSchemes;
  
  // Get colors for current event with proper fallback chain:
  // 1. Try backend color for this specific event
  // 2. Fall back to hardcoded color for this specific event
  // 3. Fall back to Valentine's as last resort
  const eventColors = isEventPage ? (eventColorSchemes[config.id] || hardcodedColorSchemes[config.id] || hardcodedColorSchemes['valentine-gift']) : null;
  
  // Determine if current color scheme is dark (for automatic text/border adaptation)
  const isDarkScheme = eventColors?.isDark === true;
  
  // Color helpers for dark/light scheme adaptation
  const accentColor = isDarkScheme && eventColors ? eventColors.secondary : (eventColors?.primary || '#007AFF');
  const accentBg = isDarkScheme && eventColors ? eventColors.secondaryLight : (eventColors ? `${eventColors.primary}08` : 'rgba(0, 122, 255, 0.05)');
  // For dark schemes (Black Friday), use gold accent colors for borders
  // For light schemes (Valentine's, etc.), use primary red colors
  const borderColorSelected = isDarkScheme && eventColors ? '#FFD700' : (eventColors?.primary || '#007AFF');
  const borderColorUnselected = isDarkScheme && eventColors ? 'rgba(255,215,0,0.3)' : (eventColors ? `${eventColors.primary}40` : 'var(--border)');
  
  // Helper function for closed page
  const getEventColorScheme = () => eventColors || eventColorSchemes['valentine-gift'];

  // If event is not active, show closed page
  if (isEventPage && eventStatus.status !== 'active' && !bypassActive) {
    return (
      <EventClosedPage
        eventName={eventNames[config.id] || 'Holiday'}
        eventId={config.id}
        status={eventStatus.status}
        startDate={eventStatus.startDate || undefined}
        colorScheme={getEventColorScheme()}
      />
    );
  }

  // Legacy function for timer compatibility
  const getEventEndDate = () => {
    const dates = getEventDates();
    return dates?.endDate || null;
  };

  // Check if current time is within the event's active period
  const isWithinEventPeriod = () => {
    if (!isEventPage) return false;
    const dates = getEventDates();
    if (!dates) return false;
    const now = getCaliforniaTime();
    const { startDate, endDate } = dates;
    // Must be after start date (if set) and before end date (if set)
    const afterStart = !startDate || now >= startDate;
    const beforeEnd = !endDate || now <= endDate;
    return afterStart && beforeEnd;
  };

  // Shared cookie-based countdown for non-event pages
  const sharedCountdown = useSharedCountdown();

  // For event pages: real countdown based on event end date
  const calculateEventTimeRemaining = () => {
    const eventEndDate = getEventEndDate();
    const withinPeriod = isWithinEventPeriod();
    
    if (!eventEndDate || !withinPeriod) {
      return null; // Not an event page, use shared countdown
    }
    
    const now = getCaliforniaTime();
    const diff = eventEndDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
    }
    
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return { days, hours, minutes, seconds, isExpired: false };
  };

  const [eventCountdown, setEventCountdown] = useState(calculateEventTimeRemaining);
  const [timerExpired, setTimerExpired] = useState(false);

  // Use event countdown if available, otherwise shared cookie-based countdown
  const countdown = eventCountdown && !eventCountdown.isExpired
    ? eventCountdown
    : sharedCountdown;

  useEffect(() => {
    const initial = calculateEventTimeRemaining();
    if (initial?.isExpired) {
      setTimerExpired(true);
      return;
    }

    // Only run interval for event pages
    if (!initial) return;

    const timer = setInterval(() => {
      const timeRemaining = calculateEventTimeRemaining();
      
      if (timeRemaining?.isExpired) {
        setTimerExpired(true);
        clearInterval(timer);
        return;
      }
      
      setEventCountdown(timeRemaining);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [config.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(prev => {
        const change = Math.floor(Math.random() * 7) - 3;
        const newCount = prev + change;
        return Math.max(38, Math.min(72, newCount));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Animate viewer count when it changes
  useEffect(() => {
    if (viewerCount !== displayedViewerCount) {
      setIsViewerCountAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedViewerCount(viewerCount);
        setIsViewerCountAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [viewerCount, displayedViewerCount]);

  // Calculate purchased today based on NYC timezone with non-linear growth curve
  const calculatePurchasedToday = () => {
    // Get current time in NYC timezone
    const now = new Date();
    const nycTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hours = nycTime.getHours();
    const minutes = nycTime.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    
    // Total range: 20 at midnight to 720 at 11:59PM (700 growth over 24h)
    const minPurchases = 20;
    const maxPurchases = 720;
    const totalGrowth = maxPurchases - minPurchases; // 700
    
    // Define growth weights for each hour (higher during peak times)
    // Peak 1: 9-11AM, Peak 2: 5-8PM
    const hourlyWeights = [
      0.5, 0.5, 0.5, 0.5, 0.5, 0.5,  // 12AM-6AM: slow
      1.0, 1.5, 2.0,                  // 6AM-9AM: ramping up
      4.0, 4.0, 3.0,                  // 9AM-12PM: first peak (9-11AM)
      2.0, 2.0, 2.0, 2.0, 2.0,        // 12PM-5PM: moderate
      4.0, 4.5, 4.5, 4.0,             // 5PM-9PM: second peak (5-8PM)
      2.0, 1.5, 1.0                   // 9PM-12AM: winding down
    ];
    
    // Calculate cumulative weight up to current time
    const totalWeight = hourlyWeights.reduce((a, b) => a + b, 0);
    let cumulativeWeight = 0;
    
    for (let h = 0; h < hours; h++) {
      cumulativeWeight += hourlyWeights[h];
    }
    // Add partial hour weight
    cumulativeWeight += hourlyWeights[hours] * (minutes / 60);
    
    // Calculate purchases based on weighted progress
    const progress = cumulativeWeight / totalWeight;
    const purchases = Math.floor(minPurchases + (totalGrowth * progress));
    
    return purchases;
  };

  // Initialize and update purchased today
  useEffect(() => {
    const updatePurchased = () => {
      const newValue = calculatePurchasedToday();
      setPurchasedToday(newValue);
    };
    
    updatePurchased();
    // Update every 2 minutes (less frequent than viewing counter)
    const interval = setInterval(updatePurchased, 120000);
    return () => clearInterval(interval);
  }, []);

  // Animate purchased today when it changes
  useEffect(() => {
    if (purchasedToday !== displayedPurchasedToday) {
      setIsPurchasedAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedPurchasedToday(purchasedToday);
        setIsPurchasedAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [purchasedToday, displayedPurchasedToday]);

  // Stock level decreases 1% per minute, minimum 7%
  useEffect(() => {
    const timer = setInterval(() => {
      setStockLevel(prev => Math.max(7, prev - 1));
    }, 60000); // Every 60 seconds
    return () => clearInterval(timer);
  }, []);

  // Update document title and meta tags from config
  useEffect(() => {
    // Check if this is a landing page (has a config slug that's not the default)
    const isLandingPage = config.slug && config.slug !== 'default';
    
    // Update title - use "{title} – FluffCo" format for landing pages
    const pageTitle = config.seo?.title || config.hero?.headline || 'Down Alternative Pillow';
   document.title = `${pageTitle} \u2013 Colagea`; 
    // Add noindex/nofollow for landing pages to prevent SEO indexing
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (isLandingPage) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      // Remove noindex for main page
      robotsMeta.remove();
    }
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', config.seo?.description || config.hero?.subheadline || '');
    
    // Update Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
 ogTitle.setAttribute('content', `${pageTitle} \u2013 Colagea`);   
    // Update Open Graph description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', config.seo?.description || config.hero?.subheadline || '');
    
    // Update Open Graph image (first gallery image)
    const firstImage = config.gallery?.images?.[0]?.src;
    if (firstImage) {
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      // Convert relative path to absolute URL
      const absoluteImageUrl = firstImage.startsWith('http') 
        ? firstImage 
        : `${window.location.origin}${firstImage}`;
      ogImage.setAttribute('content', absoluteImageUrl);
    }
    
    // Update Twitter Card tags
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', 'summary_large_image');
  }, [config.seo, config.hero, config.gallery]);

  // Analytics: Track page view on load and re-track when variant/size changes
  useEffect(() => {
    const size = selectedSize === 'Standard' ? 'standard' as const : 'king' as const;
    const qty = selectedQuantity as 1 | 2 | 4;
    const pricing = currentPricing.find(p => p.quantity === selectedQuantity);
    const localePrice = pricing?.price;

    // Facebook Pixel: Track ViewContent with current selection and locale currency
    const productData = getProductDataForTracking(size, qty, localePrice, currencyCode);
    trackViewContent(productData);
    
    // GA4: Track view_item with current selection
    if (pricing) {
      const ga4Item = createMainProductItem(
        selectedSize,
        qty,
        pricing.price,
        pricing.originalPrice
      );
      trackViewItem(ga4Item, pricing.price);
    }
  }, [selectedSize, selectedQuantity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll detection for sticky bar and sticky header
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const ctaButton = document.getElementById('main-cta-button');
          const footer = document.querySelector('footer');
          const announcementBar = document.querySelector('[data-announcement-bar]');
          const announcementHeight = announcementBar?.getBoundingClientRect().height || 44;
          
          // Sticky bar logic
          if (ctaButton) {
            const rect = ctaButton.getBoundingClientRect();
            const footerRect = footer?.getBoundingClientRect();
            const ctaOutOfView = rect.bottom < 0;
            const footerVisible = footerRect ? footerRect.top < window.innerHeight + 100 : false;
            setShowStickyBar(ctaOutOfView && !footerVisible);
          }
          
          // Sticky header logic - show when scrolling up, hide when scrolling down
          // Only activate after scrolling past the announcement bar + header height
          // Also account for ProfileResultsBanner if present (on quiz results page)
          const profileBanner = document.querySelector('[data-profile-banner]');
          const profileBannerHeight = profileBanner ? profileBanner.getBoundingClientRect().height : 0;
          const scrollThreshold = announcementHeight + 56 + (profileBanner ? profileBannerHeight + 100 : 0); // announcement + header + banner if present
          
          if (currentScrollY > scrollThreshold) {
            // Scrolling up - show header
            if (currentScrollY < lastScrollY) {
              setShowStickyHeader(true);
            } 
            // Scrolling down - hide header
            else if (currentScrollY > lastScrollY) {
              setShowStickyHeader(false);
            }
          } else {
            // Near top of page or within banner area - hide sticky header
            setShowStickyHeader(false);
          }
          
          // FAQ micro-modal logic - show when FAQ section is in view
          const faqSection = document.getElementById('faq');
          if (faqSection) {
            const faqRect = faqSection.getBoundingClientRect();
            const faqInView = faqRect.top < window.innerHeight * 0.7 && faqRect.bottom > 0;
            // Show modal when FAQ is in view, hide when scrolling back above it
            // Only if not manually dismissed
            if (faqInView && !faqMicroModalDismissed) {
              setShowFaqMicroModal(true);
            } else if (faqRect.top > window.innerHeight) {
              // User scrolled back up above FAQ - hide modal and reset dismissed state
              setShowFaqMicroModal(false);
            }
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, faqMicroModalDismissed]);

  // Award carousel auto-rotation (every 5 seconds for smoother experience)
  const awardsList = [
    { award: 'Best Hair Treatment 2025', publication: 'The Grooming' },
    { award: 'Editor\'s Choice', publication: 'Vogue' },
    { award: 'Top Innovation', publication: 'GQ' },
    { award: 'Best of Beauty', publication: 'Byrdie' },
    { award: 'Breakthrough Product', publication: 'Esquire' },
  ];

  useEffect(() => {
    const awardTimer = setInterval(() => {
      setAwardIndex(prev => (prev + 1) % awardsList.length);
    }, 5000);
    return () => clearInterval(awardTimer);
  }, [awardsList.length]);

  // Keyboard navigation for size and quantity selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if focus is within the product section
      const activeElement = document.activeElement;
      const isInProductSection = activeElement?.closest('[data-product-selector]');
      
      if (!isInProductSection) return;
      
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        // Toggle size
        setSelectedSize(prev => prev === 'Standard' ? 'King' : 'Standard');
      }
      
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const currentIdx = quantityOptions.indexOf(selectedQuantity);
        if (e.key === 'ArrowUp' && currentIdx > 0) {
          setSelectedQuantity(quantityOptions[currentIdx - 1]);
        } else if (e.key === 'ArrowDown' && currentIdx < quantityOptions.length - 1) {
          setSelectedQuantity(quantityOptions[currentIdx + 1]);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedQuantity]);

  // Just purchased toast notifications - locale-aware
  // For international visitors, show locale-appropriate locations and flags
  const usPurchaseToasts = [
    { name: "Sarah M.", location: "Austin, TX", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "James W.", location: "Denver, CO", flag: "🇺🇸", size: "King" as const, quantity: 2 },
    { name: "Maria G.", location: "Phoenix, AZ", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "DeShawn J.", location: "Atlanta, GA", flag: "🇺🇸", size: "King" as const, quantity: 2 },
    { name: "Yuki T.", location: "Seattle, WA", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Priya P.", location: "San Jose, CA", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Miguel R.", location: "Miami, FL", flag: "🇺🇸", size: "King" as const, quantity: 2 },
    { name: "Jennifer L.", location: "Chicago, IL", flag: "🇺🇸", size: "Standard" as const, quantity: 2 },
    { name: "Kwame A.", location: "Houston, TX", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Aisha B.", location: "Brooklyn, NY", flag: "🇺🇸", size: "King" as const, quantity: 2 },
    { name: "Chen W.", location: "Los Angeles, CA", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Robert K.", location: "Portland, OR", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Fatima H.", location: "Dallas, TX", flag: "🇺🇸", size: "King" as const, quantity: 2 },
    { name: "David S.", location: "Boston, MA", flag: "🇺🇸", size: "Standard" as const, quantity: 1 },
    { name: "Kenji M.", location: "San Francisco, CA", flag: "🇺🇸", size: "King" as const, quantity: 4 },
    { name: "Olivia N.", location: "Nashville, TN", flag: "🇺🇸", size: "King" as const, quantity: 2 },
  ];
  
  const ukPurchaseToasts = [
    { name: "Emma T.", location: "Manchester, UK", flag: "🇬🇧", size: "King" as const, quantity: 4 },
    { name: "Alistair B.", location: "Edinburgh, UK", flag: "🇬🇧", size: "King" as const, quantity: 2 },
    { name: "Charlotte D.", location: "Bristol, UK", flag: "🇬🇧", size: "King" as const, quantity: 4 },
    { name: "Hamish L.", location: "Glasgow, UK", flag: "🇬🇧", size: "King" as const, quantity: 2 },
    { name: "Fiona M.", location: "Birmingham, UK", flag: "🇬🇧", size: "Standard" as const, quantity: 2 },
    { name: "Nigel H.", location: "Leeds, UK", flag: "🇬🇧", size: "King" as const, quantity: 4 },
    { name: "Helen R.", location: "Liverpool, UK", flag: "🇬🇧", size: "King" as const, quantity: 2 },
    { name: "Gareth W.", location: "Cardiff, UK", flag: "🇬🇧", size: "King" as const, quantity: 4 },
    { name: "Pippa T.", location: "Brighton, UK", flag: "🇬🇧", size: "Standard" as const, quantity: 1 },
    { name: "Rupert K.", location: "Bath, UK", flag: "🇬🇧", size: "King" as const, quantity: 4 },
  ];
  
  const auPurchaseToasts = [
    { name: "Bec M.", location: "Melbourne, VIC", flag: "🇦🇺", size: "King" as const, quantity: 4 },
    { name: "Lachlan L.", location: "Sydney, NSW", flag: "🇦🇺", size: "King" as const, quantity: 2 },
    { name: "Tash B.", location: "Brisbane, QLD", flag: "🇦🇺", size: "King" as const, quantity: 4 },
    { name: "Callum H.", location: "Perth, WA", flag: "🇦🇺", size: "King" as const, quantity: 2 },
    { name: "Shazza D.", location: "Adelaide, SA", flag: "🇦🇺", size: "Standard" as const, quantity: 2 },
    { name: "Davo S.", location: "Gold Coast, QLD", flag: "🇦🇺", size: "King" as const, quantity: 4 },
    { name: "Gemma R.", location: "Canberra, ACT", flag: "🇦🇺", size: "King" as const, quantity: 2 },
    { name: "Macca W.", location: "Hobart, TAS", flag: "🇦🇺", size: "King" as const, quantity: 4 },
    { name: "Hayley K.", location: "Newcastle, NSW", flag: "🇦🇺", size: "Standard" as const, quantity: 1 },
    { name: "Jono H.", location: "Geelong, VIC", flag: "🇦🇺", size: "King" as const, quantity: 4 },
  ];
  
  const euPurchaseToasts = [
    { name: "Katharina M.", location: "Munich, Germany", flag: "🇩🇪", size: "King" as const, quantity: 4 },
    { name: "Joost H.", location: "Amsterdam, Netherlands", flag: "🇳🇱", size: "King" as const, quantity: 2 },
    { name: "Camille D.", location: "Paris, France", flag: "🇫🇷", size: "King" as const, quantity: 4 },
    { name: "Giulia B.", location: "Milan, Italy", flag: "🇮🇹", size: "King" as const, quantity: 2 },
    { name: "Alejandro S.", location: "Barcelona, Spain", flag: "🇪🇸", size: "Standard" as const, quantity: 2 },
    { name: "Anneliese H.", location: "Vienna, Austria", flag: "🇦🇹", size: "King" as const, quantity: 4 },
    { name: "Søren W.", location: "Copenhagen, Denmark", flag: "🇩🇰", size: "King" as const, quantity: 2 },
    { name: "Astrid R.", location: "Stockholm, Sweden", flag: "🇸🇪", size: "King" as const, quantity: 4 },
    { name: "Siobhán R.", location: "Dublin, Ireland", flag: "🇮🇪", size: "Standard" as const, quantity: 1 },
    { name: "Florian P.", location: "Berlin, Germany", flag: "🇩🇪", size: "King" as const, quantity: 4 },
  ];
  
  // Select purchase toasts based on current locale
  const purchaseToasts = useMemo(() => {
    if (!countryCode || isUS) return usPurchaseToasts;
    // Map geolocation countryCode to locale toast set
    if (countryCode === 'GB') return ukPurchaseToasts;
    if (countryCode === 'AU') return auPurchaseToasts;
    // EU countries
    const euCountries = ['DE','FR','IT','ES','NL','BE','SE','NO','DK','FI','AT','CH','IE','PT','PL','CZ','GR','HU','RO','BG','HR','SK','SI','LT','LV','EE','LU','MT','CY'];
    if (euCountries.includes(countryCode)) return euPurchaseToasts;
    return usPurchaseToasts;
  }, [countryCode, isUS]);

  // Toast notification rotation with randomized timing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let hideTimeoutId: NodeJS.Timeout;
    
    const showNextToast = () => {
      // Random delay between 15-40 seconds for natural feel
      const delay = 15000 + Math.random() * 25000;
      timeoutId = setTimeout(() => {
        setCurrentToast(Math.floor(Math.random() * purchaseToasts.length));
        setShowToast(true);
        // Auto-hide after 4-6 seconds (randomized)
        hideTimeoutId = setTimeout(() => {
          setShowToast(false);
        }, 4000 + Math.random() * 2000);
        // Schedule next toast
        showNextToast();
      }, delay);
    };
    
    // Initial delay before first toast (5-12 seconds)
    const initialDelay = setTimeout(() => {
      setCurrentToast(Math.floor(Math.random() * purchaseToasts.length));
      setShowToast(true);
      // Auto-hide first toast
      hideTimeoutId = setTimeout(() => {
        setShowToast(false);
      }, 4000 + Math.random() * 2000);
      // Start the rotation
      showNextToast();
    }, 5000 + Math.random() * 7000);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
      clearTimeout(hideTimeoutId);
    };
  }, []);

  const formatTime = (num: number) => num.toString().padStart(2, '0');
  
  // Calculate total seconds left for the pillowcase timer display
  const timeLeft = (countdown.days || 0) * 86400 + countdown.hours * 3600 + countdown.minutes * 60 + countdown.seconds;
  const formatTimeLeft = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${formatTime(h)}:${formatTime(m)}:${formatTime(s)}`;
  };

  const baseSizes = [
    { name: "Standard" as const, dimensions: "20\" × 28\"", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/size-standard_e907c533.jpg", badge: null },
    { name: "King" as const, dimensions: "20\" × 36\"", image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/size-king_43405920.jpg", badge: "Chosen by 81%", badgeColor: "green" }
  ];
  
  // Reorder sizes based on config.sizeOrder
  const sizeOrder = config.sizeOrder || ['standard', 'king'];
  const sizes = sizeOrder.map(sizeId => 
    baseSizes.find(s => s.name.toLowerCase() === sizeId)
  ).filter(Boolean) as typeof baseSizes;
  
  // Locale-aware pricing from DB (UK=GBP, AU=AUD, EU=EUR) with USD fallback
  const { standardPricing, kingPricing, getPricing, fp, fpc, fpInt, currencySymbol, currencyCode, marketingPrices, getBonusPillowcaseValue } = useLocalePricing();
  // Hotel price range in local currency (from DB marketing prices)
  const hotelPriceRange = useMemo(() => {
    const low = Math.min(marketingPrices.marriott, marketingPrices.ritzCarlton);
    const high = Math.max(marketingPrices.fourSeasons, marketingPrices.ritzCarlton);
    return `${fpInt(low)}-${fpInt(high).replace(/^[^0-9]*/, '')}`;
  }, [marketingPrices, fpInt]);
  const currentPricing = getPricing(selectedSize);
  const pricingSectionPricing = getPricing(pricingSectionSize);
  
  // Animate prices when size changes
  useEffect(() => {
    if (prevPricingSectionSize !== pricingSectionSize) {
      setIsPricingSectionAnimating(true);
      const timeout = setTimeout(() => {
        setPrevPricingSectionSize(pricingSectionSize);
        setIsPricingSectionAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [pricingSectionSize, prevPricingSectionSize]);

  // Track price direction for animation (up or down)
  const [priceDirection, setPriceDirection] = useState<'up' | 'down' | null>(null);
  
  // Animate price tag when size or quantity changes
  useEffect(() => {
    const currentPrice = currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0;
    const perPillowPrice = currentPrice / selectedQuantity;
    const prevPerPillow = prevPrice !== null ? prevPrice : perPillowPrice;
    
    if (prevPrice !== null && prevPerPillow !== perPillowPrice) {
      // Determine direction: price going up or down
      setPriceDirection(perPillowPrice > prevPerPillow ? 'up' : 'down');
      setIsHeroPriceAnimating(true);
      const timeout = setTimeout(() => {
        setIsHeroPriceAnimating(false);
        setPriceDirection(null);
      }, 300);
      return () => clearTimeout(timeout);
    }
    setPrevPrice(perPillowPrice);
  }, [selectedSize, selectedQuantity, currentPricing, prevPrice]);

  // Helper function to get optimized thumbnail srcset for gallery images
  const getThumbnailSrcSet = (imagePath: string): string => {
    const thumbnailMap: Record<string, string> = {
      'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/MWaaoxsUftIchIcc.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hero-pillow-64_b28c9bf6.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hero-pillow-128_ad3d4c51.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hero-pillow_1f81601c.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hero-pillow-64_b28c9bf6.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hero-pillow-128_ad3d4c51.webp 128w',
      'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/rCICKxerjCbWWkZu.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-64_2709575a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-128_0d8ea42d.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep_62593740.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-64_2709575a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-128_0d8ea42d.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_3ea51b01.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new-64_aa02f5c3.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new-128_47c54650.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_0222dfab.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new-64_aa02f5c3.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new-128_47c54650.webp 128w',
      'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/RVxCLjqishtuSISc.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/washing-machine-64_bfe4778a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/washing-machine-128_bb4be9de.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/washing-machine_30339ebc.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/washing-machine-64_bfe4778a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/washing-machine-128_bb4be9de.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging_0d931499.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging-64_dc7673d4.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging-128_b08e1f41.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging_5ef23511.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging-64_dc7673d4.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging-128_b08e1f41.webp 128w',
      'https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/sbJxxEWpfZlzjeLo.png': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-64_2709575a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-128_0d8ea42d.webp 128w',
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep_598dce7d.webp': 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-64_2709575a.webp 64w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lifestyle-sleep-128_0d8ea42d.webp 128w',
      // Expert gallery images for Hotel Quality page
      'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/expert-pillow-feathers_73d2b358.webp': '/images/optimized/expert/responsive/thumbnails/expert-pillow-feathers-100.webp 64w, /images/optimized/expert/responsive/thumbnails/expert-pillow-feathers-100.webp 128w',
      '/images/optimized/expert/couple-sleep.webp': '/images/optimized/expert/responsive/thumbnails/couple-sleep-100.webp 64w, /images/optimized/expert/responsive/thumbnails/couple-sleep-100.webp 128w',
      '/images/optimized/expert/factory-pillow-making.webp': '/images/optimized/expert/responsive/thumbnails/factory-pillow-making-100.webp 64w, /images/optimized/expert/responsive/thumbnails/factory-pillow-making-100.webp 128w',
    };
    return thumbnailMap[imagePath] || '';
  };

  // Gallery images - reads from config.gallery.images if available
  // This ensures all pages use the standard gallery system with customizations from their configs
  const getGalleryImages = () => {
    // If config has gallery images defined, use them
    if (config.gallery?.images && config.gallery.images.length > 0) {
      return config.gallery.images.map(img => {
        if (img.type === 'comparison') return 'comparison';
        if (img.type === 'hotel-comparison') return 'hotel-comparison';
        if (img.type === 'expert-pillow') return 'expert-pillow';
        if (img.type === 'couple-sleep') return 'couple-sleep';
        if (img.type === 'ethical-making') return 'ethical-making';
        if (img.type === 'vial-arguments') return 'vial-arguments';
        if (img.type === 'science-results') return 'science-results';
        if (img.type === 'ingredients-slide') return 'ingredients-slide';
        if (img.type === 'video-slide') return 'video-slide';
        if (img.type === 'steps-slide') return 'steps-slide';
        if (img.type === 'testimonials-slide') return 'testimonials-slide';
        if (img.type === 'before-after') return img.src;
        return toWebP(img.src); // Apply WebP optimization
      });
    }
    // Default gallery for pages without gallery config
    // Order: 0-product, 1-intro, 2-tips, 3-layers, 4-VS, 5-care, 6-hotel/summary, 7-pricing
    return [
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-box-product_08474614.jpg", // Colagea box product image (JPG)
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-before-after-bg-X4DwqREq4eXVP5kZcicy8Z.webp", // Before/After hair treatment
      "vial-arguments", // Single vial with argument overlays + connecting lines
      "science-results", // Science stats slide (dark blue bg)
      "ingredients-slide", // What's Inside - ingredients
      "comparison", // Custom comparison component
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/serum-application-new-2jWxrnaHfvtDK4hiSW4ndr.webp", // Serum application image
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/before-after-hair-redo-6v6dQgnwAQrZEFrJtQbQxb.webp", // Before/After real results
      "steps-slide", // How to Use steps with videos
      "testimonials-slide", // Customer reviews
      "hotel-comparison", // Colagea vs Others comparison
    ];
  };
  const galleryImages = getGalleryImages();

  // Preload gallery images AFTER initial page load for better performance
  // This ensures the first image loads quickly, then remaining images load in background
  useEffect(() => {
    // Mark the first image as preloaded immediately (it's already being loaded)
    const firstImage = galleryImages[0];
    const isComponentSlide = (img: string) => ['comparison', 'hotel-comparison', 'expert-pillow', 'couple-sleep', 'ethical-making', 'vial-arguments', 'science-results', 'ingredients-slide', 'video-slide', 'steps-slide', 'testimonials-slide'].includes(img);
    if (firstImage && !isComponentSlide(firstImage)) {
      setPreloadedImages(new Set([firstImage]));
    }

    // Delay preloading remaining images until after initial render and first image load
    const preloadRemainingImages = () => {
      const imagesToPreload = galleryImages.filter(
        (img, idx) => !isComponentSlide(img) && idx !== 0
      );
      
      // Preload images sequentially with small delays to avoid blocking
      imagesToPreload.forEach((src, index) => {
        setTimeout(() => {
          const img = new Image();
          img.onload = () => {
            setPreloadedImages(prev => {
              const newSet = new Set(Array.from(prev));
              newSet.add(src);
              return newSet;
            });
          };
          img.src = src;
        }, 100 * (index + 1)); // Stagger by 100ms each
      });
    };

    // Wait for page to be fully loaded before preloading remaining images
    if (document.readyState === 'complete') {
      // Page already loaded, start preloading after a short delay
      setTimeout(preloadRemainingImages, 500);
    } else {
      // Wait for page load event
      window.addEventListener('load', () => {
        setTimeout(preloadRemainingImages, 500);
      });
    }

    return () => {
      window.removeEventListener('load', preloadRemainingImages);
    };
  }, []);

  // Reset imageLoaded when switching gallery images
  useEffect(() => {
    const currentImage = galleryImages[activeImageIndex];
    const isComponentSlide = (img: string) => ['comparison', 'hotel-comparison', 'expert-pillow', 'couple-sleep', 'ethical-making', 'vial-arguments', 'science-results', 'ingredients-slide', 'video-slide', 'steps-slide', 'testimonials-slide'].includes(img);
    if (isComponentSlide(currentImage)) {
      // These are React components, no image to load
      setImageLoaded(true);
    } else if (preloadedImages.has(currentImage)) {
      // Image already preloaded
      setImageLoaded(true);
    } else {
      // Wait for image to load
      setImageLoaded(false);
    }
  }, [activeImageIndex, preloadedImages]);

  const handleGalleryImageLoad = () => {
    setImageLoaded(true);
  };

  // Navigate gallery programmatically (for thumbnail clicks)
  const navigateGallery = (index: number) => {
    if (galleryScrollRef.current) {
      const slideWidth = galleryScrollRef.current.offsetWidth;
      galleryScrollRef.current.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  };

  const navSections = [
    { id: "benefits", label: "Benefits" },
    { id: "difference", label: "The Difference" },
    { id: "technology", label: "Technology" },
    { id: "reviews", label: "Reviews" },
    { id: "faq", label: "FAQ" },
  ];

  // Emotional testimonials with human touches - use config testimonials or fallback to defaults
  // Note: Config testimonials are merged with default images and ratings
  const defaultTestimonialImages = [
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-1-hair_7a4adb2d.png',
    '', // initials only - no image
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-3-hair-closeup_5025ec08.png',
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-2-selfie_ee512eee.png',
    '', // initials only - no image
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-4-outdoor_193eaefa.png',
    '', // initials only - no image
    'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-5-before-after_9c276b17.png',
    '', '', '', '',
  ];
  
  const configTestimonials = config.testimonials.testimonials.map((t, idx) => ({
    name: t.name,
    location: t.location,
    text: t.content,
    rating: 5,
    image: defaultTestimonialImages[idx] || '',
    verified: t.verified,
  }));
  
  const testimonials = configTestimonials.length > 0 ? configTestimonials : [
    { 
      name: "David R.", 
      location: "Chicago, IL", 
      text: "3 months in and I can literally see new hair growing in my crown area. My barber noticed before I even said anything. After trying minoxidil for 2 years with zero results, THIS actually works.", 
      rating: 5, 
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-1-hair_7a4adb2d.png', 
      verified: true 
    },
    { 
      name: "Marcus T.", 
      location: "Portland, OR", 
      text: "Was super skeptical but my wife convinced me. 5 weeks later the thinning spot on top is filling in. No side effects, no weird scalp reactions. Just... hair growing back. Still can't believe it.", 
      rating: 5, 
      image: '', 
      verified: true 
    },
    { 
      name: "Robert K.", 
      location: "Austin, TX", 
      text: "The micro-needle thing sounded scary but it's completely painless. I use it every morning after my shower. 2 months in and the difference is VISIBLE. My hairline is actually coming back.", 
      rating: 5, 
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-3-hair-closeup_5025ec08.png', 
      verified: true 
    },
    { 
      name: "Steven C.", 
      location: "Seattle, WA", 
      text: "My dermatologist was impressed at my last checkup. Asked what I changed. When I showed him Colagea he said the ingredient profile is solid science. That validation meant everything.", 
      rating: 5, 
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-2-selfie_ee512eee.png', 
      verified: true 
    },
    { 
      name: "Jason H.", 
      location: "Denver, CO", 
      text: "I've spent thousands on hair loss treatments. Pills, lasers, special shampoos... nothing worked. Colagea is the first thing that actually delivered visible results. 6 weeks and counting.", 
      rating: 5, 
      image: '', 
      verified: true 
    },
    { 
      name: "Michael P.", 
      location: "Phoenix, AZ", 
      text: "Started losing hair at 28. Tried everything. At 34 I found Colagea and within 2 months I stopped wearing hats everywhere. My confidence is back. This stuff is legit.", 
      rating: 5, 
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-4-outdoor_193eaefa.png', 
      verified: true 
    },
    { 
      name: "Andrew W.", 
      location: "Boston, MA", 
      text: "4 months with the 4-month cure. Best decision I've made. The vials are easy to use, no mess, and the results speak for themselves. My wife keeps running her hands through my hair now lol", 
      rating: 5, 
      image: '', 
      verified: true 
    },
    { 
      name: "James L.", 
      location: "San Francisco, CA", 
      text: "Bought the 2-month cure to test. By week 3 I saw baby hairs coming in. Immediately ordered the 4-month. The before/after photos I took are insane. Wish I'd found this years ago.", 
      rating: 5, 
      image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/testimonial-5-before-after_9c276b17.png', 
      verified: true 
    },
  ];

  // Localize testimonial locations for international visitors
  const { testimonials: localizedTestimonials } = useLocalizedTestimonials(testimonials);
  const visibleTestimonials = showAllTestimonials ? localizedTestimonials : localizedTestimonials.slice(0, 4);

  // FAQs from config or defaults
  const configFaqs = config.faq.items.map(item => ({ q: item.question, a: item.answer }));
  const defaultFaqs = [
    { q: "How does Colagea Micro-Infuse® actually work?", a: "Colagea uses a patented micro-needle delivery system with 192 ultra-fine needles (0.5 mm) that create painless micro-channels in the scalp. These channels allow our clinical-grade serum to penetrate up to 3× deeper than any topical product, reaching the dermal papilla where hair growth originates. The serum delivers five active compounds—Acetyl Tetrapeptide-3, Copper Tripeptide-1, Saw Palmetto Extract, Caffeine Complex, and Niacinamide B3—directly to dormant follicles, reactivating the growth cycle at its source." },
    { q: "When will I see results?", a: "Most users notice reduced shedding within 2–3 weeks. New vellus (baby) hairs typically appear around week 4–5. By week 8–12 those mature into thicker terminal hairs, producing visibly denser coverage. In our 24-week clinical trial, 90% of participants reported measurable improvement by week 5, and average hair density increased 34% by week 16." },
    { q: "Why micro-needles instead of just applying serum topically?", a: "The stratum corneum (outermost skin layer) blocks 95% of topically applied molecules from reaching the follicle root. Colagea's 0.5 mm micro-needles create temporary channels that bypass this barrier entirely, delivering actives directly to the dermal papilla. Peer-reviewed research in the Journal of Controlled Release shows micro-needle-assisted delivery increases bioavailability by 300–500% compared to topical application alone." },
    { q: "How is Colagea different from minoxidil?", a: "Minoxidil dilates blood vessels on the scalp surface but never reaches the follicle root. It requires twice-daily application indefinitely—if you stop, all regrown hair falls out within 3–6 months. Common side effects include scalp irritation and unwanted facial hair growth. Colagea’s micro-needle delivery bypasses the skin barrier to target the dermal papilla directly. Our peptide-based formula reactivates the follicle’s own growth signals. No prescription needed, no dependency, no systemic side effects." },
    { q: "How is Colagea different from finasteride (Propecia)?", a: "Finasteride is an oral prescription drug that blocks DHT systemically—affecting your entire body, not just your scalp. It carries well-documented side effects including decreased libido, erectile dysfunction, and depression. Colagea’s Saw Palmetto Extract blocks DHT locally at the follicle level through the micro-needle delivery system, without entering your bloodstream. No prescription, no doctor visits, no sexual side effects." },
    { q: "I’ve tried everything and nothing works. Why would Colagea be different?", a: "Most hair loss products fail because of a delivery problem, not an ingredient problem. Shampoos rinse off in 60 seconds. Topical serums sit on the surface where 95% of actives are blocked. Colagea solves the delivery problem—the micro-needle system physically bypasses the skin barrier and places active compounds directly at the follicle root. It’s the same principle used in medical dermatology for drug delivery, adapted for at-home hair treatment." },
    { q: "Will my hair fall out again if I stop using Colagea?", a: "This is the key difference from minoxidil, where 100% of regrown hair falls out within months of stopping. Colagea reactivates the follicle’s own growth cycle through direct peptide signaling. Once a follicle has completed a full anagen cycle—about 4 months—the results are significantly more durable. Most users maintain density for 3–6 months after a full 4-Month Cure. For long-term maintenance, many customers do one 1-Month Cure every 4–6 months." },
    { q: "Why is the 4-Month Cure recommended over the 1-Month?", a: "Hair biology dictates this, not marketing. A dormant follicle needs 90–120 days to fully transition from telogen (resting) to mature anagen (growth). A 1-Month Cure will reduce shedding and start reactivation, but stopping at 4 weeks means interrupting the cycle before new hairs have matured. The 4-Month Cure ensures every reactivated follicle completes a full growth cycle, producing thicker, stronger terminal hairs. Plus, you save 40% per treatment compared to buying monthly." },
    { q: "Is the formula safe? Any side effects?", a: "Colagea is dermatologically tested, hypoallergenic, and manufactured in a GMP-certified facility. The formula contains zero parabens, sulfates, silicones, minoxidil, or finasteride. The micro-needles are 0.5 mm—classified as cosmetic-grade—and designed to be painless. Most users describe the sensation as a light tingling. In our clinical trial, zero participants reported adverse reactions." },
    { q: "How does the micro-needle applicator work? Does it hurt?", a: "The patented applicator cap contains 192 surgical-grade stainless steel micro-needles at 0.5 mm depth—well below the pain threshold. Screw the cap onto the serum vial, press gently against the target area, and roll for 30 seconds. The micro-channels close within 30–60 minutes. Most users describe the sensation as a light tingling—comparable to a scalp massage. The entire application takes under 5 minutes." },
    { q: "Can women use Colagea?", a: "Absolutely. Female pattern hair loss affects approximately 40% of women by age 50, yet most treatments are designed for male-pattern baldness. Colagea’s formula targets follicle health regardless of gender—the peptides, DHT blockers, and growth stimulants work on the same biological pathways in both sexes. In our clinical trial, female participants showed a 78% improvement in hair density, slightly higher than the male average of 72%." },
    { q: "What is the 60-Day Money-Back Guarantee?", a: "If you’re not satisfied with your results within 60 days of purchase, contact us for a full refund with free return shipping. No questions asked, no fine print. The guarantee applies to all cure durations (1-Month, 2-Month, and 4-Month)." },
    { q: "Is there a subscription or auto-renewal?", a: "No. Colagea is a one-time purchase with no hidden subscriptions, auto-renewals, or recurring charges. You buy what you need, when you need it. We don’t believe in locking customers into subscriptions—if our product works (and we’re confident it will), you’ll come back on your own terms." },
    { q: "How long does shipping take?", a: "We ship directly from our US fulfillment center. Standard shipping is free and takes 3–5 business days. You’ll receive a tracking number via email as soon as your order ships. All orders include free returns within 60 days." },
  ];
  // Always show the comprehensive defaultFaqs; append any unique configFaqs from DB
  const defaultQuestions = new Set(defaultFaqs.map(f => f.q.toLowerCase()));
  const extraConfigFaqs = configFaqs.filter(f => !defaultQuestions.has(f.q.toLowerCase()));
  const faqs = [...defaultFaqs, ...extraConfigFaqs];

  const scrollToSection = (id: string) => {
    // Map nav IDs to actual section IDs
    const sectionIdMap: Record<string, string> = {
      'reviews': 'reviews-section',
    };
    const targetId = sectionIdMap[id] || id;
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToPricing = () => {
    const element = document.getElementById('hero-pricing');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const selectOptionAndScroll = (quantity: number, size: "Standard" | "King") => {
    setSelectedSize(size);
    setSelectedQuantity(quantity);
    scrollToPricing();
  };

  const benefitCards = [
    { title: "Results in 4 Weeks", desc: "Clinical trials show visible hair density improvement within the first month of consistent use.", icon: Clock, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/before-after-hair-redo-6v6dQgnwAQrZEFrJtQbQxb.webp" },
    { title: "60-Second Routine", desc: "Apply once daily. The micro-needle cap does the work. No mess, no complicated steps.", icon: Sparkles, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-YsyfBpGiRjpqdjxKzxRwdk.webp" },
    { title: "Zero Side Effects", desc: "No pills, no hormones, no sexual dysfunction. 100% topical with natural active ingredients.", icon: Shield, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lab-research-colagea-hG2ScjduDDqHse7hJEWwxY.webp" },
    { title: "Dermatologist Approved", desc: "Tested and recommended by board-certified dermatologists. Safe for all skin types.", icon: Droplets, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-follicle-reactivation-DE9h28jiMJkWRRwK2yLTLY.webp" },
    { title: "Use It Anywhere, Anytime", desc: "Each sealed vial is pre-dosed and TSA-approved. Treat your hair at home, at the gym, or on the go. No refrigeration needed.", icon: Wind, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/satisfied-customer-colagea-hQhoatfmjViZeUsi7PQapy.webp" },
    { title: "Risk-Free: 60-Day Full Refund", desc: "Try Colagea for 60 days. If you don't see thicker, stronger hair, we'll refund every cent. No forms, no hassle, no fine print.", icon: RefreshCw, image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/happy-man-mirror-bS9QroVXPQ5AM4k6GSBkXx.webp" },
  ];

  // CSS variables for event colors
  const eventCssVars = isEventPage && eventColors ? {
    '--event-primary': eventColors.primary,
    '--event-secondary': eventColors.secondary,
    '--event-accent': eventColors.accent,
    '--event-accent-dark': eventColors.accentDark,
    '--event-background': eventColors.background,
  } as React.CSSProperties : {};

  // Check for quiz profile parameter and use-case source
  const urlParams = new URLSearchParams(window.location.search);
  const profileId = urlParams.get('profile');
  const userEmail = urlParams.get('email');
  const fromUseCase = urlParams.get('from'); // e.g., 'side-sleeper-pillow'
  const quizTags = urlParams.get('tags')?.split(',').filter(Boolean) || []; // Parse quiz tags for badges
  const quizLabels = urlParams.get('labels')?.split(',').filter(Boolean) || []; // Human-readable labels for results
  
  // Convert use-case slug to display name
  const getUseCaseName = (slug: string | null) => {
    // Don't show badge for generic 'quiz' source - use quizLabels instead
    if (!slug || slug === 'quiz') return null;
    const names: Record<string, string> = {
      'side-sleeper-pillow': 'Side Sleepers',
      'hot-sleeper': 'Hot Sleepers',
      'neck-pain': 'Neck Pain Relief',
    };
    return names[slug] || null; // Don't fallback to slug parsing, only show known use cases
  };
  
  const useCaseName = getUseCaseName(fromUseCase);

  return (
    <div className="min-h-screen bg-background" style={eventCssVars}>
      <main id="main-content">

      {/* Profile Results Banner - shown when user arrives from quiz OR after completing quiz on PDP */}
      {(profileId || quizResultProfileId) && <ProfileResultsBanner profileId={(profileId || quizResultProfileId)!} email={userEmail || undefined} tags={profileId ? quizTags : quizResultTags} labels={profileId ? quizLabels : quizResultLabels} onRestartQuiz={() => setIsQuizModalOpen(true)} pageId={config.id} />}
      {/* STICKY ANNOUNCEMENT BAR - clickable on mobile */}
      <div 
        data-announcement-bar
        className="sticky top-0 z-[60] text-white py-1.5 sm:py-2.5 px-3 sm:px-4 text-center text-sm sm:cursor-default cursor-pointer transition-colors pointer-events-auto"
        style={{ backgroundColor: isEventPage && eventColors ? eventColors.primary : '#0066CC' }}
        onClick={(e) => {
          // Only trigger on mobile (when Claim Now button is hidden)
          if (window.innerWidth < 640) {
            // Haptic feedback for mobile devices
            if (navigator.vibrate) {
              navigator.vibrate(10);
            }
            scrollToPricing();
          }
        }}
      >
        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <span>{config.announcementBar.text}</span>
          {!timerExpired && (
            <div className="flex items-center gap-1 font-mono text-xs bg-white/10 px-2 py-1 rounded">
              {countdown.days > 0 && (
                <>
                  <span className="tabular-nums">{countdown.days}d</span>
                  <span className="mx-0.5">:</span>
                </>  
              )}
              <span className="tabular-nums">{formatTime(countdown.hours)}</span>
              <span>:</span>
              <span className="tabular-nums">{formatTime(countdown.minutes)}</span>
              <span>:</span>
              <span className="tabular-nums">{formatTime(countdown.seconds)}</span>
            </div>
          )}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              scrollToPricing();
            }}
            className="hidden sm:block text-white text-xs font-medium px-4 py-1.5 rounded-full transition-colors animate-cta-shimmer"
            style={isEventPage && eventColors ? {
              backgroundColor: eventColors.secondary,
            } : {
              backgroundColor: '#007AFF'
            }}
            onMouseEnter={(e) => {
              if (isEventPage && eventColors) {
                // Darken the secondary color by 10% on hover
                const color = eventColors.secondary;
                const darkened = color.replace('#', '');
                const r = Math.max(0, parseInt(darkened.substr(0, 2), 16) - 25);
                const g = Math.max(0, parseInt(darkened.substr(2, 2), 16) - 25);
                const b = Math.max(0, parseInt(darkened.substr(4, 2), 16) - 25);
                e.currentTarget.style.backgroundColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
              } else {
                e.currentTarget.style.backgroundColor = '#0066CC';
              }
            }}
            onMouseLeave={(e) => {
              if (isEventPage && eventColors) {
                e.currentTarget.style.backgroundColor = eventColors.secondary;
              } else {
                e.currentTarget.style.backgroundColor = '#007AFF';
              }
            }}
          >
            {config.announcementBar.ctaText}
          </button>
        </div>
      </div>

      {/* STICKY HEADER - Shows on scroll up, hides on scroll down (desktop only) */}
      {/* Positioned below the sticky announcement bar (top-[44px] accounts for announcement bar height) */}
      <header 
        className={`fixed top-[44px] left-0 right-0 z-50 border-b border-border hidden md:block transition-transform duration-300 ${isEventPage ? 'bg-white' : 'bg-background'} ${showStickyHeader ? 'translate-y-0' : '-translate-y-[calc(100%+44px)]'}`}
        style={{ boxShadow: showStickyHeader ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' }}
      >
        <div className="container px-4 flex items-center justify-between h-14">
          <FluffLogo className="h-9 w-auto" color="currentColor" />
          <nav className="flex items-center gap-6">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {section.label}
              </button>
            ))}
          </nav>
          <button 
            onClick={scrollToPricing}
            className="text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
            style={{ 
              backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
              boxShadow: `0 2px 6px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`
            }}
          >
            {`${getPageCTA()} →`}
          </button>
        </div>
      </header>

      {/* HEADER - Normal (not sticky) */}
      <header className={`border-b border-border ${isEventPage ? 'bg-white' : 'bg-background'}`}>
        <div className="container px-4 flex items-center justify-between h-14">
          <FluffLogo className="h-9 w-auto" color="currentColor" />
          <nav className="hidden md:flex items-center gap-6">
            {navSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {section.label}
              </button>
            ))}
          </nav>
          <button 
            onClick={scrollToPricing}
            className="text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
            style={{ 
              backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
              boxShadow: `0 2px 6px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`
            }}
            onMouseEnter={(e) => {
              const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
              e.currentTarget.style.boxShadow = `0 2px 8px ${color}35`;
              e.currentTarget.style.filter = 'brightness(0.97)';
            }}
            onMouseLeave={(e) => {
              const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
              e.currentTarget.style.boxShadow = `0 2px 6px ${color}40`;
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {`${getPageCTA()} →`}
          </button>
        </div>
      </header>

      {/* BLACK FRIDAY URGENCY SECTION - Only shown on Black Friday page */}
      {config.id === 'black-friday' && <BlackFridayUrgencySection backgroundColor={eventColors?.accentDark || '#f8f8f6'} primaryColor={eventColors?.primary || '#FFD700'} textColor={eventColors?.textOnLight || eventColors?.primary || '#1a1a1a'} accentColor={eventColors?.accent || '#FF6B6B'} />}

      {/* HERO SECTION */}
      <section 
        data-hero-section
        className="pt-0 pb-8 md:py-12 md:pt-5 overflow-x-clip"
        style={isEventPage && eventColors ? {
          background: `linear-gradient(to bottom, ${eventColors.background}, white)`
        } : {}}
      >
        <div className="container px-0 md:px-4 lg:px-4 max-w-full overflow-x-hidden md:overflow-x-visible">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-12 max-w-full">
            {/* LEFT: Product Gallery - Sticky on desktop/tablet */}
            <div className="space-y-2 md:space-y-4 md:sticky md:top-[88px] md:self-start md:h-fit w-full min-w-0 relative md:pl-0 overflow-visible">
              {/* 60-Day Guarantee Badge - Desktop only here, Mobile is fixed positioned via JS */}
              {/* Desktop badge - positioned at top-right corner overlapping gallery */}
              <div className="hidden md:block absolute z-30 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]" style={{ top: '-30px', right: '-35px' }}>
                <div className="relative">
                  {/* Scalloped badge shape - slightly larger for better readability */}
                  <svg viewBox="0 0 80 80" className="w-[80px] h-[80px] lg:w-[88px] lg:h-[88px] drop-shadow-md">
                    <path d="M40 2 L44 8 L52 6 L54 14 L62 14 L62 22 L70 24 L68 32 L76 38 L70 44 L74 52 L66 54 L66 62 L58 62 L54 70 L46 68 L40 76 L34 68 L26 70 L22 62 L14 62 L14 54 L6 52 L10 44 L4 38 L12 32 L10 24 L18 22 L18 14 L26 14 L28 6 L36 8 Z" fill={isEventPage ? eventColors?.accent : '#ffffff'} stroke={isEventPage ? eventColors?.primary : '#e0ddd5'} strokeWidth="1" strokeLinejoin="round" strokeLinecap="round"/>
                  </svg>
                  {/* Badge text content - unified design across breakpoints */}
                  <div className="absolute inset-[6px] lg:inset-[7px] flex flex-col items-center justify-center">
                    {(() => {
                      const trialText = siteSettings.trial_period_text || '60-Day Guarantee';
                      const match = trialText.match(/(\d+)[\-\s]*(\w+)\s+(.*)/i);
                      const num = match ? match[1] : '60';
                      const word1 = match ? match[2] : 'Day';
                      const rest = match ? match[3] : 'Guarantee';
                      const restWords = rest.split(/\s+/);
                      const primaryColor = isEventPage ? eventColors?.primary : '#004C99';
                      return (
                        <div className="flex flex-col items-center justify-center gap-0" style={{paddingBottom: '7px'}}>
                          <span className="text-[24px] lg:text-[28px] font-black leading-none" style={{ color: primaryColor, marginBottom: '-2px', fontSize: '30px' }}>{num}</span>
                          <span className="text-[10px] lg:text-[11px] font-bold uppercase tracking-wider leading-none mt-0.5" style={{ color: primaryColor, marginTop: '0px', marginBottom: '1px', fontSize: '12px' }}>{word1}S</span>
                          <div className="flex flex-col items-center">
                            {restWords.map((w: string, i: number) => (
                              <span key={i} className="text-[7px] lg:text-[8px] font-semibold uppercase tracking-wide leading-tight" style={{ color: primaryColor }}>{w}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div 
                className="aspect-square bg-secondary rounded-none md:rounded-lg overflow-hidden relative"
              >
                {/* 60-Day Guarantee Badge - Mobile only, first slide only */}
                {activeImageIndex === 0 && (
                  <div className="md:hidden absolute top-2 right-2 z-30 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                    <div className="relative">
                      {/* Scalloped badge shape - same size as desktop (80px) */}
                      <svg viewBox="0 0 80 80" className="w-[80px] h-[80px] drop-shadow-md">
                        <path d="M40 2 L44 8 L52 6 L54 14 L62 14 L62 22 L70 24 L68 32 L76 38 L70 44 L74 52 L66 54 L66 62 L58 62 L54 70 L46 68 L40 76 L34 68 L26 70 L22 62 L14 62 L14 54 L6 52 L10 44 L4 38 L12 32 L10 24 L18 22 L18 14 L26 14 L28 6 L36 8 Z" fill={isEventPage ? eventColors?.accent : '#ffffff'} stroke={isEventPage ? eventColors?.primary : '#e0ddd5'} strokeWidth="1" strokeLinejoin="round" strokeLinecap="round"/>
                      </svg>
                      {/* Badge text content - exact same as desktop */}
                      <div className="absolute inset-[6px] flex flex-col items-center justify-center">
                        {(() => {
                          const trialText = siteSettings.trial_period_text || '60-Day Guarantee';
                          const match = trialText.match(/(\d+)[\-\s]*(\w+)\s+(.*)/i);
                          const num = match ? match[1] : '60';
                          const word1 = match ? match[2] : 'Day';
                          const rest = match ? match[3] : 'Guarantee';
                          const restWords = rest.split(/\s+/);
                          const primaryColor = isEventPage ? eventColors?.primary : '#004C99';
                          return (
                            <div className="flex flex-col items-center justify-center gap-0">
                              <span className="text-[24px] font-black leading-none" style={{ color: primaryColor, marginBottom: '-2px' }}>{num}</span>
                              <span className="text-[10px] font-bold uppercase tracking-wider leading-none mt-0.5" style={{ color: primaryColor, marginBottom: '2px' }}>{word1}S</span>
                              <div className="flex flex-col items-center">
                                {restWords.map((w: string, i: number) => (
                                  <span key={i} className="text-[7px] font-semibold uppercase tracking-wide leading-tight" style={{ color: primaryColor }}>{w}</span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                {/* Horizontal scroll carousel - all images laid out side-by-side */}
                <div 
                  className="flex w-full h-full overflow-x-scroll snap-x snap-mandatory scrollbar-hide"
                  style={{
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch',
                  }}
                  onScroll={(e) => {
                    const container = e.currentTarget;
                    const scrollLeft = container.scrollLeft;
                    const slideWidth = container.offsetWidth;
                    const newIndex = Math.round(scrollLeft / slideWidth);
                    if (newIndex !== activeImageIndex) {
                      setActiveImageIndex(newIndex);
                    }
                  }}
                  ref={(el) => {
                    if (el && galleryScrollRef.current !== el) {
                      galleryScrollRef.current = el;
                    }
                  }}
                >
                  {/* Render all gallery images side-by-side */}
                  {galleryImages.map((image, index) => (
                    <div 
                      key={index}
                      className={`w-full h-full flex-shrink-0 snap-center transition-opacity duration-300 ${index === activeImageIndex ? 'opacity-100' : 'opacity-100'}`}
                      style={index === activeImageIndex ? { animation: 'galleryFadeIn 0.35s ease-out' } : {}}
                    >
                      {image === 'comparison' ? (
                        <ComparisonSlide />
                      ) : image === 'hotel-comparison' ? (
                        <HotelComparisonSlide fluffPrice={fpInt((currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0) / selectedQuantity)} hotelPriceRange={hotelPriceRange} />
                      ) : image === 'expert-pillow' ? (
                        <ExpertPillowSlide />
                      ) : image === 'couple-sleep' ? (
                        <CoupleSleepSlide />
                      ) : image === 'ethical-making' ? (
                        <EthicalMakingSlide />
                      ) : image === 'vial-arguments' ? (
                        <VialArgumentsSlide />
                      ) : image === 'science-results' ? (
                        <ScienceResultsSlide />
                      ) : image === 'ingredients-slide' ? (
                        <IngredientsSlide />
                      ) : image === 'video-slide' ? (
                        <VideoSlide />
                      ) : image === 'steps-slide' ? (
                        <StepsGallerySlide />
                      ) : image === 'testimonials-slide' ? (
                        <TestimonialsGallerySlide />
                      ) : (
                        <BlurImage 
                          src={image} 
                          alt="Product" 
                          className="w-full h-full object-cover"
                          blurAmount={15}
                          transitionDuration={400}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : undefined}
                          onLoad={() => {
                            if (index === activeImageIndex) {
                              handleGalleryImageLoad();
                            }
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {/* First Image Badges - Base page only (exclude neck-pain-relief and restorative-alignment which have their own overlays) */}
                {activeImageIndex === 0 && imageLoaded && config.id !== 'neck-pain-relief' && config.id !== 'restorative-alignment' && (
                  <>
                    {/* +7,500 hairs text - single line just above the Voted badge at bottom center */}
                    <div className="absolute bottom-12 lg:bottom-14 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.1s_both] z-20 whitespace-nowrap">
                      <p className="text-sm md:text-base lg:text-lg font-semibold text-gray-700 tracking-wide">
                        <span className="text-[#0066CC] font-bold">+7,500</span> hairs within 5 weeks
                      </p>
                    </div>
                    {/* Event Headline Overlay - Top Right for event pages */}
                    {isEventPage && config.hero.eventHeadline && (
                      <div className="absolute top-4 right-4 lg:top-6 lg:right-6 max-w-[280px] lg:max-w-[380px] animate-[fadeSlideIn_0.4s_ease-out_0.15s_both] z-20 text-right">
                        <p 
                          className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                          style={{ color: '#ffffff' }}
                        >
                          {/* Support line breaks in eventHeadline using <br> tag or newline */}
                          {config.hero.eventHeadline.split('\n').map((line, idx, arr) => (
                            <span key={idx}>
                              {line}
                              {idx < arr.length - 1 && <br />}
                            </span>
                          ))}
                        </p>
                      </div>
                    )}
                    {/* Profile-Based Contextual Badge - Top Right for quiz profiles (same design as event headline) */}
                    {!isEventPage && quizTags.length > 0 && (() => {
                      // Define playful sentences for each profile tag
                      const profileMessages: Record<string, string> = {
                        'neck-pain': 'Finally, mornings\nwithout the ouch',
                        'hot-sleeper': 'No more flipping\nfor the cool side',
                        'restless': 'Sleep through\nthe night, finally',
                        'stomach': 'Designed for\nface-down dreamers',
                        'back': 'Perfect support,\nzero pressure points',
                        'side': 'Made for\nside sleepers like you',
                      };
                      // Find the first matching tag
                      const matchedTag = quizTags.find(tag => profileMessages[tag]);
                      if (!matchedTag) return null;
                      const message = profileMessages[matchedTag];
                      return (
                        <div className="absolute top-4 right-4 lg:top-6 lg:right-6 max-w-[280px] lg:max-w-[380px] animate-[fadeSlideIn_0.4s_ease-out_0.15s_both] z-20 text-right">
                          <p 
                            className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                            style={{ color: '#ffffff' }}
                          >
                            {message.split('\n').map((line, idx, arr) => (
                              <span key={idx}>
                                {line}
                                {idx < arr.length - 1 && <br />}
                              </span>
                            ))}
                          </p>
                        </div>
                      );
                    })()}
                    {/* Dynamic Price Tag Overlay - Only for hotel-quality page - 15% higher position */}
                    {config.id === 'hotel-quality' && (
                      <div className="absolute bottom-[22%] right-4 lg:bottom-[26%] lg:right-6 animate-[fadeSlideIn_0.4s_ease-out_0.3s_both] z-20">
                        <div className="relative">
                          {/* Price tag shape - blue brand border */}
                          <div className="bg-white shadow-xl rounded-lg px-4 py-3 border-2 border-[#0066CC] relative">
                            {/* Tag hole - blue brand */}
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#0066CC] rounded-full border-2 border-white shadow-sm"></div>
                            {/* String */}
                            <div className="absolute -left-4 top-1/2 w-3 h-0.5 bg-[#0066CC]/50"></div>
                            
                            {/* Price per unit with animation - round DOWN */}
                            <div className="text-center">
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide font-medium mb-0.5">Per Pillow</div>
                              <div className="flex items-baseline justify-center gap-1">
                                <span 
                                  className="text-2xl lg:text-3xl font-bold text-gray-900 inline-block"
                                  style={{
                                    transition: 'transform 0.25s ease-out, opacity 0.2s ease',
                                    transform: isHeroPriceAnimating ? 'scale(1.05)' : 'scale(1)',
                                    opacity: isHeroPriceAnimating ? 0.85 : 1
                                  }}
                                >
                                  {fpInt((currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0) / selectedQuantity)}
                                </span>
                                <span className="text-xs text-gray-400 line-through">
                                  {fpInt((currentPricing.find(p => p.quantity === selectedQuantity)?.originalPrice || 0) / selectedQuantity)}
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                Total: {fpInt(currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0)} for {selectedQuantity}
                              </div>
                              <div className="mt-1 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block">
                                Save {fpInt(currentPricing.find(p => p.quantity === selectedQuantity)?.savings || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Award Badges - Dermatologically Tested */}
                    <AwardBadges configId={config.id} />

                    {/* Award Badge - Single static badge */}
                    <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both] ${config.gallery?.hideAwardCarouselMobile ? 'hidden lg:block' : ''}`}>
                      <div className="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 lg:px-4 lg:py-2 shadow-md flex items-center gap-1.5 whitespace-nowrap">
                        <span className="text-[13px] lg:text-sm">🏆</span>
                        <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">Voted Best Men's Hair Innovation 2025</span>
                      </div>
                    </div>

                  </>
                )}
                {/* Neck Pain Relief - First Image Overlay */}
                {activeImageIndex === 0 && config.id === 'neck-pain-relief' && imageLoaded && (
                  <>
                    {/* Award Badges - Dermatologically Tested */}
                    <AwardBadges configId={config.id} />
                    {/* Bottom Left Badge - Neutral Alignment */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-[#0066CC] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#2563eb] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Neutral Alignment</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Maintains natural spine curvature</p>
                    </div>
                    {/* Bottom Right Badge - Proper Support */}
                    <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm text-[#0066CC] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#2563eb] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Proper Support</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Consistent support night after night</p>
                    </div>
                    {/* Award Badge - Top Center (same style as base page) */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 lg:px-2.5 lg:py-1.5 shadow-md flex items-center gap-1.5">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => prev === 0 ? awardsList.length - 1 : prev - 1); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Previous award"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-800" />
                        </button>
                        <div 
                          key={awardIndex}
                          className="flex items-center gap-1.5 whitespace-nowrap animate-[fadeInUp_0.4s_ease-out_both]"
                        >
                          <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">
                            {awardsList[awardIndex].award}
                          </span>
                          <span className="text-muted-foreground text-[10px] lg:text-[11px]">
                            {awardsList[awardIndex].publication}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => (prev + 1) % awardsList.length); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Next award"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Restorative Alignment - First Image Overlay */}
                {activeImageIndex === 0 && config.id === 'restorative-alignment' && imageLoaded && (
                  <>
                    {/* Award Badges - Dermatologically Tested */}
                    <AwardBadges configId={config.id} />
                    {/* Bottom Left Badge - Neutral Alignment */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-[#0066CC] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#c9a962] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Neutral Alignment</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Maintains natural spine curvature</p>
                    </div>
                    {/* Bottom Right Badge - Proper Support */}
                    <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm text-[#0066CC] px-3 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[160px] lg:max-w-[200px] animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#c9a962] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Proper Support</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Consistent support night after night</p>
                    </div>
                    {/* Award Badge - Top Center (same style as base page) */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 animate-[fadeSlideIn_0.4s_ease-out_0.2s_both]">
                      <div className="bg-white/95 backdrop-blur-sm rounded-md px-2 py-1 lg:px-2.5 lg:py-1.5 shadow-md flex items-center gap-1.5">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => prev === 0 ? awardsList.length - 1 : prev - 1); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Previous award"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-800" />
                        </button>
                        <div 
                          key={awardIndex}
                          className="flex items-center gap-1.5 whitespace-nowrap animate-[fadeInUp_0.4s_ease-out_both]"
                        >
                          <span className="text-gray-900 text-[11px] lg:text-xs font-semibold">
                            {awardsList[awardIndex].award}
                          </span>
                          <span className="text-muted-foreground text-[10px] lg:text-[11px]">
                            {awardsList[awardIndex].publication}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setAwardIndex(prev => (prev + 1) % awardsList.length); }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Next award"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {/* Before/After Overlay Pills - Gallery slide index 1 */}
                {activeImageIndex === 1 && galleryImages[activeImageIndex] !== 'comparison' && galleryImages[activeImageIndex] !== 'hotel-comparison' && imageLoaded && (() => {
                  // JSON-driven pill positioning for before/after slide
                  // Layout: 4 corners - TL & BL for Before (left half), TR & BR for After (right half)
                  // Headers at bottom, detail pills at top
                  const pills = [
                    // TOP-LEFT: Before detail
                    { id: 'before-detail', position: { top: '3%', left: '3%' }, icon: 'down' as const, iconColor: '#0066CC', title: 'Micro-Needles', description: '24K gold 0.5mm needles create micro-channels in the scalp', delay: '0.1s' },
                    // TOP-RIGHT: After detail
                    { id: 'after-detail', position: { top: '3%', right: '3%' }, icon: 'up' as const, iconColor: '#16a34a', title: 'Nutrient Delivery', description: 'Active ingredients reach follicles to stop loss and stimulate regrowth', delay: '0.15s' },
                    // BOTTOM-LEFT: Before header
                    { id: 'before-header', position: { bottom: '3%', left: '3%' }, icon: null, iconColor: '', title: 'BEFORE', description: 'Weakened follicles', delay: '0.25s', isHeader: true },
                    // BOTTOM-RIGHT: After header
                    { id: 'after-header', position: { bottom: '3%', right: '3%' }, icon: null, iconColor: '', title: 'AFTER', description: 'Revitalized growth', delay: '0.35s', isHeader: true },
                  ];
                  return (
                    <>
                      {pills.map((pill) => (
                        <div
                          key={pill.id}
                          className={`absolute bg-white/95 backdrop-blur-sm text-[#0066CC] shadow-lg max-w-[44%] lg:max-w-[45%] ${
                            pill.isHeader
                              ? 'px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg lg:rounded-xl'
                              : 'px-2.5 py-2 lg:px-3 lg:py-2.5 rounded-lg lg:rounded-xl'
                          }`}
                          style={{
                            ...pill.position,
                            animationName: 'fadeSlideIn',
                            animationDuration: '0.4s',
                            animationTimingFunction: 'ease-out',
                            animationDelay: pill.delay,
                            animationFillMode: 'both',
                          }}
                        >
                          {pill.isHeader ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs lg:text-sm font-bold tracking-wide">{pill.title}</span>
                              <span className="text-[10px] lg:text-xs text-gray-500 leading-tight">{pill.description}</span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-1">
                                <span className="w-5 h-5 lg:w-6 lg:h-6 rounded-full text-white flex items-center justify-center font-semibold flex-shrink-0" style={{ backgroundColor: pill.iconColor }}>
                                  {pill.icon === 'down' ? (
                                    <svg className="w-3 h-3 lg:w-3.5 lg:h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                                  ) : (
                                    <svg className="w-3 h-3 lg:w-3.5 lg:h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
                                  )}
                                </span>
                                <span className="text-[11px] lg:text-sm font-bold leading-tight">{pill.title}</span>
                              </div>
                              <p className="text-[9px] lg:text-xs text-gray-600 leading-snug">{pill.description}</p>
                            </>
                          )}
                        </div>
                      ))}
                    </>
                  );
                })()}
                {/* Engineering Detail Overlay Badges for engineering-detail image - ALTERNATE POSITIONS with animations */}
                {(galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_3ea51b01.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/engineering-detail-new_0222dfab.webp') && imageLoaded && (
                  <>
                    {/* Top Right Badge - Cotton Shell - Responsive */}
                    <div className="absolute top-3 right-3 lg:top-6 lg:right-6 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">Cotton Shell</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">100% breathable cotton exterior</p>
                    </div>
                    {/* Middle Left Badge - Microfiber Fill - Responsive */}
                    <div className="absolute top-1/2 -translate-y-1/2 left-3 lg:left-6 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Microfiber Fill</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">5x thinner than human hair for plush feel</p>
                    </div>
                    {/* Bottom Right Badge - Structural Core - Responsive */}
                    <div className="absolute bottom-3 right-3 lg:bottom-6 lg:right-6 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[140px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">Structural Core</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Sanforized for lasting shape retention</p>
                    </div>
                  </>
                )}
                {/* Serum Application Overlay - 3 pills about application benefits */}
                {galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/serum-application-new-2jWxrnaHfvtDK4hiSW4ndr.webp' && imageLoaded && (
                  <>
                    {/* Top Right Badge - 30 Second Ritual */}
                    <div className="absolute top-3 right-3 lg:top-5 lg:right-5 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[145px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">1</span>
                        <span className="text-[11px] lg:text-sm font-bold">30-Second Ritual</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Apply directly to scalp, done</p>
                    </div>
                    {/* Bottom Left Badge - Micro-Infuse */}
                    <div className="absolute bottom-12 left-3 lg:bottom-16 lg:left-5 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[145px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">2</span>
                        <span className="text-[11px] lg:text-sm font-bold">Micro-Infuse Tech</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Penetrates 3x deeper than serums</p>
                    </div>
                    {/* Bottom Right Badge - No Mess */}
                    <div className="absolute bottom-3 right-3 lg:bottom-5 lg:right-5 bg-white/95 backdrop-blur-sm text-[#0066CC] px-2.5 py-2 lg:px-4 lg:py-3 rounded-lg lg:rounded-xl shadow-lg max-w-[145px] lg:max-w-[180px] animate-[fadeSlideIn_0.4s_ease-out_0.4s_both]">
                      <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
                        <span className="w-5 h-5 lg:w-6 lg:h-6 bg-[#0066CC] text-white rounded-full text-[11px] lg:text-sm flex items-center justify-center font-semibold">3</span>
                        <span className="text-[11px] lg:text-sm font-bold">No Mess, No Hassle</span>
                      </div>
                      <p className="text-[10px] lg:text-xs text-gray-600 leading-snug">Clean, precise application every time</p>
                    </div>
                  </>
                )}
                {/* 5-Star Hotel Comfort Overlay - Three icon cards at bottom */}
                {(galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging_0d931499.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hotel-comfort-packaging_5ef23511.webp' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp') && imageLoaded && (
                  <>
                    {/* Top Banner - Main Headline with The Grooming Badge */}
                    <div className="absolute top-4 left-0 right-0 text-center animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]">
                      <h3 className={`text-lg lg:text-2xl font-bold tracking-tight ${
                        (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                          ? 'text-white' 
                          : 'text-[#0066CC]'
                      }`}>
                        {config.gallery?.slotOverrides?.summary?.headline 
                          ? config.gallery.slotOverrides.summary.headline.split('\n').map((line, i) => (
                              <span key={i} className={i > 0 ? 'block' : ''}>{line}</span>
                            ))
                          : '5-Star Hotel Comfort at Home'
                        }
                      </h3>
                      {/* The Grooming 94/100 Badge - Trustpilot style */}
                      <div className="inline-flex items-center gap-2 mt-2 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={i < 5 ? '#00b67a' : '#dcdce6'}>
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          ))}
                        </div>
                        <span className="text-xs font-bold text-[#00b67a]">94/100</span>
                        <span className="text-[10px] text-gray-500 font-medium">The Grooming</span>
                      </div>
                    </div>
                    
                    {/* Three Icon Cards Row - Bottom area - 4% on mobile, 12% on desktop */}
                    <div className="absolute bottom-[4%] lg:bottom-[12%] left-4 right-4 flex justify-between gap-2 lg:gap-4">
                      {/* Ethical Down Alternative */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#007AFF]">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M8.5 5A1.5 1.5 0 0 0 7 6.5c0 .5.2.9.5 1.2L9 9l-1.5 1.3c-.3.3-.5.7-.5 1.2a1.5 1.5 0 0 0 3 0c0-.5-.2-.9-.5-1.2L8 9l1.5-1.3c.3-.3.5-.7.5-1.2A1.5 1.5 0 0 0 8.5 5zM12 12c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-6c0-1.1-.9-2-2-2h-8z"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>Ethical Down</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>Alternative</span>
                      </div>
                      {/* Clinical Grade */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#007AFF]">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full"><rect x="4" y="6" width="16" height="12" rx="2"/><line x1="4" y1="10" x2="20" y2="10"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>Clinical Grade</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>Count Cotton</span>
                      </div>
                      {/* 5-Star Quality */}
                      <div className="flex flex-col items-center text-center flex-1 animate-[fadeSlideIn_0.4s_ease-out_0.35s_both]">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 mb-1 text-[#007AFF]">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 9h.01M15 9h.01M9 13h.01M15 13h.01"/></svg>
                        </div>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>5-Star Luxury</span>
                        <span className={`text-[10px] lg:text-xs font-semibold leading-tight ${
                          (galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_06f2e2f5.png' || galleryImages[activeImageIndex] === 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/black-friday-packaging_7f4109f2.webp')
                            ? 'text-white' 
                            : 'text-[#0066CC]'
                        }`}>Quality</span>
                      </div>
                    </div>
                  </>
                )}
                {/* Before/After Real Results - Slide 6 overlay */}
                {(galleryImages[activeImageIndex]?.includes('before-after-hair-redo') || galleryImages[activeImageIndex]?.includes('before-after-hair-slide6')) && imageLoaded && (
                  <>
                    {/* Bottom gradient overlay matching video slide style */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent z-10 pointer-events-none" />
                    {/* Top subtle overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/25 to-transparent z-10 pointer-events-none" style={{ height: '25%' }} />
                    {/* BEFORE / AFTER labels at top */}
                    <div className="absolute top-3 left-3 lg:top-4 lg:left-4 z-20 animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
                      <div className="bg-white/90 backdrop-blur-sm px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-md shadow-sm">
                        <span className="text-[10px] lg:text-xs font-bold text-gray-700 tracking-wider">BEFORE</span>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 lg:top-4 lg:right-4 z-20 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
                      <div className="bg-[#16a34a]/90 backdrop-blur-sm px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-md shadow-sm">
                        <span className="text-[10px] lg:text-xs font-bold text-white tracking-wider">AFTER</span>
                      </div>
                    </div>
                    {/* Simple vertical divider line in center */}
                    <div className="absolute top-[10%] bottom-[25%] left-1/2 -translate-x-1/2 w-px bg-white/30 z-10" />
                    {/* Bottom text content - client info + context (matching video slide gradient style) */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 lg:p-5 z-20">
                      <p className="text-white text-sm lg:text-base font-semibold leading-snug">
                        Thomas R., 34 &mdash; <span className="text-[#4d9fff]">8 weeks apart</span>
                      </p>
                      <p className="text-white/70 text-xs lg:text-sm mt-0.5">Visible crown density improvement with 2-Month Cure</p>
                      <p className="text-white/35 text-[8px] lg:text-[9px] mt-1.5">Individual results may vary &mdash; Colagea Micro-Infuse&reg;</p>
                    </div>
                  </>
                )}
              </div>
              {/* Thumbnail container with hidden scrollbar and gradient indicators */}
              <div className="relative -mx-0 lg:mx-0">
                {/* Left gradient indicator - adapts to event color scheme */}
                <div 
                  className="absolute left-0 top-0 bottom-2 w-12 pointer-events-none z-10 opacity-0 transition-opacity duration-200" 
                  id="thumb-gradient-left"
                  style={{ background: `linear-gradient(to right, ${isEventPage && eventColors ? eventColors.background : 'var(--background)'}, transparent)` }}
                />
                {/* Right gradient indicator - adapts to event color scheme */}
                <div 
                  className="absolute right-0 top-0 bottom-2 w-12 pointer-events-none z-10" 
                  id="thumb-gradient-right"
                  style={{ background: `linear-gradient(to left, ${isEventPage && eventColors ? eventColors.background : 'var(--background)'}, transparent)` }}
                />
                <div 
                  className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide px-4 md:px-0"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    const leftGradient = document.getElementById('thumb-gradient-left');
                    const rightGradient = document.getElementById('thumb-gradient-right');
                    
                    // Only show gradients if content actually overflows
                    const hasOverflow = el.scrollWidth > el.clientWidth;
                    
                    if (!hasOverflow) {
                      // No overflow - hide both gradients
                      if (leftGradient) leftGradient.style.opacity = '0';
                      if (rightGradient) rightGradient.style.opacity = '0';
                    } else {
                      // Has overflow - show gradients based on scroll position
                      if (leftGradient) leftGradient.style.opacity = el.scrollLeft > 10 ? '1' : '0';
                      if (rightGradient) rightGradient.style.opacity = el.scrollLeft < el.scrollWidth - el.clientWidth - 10 ? '1' : '0';
                    }
                  }}
                >
                  {galleryImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => navigateGallery(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        activeImageIndex === idx ? 'border-foreground' : 'border-transparent hover:border-muted-foreground'
                      }`}
                    >
                      {/* Thumbnail with dynamic image and tag indicator - detect comparison slides by content */}
                      {galleryImages[idx] === 'comparison' ? (
                        <ComparisonThumbnail />
                      ) : galleryImages[idx] === 'hotel-comparison' ? (
                        <HotelComparisonThumbnail />
                      ) : galleryImages[idx] === 'expert-pillow' ? (
                        <ExpertPillowThumbnail />
                      ) : galleryImages[idx] === 'couple-sleep' ? (
                        <CoupleSleepThumbnail />
                      ) : galleryImages[idx] === 'ethical-making' ? (
                        <EthicalMakingThumbnail />
                      ) : galleryImages[idx] === 'vial-arguments' ? (
                        <VialArgumentsThumbnail />
                      ) : galleryImages[idx] === 'science-results' ? (
                        <ScienceResultsThumbnail />
                      ) : galleryImages[idx] === 'ingredients-slide' ? (
                        <IngredientsThumbnail />
                      ) : galleryImages[idx] === 'video-slide' ? (
                        <VideoSlideThumbnail />
                      ) : galleryImages[idx] === 'steps-slide' ? (
                        <StepsGalleryThumbnail />
                      ) : galleryImages[idx] === 'testimonials-slide' ? (
                        <TestimonialsGalleryThumbnail />
                      ) : (
                        <div className="w-full h-full relative">
                          <img 
                            src={galleryImages[idx]} 
                            srcSet={getThumbnailSrcSet(galleryImages[idx])}
                            sizes="64px"
                            alt="" 
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                          {/* Tag indicators based on image content */}
                          {idx === 0 && config.id === 'hotel-quality' && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">50% off</span>
                            </div>
                          )}
                          {idx === 0 && config.id === 'neck-pain-relief' && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">align</span>
                            </div>
                          )}
                          {idx === 0 && config.id === 'restorative-alignment' && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">align</span>
                            </div>
                          )}
                          {idx === 0 && config.id !== 'hotel-quality' && config.id !== 'neck-pain-relief' && config.id !== 'restorative-alignment' && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">product</span>
                            </div>
                          )}
                          {/* Gallery index 1 - before/after image */}
                          {idx === 1 && !galleryImages[idx].includes('comparison') && !galleryImages[idx].includes('hotel-comparison') && !galleryImages[idx].includes('engineering-detail') && !galleryImages[idx].includes('washing-machine') && !galleryImages[idx].includes('hotel-comfort-packaging') && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">results</span>
                            </div>
                          )}
                          {galleryImages[idx].includes('hotel-comfort-packaging') && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">hotel</span>
                            </div>
                          )}
                          {/* Gallery 2 - before/after slot (index 1) */}
                          {/* Gallery 3 - engineering-detail */}
                          {galleryImages[idx].includes('engineering-detail') && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">3 layers</span>
                            </div>
                          )}
                          {/* Gallery 5 - washing-machine */}
                          {galleryImages[idx].includes('washing-machine') && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">care</span>
                            </div>
                          )}
                          {/* Serum application thumbnail label */}
                          {galleryImages[idx].includes('serum-application') && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#0066CC]">apply</span>
                            </div>
                          )}
                          {/* Before/After real results slide */}
                          {(galleryImages[idx].includes('before-after-hair-slide6') || galleryImages[idx].includes('before-after-hair-redo')) && (
                            <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
                              <span className="text-[7px] font-semibold text-[#16a34a]">b/a</span>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              {/* Engineered in Switzerland - desktop only */}
              <div className="hidden md:flex items-center justify-center gap-1.5 mt-1.5 text-xs text-gray-400">
                <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="2" fill="#D52B1E"/>
                  <path d="M14 8h4v16h-4z" fill="white"/>
                  <path d="M8 14h16v4H8z" fill="white"/>
                </svg>
                <span>Engineered in Switzerland</span>
              </div>
            </div>

            {/* RIGHT: Product Info */}
            <div className="space-y-4 md:space-y-6 px-4 md:px-0 overflow-hidden md:overflow-visible max-w-full" id="hero-pricing" data-hero-section>
              <div className="flex flex-wrap items-center gap-2 md:gap-3" style={{marginBottom: '12px'}}>
                {/* Hotel Quality: Show 5-Star Secret badge instead of stars */}
                {config.id === 'hotel-quality' ? (
                  <div className="flex items-center gap-2 relative">
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#1a1a1a] rounded-full">
                      <svg className="w-3.5 h-3.5 text-[#c9a962]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span className="text-xs font-semibold text-white">The 5-Star Secret</span>
                    </div>
                    <span className="text-xs text-muted-foreground"><CustomerCount format="short" /> Happy Customers</span>

                  </div>
                ) : (
                  <button 
                    onClick={() => {
                      const reviewsSection = document.getElementById('reviews-section');
                      if (reviewsSection) {
                        reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <TrustpilotStars className="h-4" />
                    <span className="text-sm text-muted-foreground"><CustomerCount format="short" /> Happy Customers</span>
                  </button>
                )}
                {/* The Grooming Badge with Hover Info-box - hidden on mobile and hotel-quality page */}
                {config.id !== 'hotel-quality' && (
                <div className="hidden sm:block relative group">
                  <div 
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full cursor-pointer border border-gray-200"
                    style={isEventPage ? { backgroundColor: 'white' } : { backgroundColor: 'white' }}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={isEventPage ? eventColors?.primary : '#00b67a'}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span className="text-xs font-bold" style={isEventPage ? { color: eventColors?.primary } : { color: '#00b67a' }}>94/100</span>
                    <span className="text-xs text-muted-foreground font-medium">The Grooming</span>
                  </div>
                  {/* Hover Info-box - desktop only, centered with viewport bounds, reduced hover zone */}
                  <div className="hidden sm:block absolute top-full left-1/2 -translate-x-1/2 w-72 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200" style={{ minWidth: '288px', maxWidth: 'calc(100vw - 24px)', paddingTop: '5px' }}>
                    <div className="bg-white dark:bg-card rounded-xl shadow-xl border border-border p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/the-grooming-logo-e48dRNAri5SnnXfBn3ogpn.webp" alt="The Grooming" className="h-6 w-auto flex-shrink-0" loading="lazy" decoding="async" />
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-sm text-foreground">The Grooming</span>
                          {/* Twitter-style verified checkmark */}
                          <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 22 22" fill="none">
                            <circle cx="11" cy="11" r="11" fill="#1D9BF0"/>
                            <path d="M9.5 14.25L6.75 11.5L7.8125 10.4375L9.5 12.125L14.1875 7.4375L15.25 8.5L9.5 14.25Z" fill="white"/>
                          </svg>
                        </div>
                        <div className="text-xs text-muted-foreground">By Dr. Sarah Mitchell</div>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Value</span>
                        <span className="font-semibold text-foreground">19/20</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Effectiveness</span>
                        <span className="font-semibold text-foreground">19/20</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quality</span>
                        <span className="font-semibold text-foreground">20/20</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ease of Use</span>
                        <span className="font-semibold text-foreground">18/20</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Long-Term Results</span>
                        <span className="font-semibold text-foreground">19/20</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border mt-2">
                        <span className="font-semibold text-foreground">Total</span>
                        <span className="font-bold text-[#00b67a]">94/100</span>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
                )}
              </div>

              <div>
                <h1 
                  className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight font-heading uppercase"
                  style={isEventPage ? { color: eventColors?.primary } : {}}
                >
                  {config.hero.headline}
                </h1>
                {/* Product Tags - Inline under title (desktop only) */}
                <div className="mt-2 hidden md:flex flex-wrap items-center gap-2">
                  {['Clinically Tested', 'Dermatologist Approved', 'Award Winning'].map((tag) => (
                    <span 
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full"
                      style={{
                        backgroundColor: isEventPage && eventColors ? eventColors.secondaryLight : 'rgba(0,0,0,0.04)',
                        color: isEventPage && eventColors ? (isDarkScheme ? eventColors.secondary : eventColors.primary) : 'inherit'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Made for Use Case Badge - Only for specific use-case pages, not quiz */}
                {useCaseName && !quizLabels.length && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-[#1d9bf0]/10 rounded-full">
                    <span className="w-3 h-3 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                      <Check className="w-2 h-2 text-white stroke-[3]" />
                    </span>
                    <span className="text-xs font-semibold text-[#1d9bf0]">
                      Made for {useCaseName}
                    </span>
                  </div>
                )}
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {config.hero.subheadline}
                </p>
                {/* Angle-based emphasis for event pages */}
                {isEventPage && currentAngleModifier.heroEmphasis && (
                  <p className="mt-2 text-sm font-medium" style={{ color: eventColors?.primary }}>
                    {currentAngleModifier.heroEmphasis}
                  </p>
                )}
                {/* Hotel Quality additional reinforcement */}
                {config.id === 'hotel-quality' && (
                  <p className="mt-2 text-sm font-medium text-green-700">
                    Same suppliers as Four Seasons & Ritz-Carlton, 50%+ less.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {config.hero.usps
                  .map(usp => {
                    // Replace "Made in USA" / "Made in the USA" with alternative for non-US visitors
                    if (!isUS && (usp.title.toLowerCase().includes('made in usa') || usp.title.toLowerCase().includes('made in the usa'))) {
                      return { ...usp, title: nonUsAlternativeUsp.title, description: nonUsAlternativeUsp.description };
                    }
                    return usp;
                  })
                  .map((usp, idx) => {
                  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
                    'support': Moon,
                    'temperature': Thermometer,
                    'hypoallergenic': Shield,
                    'location': MapPin,
                    'shield': Shield,
                    'clock': Clock,
                  };
                  const IconComponent = iconMap[usp.icon] || Moon;
                  return (
                    <div key={idx} className="flex items-start gap-3">
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isEventPage ? '' : ''}`}
                        style={isEventPage ? { backgroundColor: `${eventColors?.primary}1a` } : { backgroundColor: 'white', border: '1px solid #e2e8f0' }}
                      >
                        <span style={isEventPage ? { color: eventColors?.primary } : { color: '#004C99' }}>
                          <IconComponent className="w-4 h-4" />
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-sm">{usp.title}</div>
                        <div className="text-xs text-muted-foreground">{usp.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Contextual Carousel - Only shown when user has quiz tags */}
              {(() => {
                // Build contextual cards based on quiz tags
                const contextualCards: { id: string; title: string; description: string; image: string; icon: React.ReactNode }[] = [];
                
                // Sleep position card - only show user's specific position
                if (quizTags.includes('side-sleeper')) {
                  contextualCards.push({
                    id: 'side-sleeper',
                    title: 'Made for Side Sleepers',
                    description: 'Higher loft fills the shoulder gap, keeping your spine perfectly aligned all night.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-side_86d85ca5.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('back-sleeper')) {
                  contextualCards.push({
                    id: 'back-sleeper',
                    title: 'Perfect for Back Sleepers',
                    description: 'Cradles your neck curve with just the right support for natural spinal alignment.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-back_c9753bdc.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('stomach-sleeper')) {
                  contextualCards.push({
                    id: 'stomach-sleeper',
                    title: 'Designed for Stomach Sleepers',
                    description: 'Low profile support prevents neck strain while keeping you comfortable face-down.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-stomach_ac1d0f29.png',
                    icon: <Bed className="w-4 h-4" />
                  });
                } else if (quizTags.includes('mixed-sleeper')) {
                  contextualCards.push({
                    id: 'mixed-sleeper',
                    title: 'Adapts to Any Position',
                    description: 'Responsive fill adjusts as you move, providing perfect support in every position.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-alignment_ec09de72.png',
                    icon: <RefreshCw className="w-4 h-4" />
                  });
                }
                
                // Note: Pillow replacement messaging is now shown in a separate section above the CTA button
                
                // Neck pain card
                if (quizTags.includes('neck-pain') || quizTags.includes('alignment-issues')) {
                  contextualCards.push({
                    id: 'neck-support',
                    title: 'Neck Pain Relief',
                    description: 'Proper cervical alignment eliminates morning stiffness. Wake up without that familiar ache.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-neck-relief_b6ae0c8d.png',
                    icon: <Shield className="w-4 h-4" />
                  });
                }
                
                // Shoulder pain card
                if (quizTags.includes('shoulder-pain') || quizTags.includes('circulation-issues')) {
                  contextualCards.push({
                    id: 'shoulder-relief',
                    title: 'Shoulder Pressure Relief',
                    description: 'Contoured support reduces pressure points, preventing that 3am wake-up to switch sides.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-shoulder-relief_e25948cc.png',
                    icon: <Heart className="w-4 h-4" />
                  });
                }
                
                // Temperature card
                if (quizTags.includes('hot-sleeper') || quizTags.includes('temperature') || quizTags.includes('warm-sleeper')) {
                  contextualCards.push({
                    id: 'cooling',
                    title: 'Cooling Technology',
                    description: 'Breathable materials and airflow design keep you cool all night. No more flipping to the cool side.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-cooling-closeup_09b6cb4a.png',
                    icon: <Thermometer className="w-4 h-4" />
                  });
                }
                
                // Allergy card
                if (quizTags.includes('allergies') || quizTags.includes('dust-mites') || quizTags.includes('congested')) {
                  contextualCards.push({
                    id: 'hypoallergenic',
                    title: 'Hypoallergenic Protection',
                    description: 'Dust mite resistant materials reduce allergens by up to 80%. Breathe easier, sleep better.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-hypoallergenic_353d76e2.png',
                    icon: <Shield className="w-4 h-4" />
                  });
                }
                
                // Support issues card
                if (quizTags.includes('poor-support') || quizTags.includes('compression') || quizTags.includes('severe-support-issues')) {
                  contextualCards.push({
                    id: 'lasting-support',
                    title: 'Lasting Support',
                    description: 'Premium fill maintains shape night after night. No more constant fluffing or adjusting.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-lasting-support_7629007e.png',
                    icon: <Layers className="w-4 h-4" />
                  });
                }
                
                // Hotel quality card
                if (quizTags.includes('hotel-quality') || quizTags.includes('pillow-issue')) {
                  contextualCards.push({
                    id: 'hotel-quality',
                    title: 'Hotel-Grade Quality',
                    description: 'Same suppliers as Four Seasons and Ritz-Carlton. Luxury hotel sleep at a fraction of the price.',
                    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/card-hotel-quality_c6b1c824.png',
                    icon: <Star className="w-4 h-4" />
                  });
                }
                
                // Only render if we have cards
                if (contextualCards.length === 0) return null;
                
                // Only enable auto-switch if more than 3 cards
                const shouldAnimate = contextualCards.length > 3;
                
                // Update cards count for auto-switch (only if animating)
                const totalSlides = contextualCards.length;
                if (shouldAnimate && totalSlides !== contextualCardsCount) {
                  setTimeout(() => setContextualCardsCount(totalSlides), 0);
                }
                
                // Calculate visible cards based on screen size
                // Desktop: 3 cards, Mobile: 2 cards
                // If 3 or fewer cards, show all without animation
                const visibleStartIndex = shouldAnimate ? (contextualCarouselIndex % contextualCards.length) : 0;
                
                // Get cards to display
                const visibleCards = shouldAnimate 
                  ? [
                      contextualCards[visibleStartIndex],
                      contextualCards[(visibleStartIndex + 1) % contextualCards.length],
                      contextualCards[(visibleStartIndex + 2) % contextualCards.length]
                    ].filter(Boolean)
                  : contextualCards.slice(0, 3);
                
                // Swipe/drag handlers for carousel
                const handleDragStart = (clientX: number) => {
                  if (!shouldAnimate) return;
                  setIsDragging(true);
                  setDragStartX(clientX);
                  setDragCurrentX(clientX);
                };
                
                const handleDragMove = (clientX: number) => {
                  if (!isDragging) return;
                  setDragCurrentX(clientX);
                };
                
                const handleDragEnd = () => {
                  if (!isDragging) return;
                  
                  // Skip if in cooldown period
                  if (swipeCooldownRef.current) {
                    setIsDragging(false);
                    setDragStartX(0);
                    setDragCurrentX(0);
                    return;
                  }
                  
                  const diff = dragStartX - dragCurrentX;
                  const threshold = 50; // Minimum drag distance to trigger slide change
                  
                  if (Math.abs(diff) > threshold) {
                    // Set cooldown to prevent rapid triggers
                    swipeCooldownRef.current = true;
                    
                    if (diff > 0) {
                      // Swiped left - go to next
                      setSlideDirection('left');
                      setContextualCarouselIndex((prev) => (prev + 1) % contextualCards.length);
                    } else {
                      // Swiped right - go to previous
                      setSlideDirection('right');
                      setContextualCarouselIndex((prev) => (prev - 1 + contextualCards.length) % contextualCards.length);
                    }
                    
                    // Reset direction after animation and clear cooldown
                    setTimeout(() => {
                      setSlideDirection(null);
                      swipeCooldownRef.current = false;
                    }, 500);
                  }
                  
                  setIsDragging(false);
                  setDragStartX(0);
                  setDragCurrentX(0);
                };
                
                // Wheel handler for Apple Magic Mouse / trackpad horizontal swipe
                const handleWheel = (e: React.WheelEvent) => {
                  if (!shouldAnimate) return;
                  
                  // Skip if in cooldown period (prevents rapid multiple triggers)
                  if (swipeCooldownRef.current) return;
                  
                  // Check if this is a horizontal scroll (Magic Mouse/trackpad)
                  const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
                  if (!isHorizontalScroll) return;
                  
                  // Prevent page scroll when swiping carousel
                  e.preventDefault();
                  
                  const now = Date.now();
                  const timeSinceLastWheel = now - lastWheelTimeRef.current;
                  
                  // Reset accumulator if too much time has passed (gesture ended)
                  if (timeSinceLastWheel > 200) {
                    wheelAccumulatorRef.current = 0;
                  }
                  
                  lastWheelTimeRef.current = now;
                  wheelAccumulatorRef.current += e.deltaX;
                  
                  const threshold = 100; // Accumulated scroll needed to trigger slide change
                  
                  if (Math.abs(wheelAccumulatorRef.current) > threshold) {
                    // Set cooldown to prevent rapid triggers
                    swipeCooldownRef.current = true;
                    
                    if (wheelAccumulatorRef.current > 0) {
                      // Scrolled right - go to next
                      setSlideDirection('left');
                      setContextualCarouselIndex((prev) => (prev + 1) % contextualCards.length);
                    } else {
                      // Scrolled left - go to previous
                      setSlideDirection('right');
                      setContextualCarouselIndex((prev) => (prev - 1 + contextualCards.length) % contextualCards.length);
                    }
                    
                    // Reset direction after animation and clear cooldown
                    setTimeout(() => {
                      setSlideDirection(null);
                      swipeCooldownRef.current = false;
                    }, 500);
                    wheelAccumulatorRef.current = 0;
                  }
                };
                
                return (
                  <div className="mt-5">
                    {/* Carousel Container - Refined design matching page aesthetic */}
                    <div 
                      className="relative overflow-hidden rounded-xl bg-secondary/30 border border-border select-none"
                      style={{ cursor: shouldAnimate ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
                      onMouseDown={(e) => handleDragStart(e.clientX)}
                      onMouseMove={(e) => handleDragMove(e.clientX)}
                      onMouseUp={handleDragEnd}
                      onMouseLeave={handleDragEnd}
                      onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
                      onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
                      onTouchEnd={handleDragEnd}
                      onWheel={handleWheel}
                    >
                      {/* Title Header - Clean, minimal */}
                      <div className="px-4 pt-4 pb-3 border-b border-border/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-4 bg-[#1d9bf0] rounded-full"></div>
                            <h4 className="text-sm font-semibold text-foreground">Why Colagea is Right for You</h4>
                          </div>
                          <span className="text-xs text-muted-foreground bg-background/50 px-2 py-1 rounded-full">Based on your profile</span>
                        </div>
                      </div>
                      
                      {/* Cards Grid - Responsive based on card count */}
                      <div className="p-4">
                        <div className={`grid gap-3 ${
                          visibleCards.length === 1 
                            ? 'grid-cols-1' 
                            : visibleCards.length === 2 
                              ? 'grid-cols-2' 
                              : 'grid-cols-2 md:grid-cols-3'
                        }`}>
                          {visibleCards.map((card, idx) => (
                            <div 
                              key={`${card.id}-${shouldAnimate ? contextualCarouselIndex : 'static'}-${idx}`} 
                              className={`group flex flex-col rounded-lg border border-border bg-background overflow-hidden hover:border-[#1d9bf0]/30 transition-all duration-200 ${
                                visibleCards.length >= 3 && idx === 2 ? 'hidden md:flex' : ''
                              }`}
                              style={shouldAnimate ? { 
                                animation: `${slideDirection === 'right' ? 'slideInFromLeft' : 'slideInFromRight'} 0.4s ease-out forwards`,
                                animationDelay: `${idx * 0.05}s`,
                                opacity: 0
                              } : {}}
                            >
                              {/* Image - Compact with subtle overlay */}
                              <div className="relative w-full h-24 md:h-28 overflow-hidden bg-secondary">
                                <img 
                                  src={card.image} 
                                  alt={card.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  decoding="async"
                                />
                                {/* Subtle gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                              </div>
                              {/* Content */}
                              <div className="p-3">
                                <h4 className="text-xs font-semibold text-foreground leading-tight mb-1">{card.title}</h4>
                                <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-4">{card.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Dots Navigation - Refined style */}
                      {shouldAnimate && (
                        <div 
                          className="flex justify-center gap-1.5 pb-4"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {contextualCards.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setContextualCarouselIndex(idx);
                              }}
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                idx === contextualCarouselIndex 
                                  ? 'bg-[#1d9bf0] w-4' 
                                  : 'bg-border hover:bg-muted-foreground/50 w-1.5'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* OEKO-TEX Standard 100 Badge - Full width drawer style */}
              <div className="mt-3 mb-4">
                <button 
                  ref={oekoTexRef}
                  onClick={() => setOekoTexExpanded(!oekoTexExpanded)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 text-left"
                  style={{
                    boxShadow: oekoTexExpanded ? '0 4px 12px -2px rgba(0, 0, 0, 0.08)' : 'none',
                  }}
                >
                  {/* Badge header - thinner padding */}
                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer">
                    <div className="flex items-center gap-2.5">
                      {/* Clinical Results Icon */}
                      <div className="flex items-center justify-center bg-white rounded px-1.5 py-1 border border-gray-200">
                        <svg className="w-5 h-5 text-[#0066CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                      </div>
                      <span className="text-sm text-gray-700">Clinically Proven Results</span>
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400 ml-2">
                        <svg className="w-3.5 h-3.5 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/></svg>
                        Experts Approved
                      </span>
                    </div>
                    {/* Arrow indicator */}
                    <svg 
                      className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-300"
                      style={{ 
                        transform: oekoTexExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <path d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                  {/* Expanded content - conditionally rendered for iOS Safari compatibility */}
                  {oekoTexExpanded && (
                    <div className="border-t border-gray-200 animate-[fadeSlideIn_0.3s_ease-out]">
                      <div className="px-3 py-3">
                      {/* Trust headline */}
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                        <span className="font-semibold text-[#0066CC]">Backed by independent clinical studies.</span> Our Micro-Infusion formula is dermatologically tested and validated by trichology experts across 3 clinical trials.
                      </p>
                      {/* Key results */}
                      <div className="space-y-2 mb-3">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7"/></svg>
                          <span className="text-sm text-gray-700"><strong>93% of users</strong> report visibly thicker hair after 60 days</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7"/></svg>
                          <span className="text-sm text-gray-700"><strong>87% reduction</strong> in hair loss within the first 30 days</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7"/></svg>
                          <span className="text-sm text-gray-700"><strong>Dermatologically tested:</strong> Safe for all skin types including sensitive scalps</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7"/></svg>
                          <span className="text-sm text-gray-700"><strong>No parabens, sulfates, or silicones</strong>, clean formula</span>
                        </div>
                      </div>
                      {/* Footer note */}
                      <p className="text-xs text-gray-500 border-t border-gray-100 pt-2">
                        Based on independent clinical studies conducted over 120 days with 200+ participants.
                      </p>
                      </div>
                    </div>
                  )}
                </button>
              </div>

              {/* Quantity/Pricing Options - REDESIGNED PILLOWCASE BONUS */}
              <div data-product-selector>
                <div className="text-sm font-medium mb-2 font-heading uppercase tracking-wide" style={{marginBottom: '12px'}}>Select Your Plan</div>
              <div className="space-y-3">
                {currentPricing.filter(option => quantityOptions.includes(option.quantity)).map((option) => (
                  <div 
                    key={option.quantity} 
                    className={`group transition-all duration-200 ${option.bonus ? 'hover:scale-[1.01] hover:shadow-md rounded-xl' : ''} ${option.badge ? 'mt-1' : ''}`}
                    onMouseEnter={() => setHoveredQuantity(option.quantity)}
                    onMouseLeave={() => setHoveredQuantity(null)}
                  >
                    <button
                      onClick={() => setSelectedQuantity(option.quantity)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 relative ${
                        !option.bonus ? 'hover:scale-[1.01] hover:shadow-md' : ''
                      } ${
                        option.bonus ? 'rounded-b-none border-b' : ''
                      } ${
                        selectedQuantity === option.quantity
                          ? 'shadow-sm'
                          : isEventPage && eventColors ? '' : 'border-border group-hover:border-muted-foreground'
                      }`}
                    style={selectedQuantity === option.quantity && isEventPage && eventColors ? {
                      borderColor: borderColorSelected,
                      backgroundColor: accentBg
                    } : selectedQuantity === option.quantity ? {
                      borderColor: '#007AFF',
                      backgroundColor: 'rgba(0, 122, 255, 0.05)'
                    } : isEventPage && eventColors && hoveredQuantity === option.quantity ? {
                      borderColor: borderColorSelected,
                      backgroundColor: isDarkScheme ? accentBg : `${eventColors.primary}05`
                    } : isEventPage && eventColors ? {
                      borderColor: borderColorUnselected
                    } : {}}
                    >
                      {/* Badges container - flex layout for consistent spacing */}
                      <div className="absolute -top-2.5 right-3 flex items-center gap-1.5">
                        {option.badge && !(config.hideMostPopularBadge && option.badge === 'Most Popular') && (
                          <span 
                            className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                            style={isEventPage && eventColors ? {
                              backgroundColor: eventColors.primary,
                              color: 'white'
                            } : {
                              backgroundColor: '#004C99',
                              color: 'white'
                            }}
                          >
                            {option.badge}
                          </span>
                        )}
                        {/* Discount badge - always shown */}
                        <span 
                          className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                          style={{
                            backgroundColor: '#16a34a',
                            color: 'white'
                          }}
                        >
                          -{option.discount}%
                        </span>
                      </div>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{option.quantity === 1 ? '1-Month Cure' : option.quantity === 2 ? '2-Month Cure' : '4-Month Cure'}</div>
                          <div className="text-xs text-muted-foreground">
                            {option.quantity === 1 ? '3 treatments' : option.quantity === 2 ? '6 treatments' : '12 treatments'}
                            <span className={`transition-opacity duration-200 ${selectedQuantity === option.quantity ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                              {' \u00b7 '}{fp(option.price / (option.quantity === 1 ? 3 : option.quantity === 2 ? 6 : 12))}/treatment
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-lg">{fp(option.price)}</span>
                            <span className="text-sm text-muted-foreground line-through">{fp(option.originalPrice)}</span>
                          </div>
                          <div className="text-xs text-green-600 font-medium">Save {fp(option.savings)}</div>
                        </div>
                      </div>
                    </button>
                    
                    {/* Adjacent Pillowcase Bonus Box - Part of the selector, clickable */}
                    {option.bonus && (
                      <button
                        onClick={() => setSelectedQuantity(option.quantity)}
                        className={`w-full px-4 py-2 rounded-b-xl border-2 border-t-0 transition-all duration-200 flex items-center justify-between cursor-pointer ${
                          selectedQuantity === option.quantity
                            ? ''
                            : isEventPage && eventColors ? '' : 'border-border bg-secondary/20 group-hover:border-muted-foreground'
                        }`}
                        style={selectedQuantity === option.quantity && isEventPage && eventColors ? {
                          borderColor: borderColorSelected,
                          backgroundColor: accentBg
                        } : selectedQuantity === option.quantity ? {
                          borderColor: '#007AFF',
                          backgroundColor: 'rgba(0, 122, 255, 0.05)'
                        } : isEventPage && eventColors && hoveredQuantity === option.quantity ? {
                          borderColor: borderColorSelected,
                          backgroundColor: accentBg
                        } : isEventPage && eventColors ? {
                          borderColor: borderColorUnselected,
                          backgroundColor: accentBg
                        } : {}}
                      >
                        <div className="flex items-center gap-1.5">
                          <Gift 
                            className="w-3.5 h-3.5" 
                            style={selectedQuantity === option.quantity && isEventPage && eventColors ? {
                              color: eventColors.primary
                            } : selectedQuantity === option.quantity ? {
                              color: '#007AFF'
                            } : {
                              color: 'var(--muted-foreground)'
                            }}
                          />
                          <span 
                            className="text-xs"
                            style={selectedQuantity === option.quantity && isEventPage && eventColors ? {
                              color: eventColors.primary,
                              fontWeight: 500
                            } : selectedQuantity === option.quantity ? {
                              color: '#007AFF',
                              fontWeight: 500
                            } : {
                              color: 'var(--muted-foreground)'
                            }}
                          >+ Exclusive Mystery Gift</span>
                          {/* Timer on desktop only */}
                          {!timerExpired && (
                            <span className="hidden md:inline-flex items-center gap-1 text-[10px] text-muted-foreground ml-1">
                              <Clock className="w-2.5 h-2.5" />
                              <span className="tabular-nums">{formatTimeLeft(timeLeft)}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground line-through">$10</span>
                          <span className="text-xs font-medium text-green-600">FREE</span>
                        </div>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              </div>

              {/* Why Your Pillow Needs Replacing + CTA - Unified element for quiz users with old pillow */}
              {(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const quizTagsForReplace = urlParams.get('tags')?.split(',').filter(Boolean) || [];
                const needsReplacement = quizTagsForReplace.includes('old-pillow') || quizTagsForReplace.includes('very-old-pillow') || quizTagsForReplace.includes('danger');
                
                if (!needsReplacement) return null;
                
                return (
                  <div className="rounded-xl overflow-hidden bg-secondary/30 border border-border">
                    {/* Why Your Pillow Needs Replacing - Top part */}
                    <div className="px-4 pt-4 pb-3 border-b border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1 h-4 bg-[#1d9bf0] rounded-full"></div>
                          <h4 className="text-sm font-semibold text-foreground">Why Your Pillow Needs Replacing</h4>
                        </div>
                        {/* Alarm-style pillow age badge - light background with black text */}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          quizTagsForReplace.includes('very-old-pillow') 
                            ? 'bg-[#007AFF]/10 text-foreground border border-[#007AFF]/20' 
                            : 'bg-[#f5c542]/10 text-foreground border border-[#f5c542]/20'
                        }`}>
                          <span className={`w-2 h-2 rounded-full animate-pulse ${
                            quizTagsForReplace.includes('very-old-pillow') ? 'bg-[#007AFF]' : 'bg-[#f5c542]'
                          }`}></span>
                          {quizTagsForReplace.includes('very-old-pillow') ? '5+ years old' : '2-5 years old'}
                        </div>
                      </div>
                    </div>
                    {/* Content section - matching carousel padding */}
                    <div className="p-4">
                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">60% support loss</span> – Old pillows lose structural integrity</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">2 million dust mites</span> – Accumulate in pillows over 2 years</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#1d9bf0] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80"><span className="font-semibold">Neck strain increases</span> – Poor support leads to morning pain</p>
                        </div>
                      </div>
                    </div>
                    {/* CTA Button - Bottom part, visually attached */}
                    <div className="px-4 pb-4">
                      <button 
                        id="main-cta-button" 
                        onClick={() => {
                          // Track AddToCart before opening checkout popup
                          try {
                            console.log('[CTA Click] selectedSize:', selectedSize, 'selectedQuantity:', selectedQuantity);
                            console.log('[CTA Click] currentPricing:', currentPricing);
                            const size = selectedSize === 'Standard' ? 'standard' as const : 'king' as const;
                            const qty = selectedQuantity as 1 | 2 | 4;
                            const pricing = currentPricing.find(p => p.quantity === selectedQuantity);
                            console.log('[CTA Click] pricing found:', pricing);
                            if (pricing) {
                              const productData = getProductDataForTracking(size, qty, pricing.price, currencyCode);
                              console.log('[CTA Click] productData:', productData);
                              trackAddToCart(productData);
                              const ga4Item = createMainProductItem(selectedSize, qty, pricing.price, pricing.originalPrice);
                              console.log('[CTA Click] ga4Item:', ga4Item);
                              trackGA4AddToCart([ga4Item], pricing.price);
                              console.log('[CTA Click] Tracking fired successfully');
                            } else {
                              console.warn('[CTA Click] No pricing found for quantity:', selectedQuantity);
                            }
                          } catch (error) {
                            console.error('[CTA Click] Error during tracking:', error);
                          }
                          setShowOrderPopup(true);
                        }}
                        className="w-full text-white font-semibold py-4 rounded-full text-lg transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                        style={{ 
                          backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
                          boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`,
                          ['--event-primary' as string]: isEventPage && eventColors ? eventColors.primary : '#007AFF'
                        }}
                        onMouseEnter={(e) => {
                          const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                          e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                          e.currentTarget.style.filter = 'brightness(0.95)';
                        }}
                        onMouseLeave={(e) => {
                          const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                          e.currentTarget.style.filter = 'brightness(1)';
                        }}
                      >
                        {getPageCTAWithDiscount(currentPricing.find(p => p.quantity === selectedQuantity)?.discount || 0)}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Regular CTA button with Welcome Pack blending behind */}
              {(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const quizTagsForReplace = urlParams.get('tags')?.split(',').filter(Boolean) || [];
                const needsReplacement = quizTagsForReplace.includes('old-pillow') || quizTagsForReplace.includes('very-old-pillow') || quizTagsForReplace.includes('danger');
                
                if (needsReplacement) return null;
                
                return (
                  <div className="relative">
                    {/* Welcome Pack - on top, slightly narrower than button for tab effect */}
                    <div className="pb-7 pt-4 px-5 rounded-t-xl border border-b-0 border-border/40 bg-white animate-[fadeIn_0.4s_ease-out_0.2s_both]">
                      <div className="flex items-center gap-2.5 mb-3.5">
                        <Gift className="w-4 h-4 text-[#007AFF]" />
                        <span className="text-xs font-sans tracking-wide font-semibold text-foreground">Free Welcome Pack</span>
                        <span className="text-[10px] text-muted-foreground ml-auto uppercase tracking-wider"><span className="text-emerald-600 font-medium">$59.97</span> VALUE INCLUDED FOR FREE</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        {/* Derma+ Brush */}
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-7 h-7 rounded-md bg-[#007AFF]/8 flex items-center justify-center flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-[#007AFF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="1" width="12" height="10" rx="2"/><line x1="9" y1="4" x2="9" y2="8"/><line x1="12" y1="4" x2="12" y2="8"/><line x1="15" y1="4" x2="15" y2="8"/><path d="M8 11v4a4 4 0 008 0v-4"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium text-foreground leading-tight">Derma+ Brush</div>
                            <div className="text-[10px] text-muted-foreground line-through">$29.99</div>
                          </div>
                        </div>
                        <div className="text-muted-foreground/30 text-xs">+</div>
                        {/* E-Book */}
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-7 h-7 rounded-md bg-[#007AFF]/8 flex items-center justify-center flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-[#007AFF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><path d="M8 7h8"/><path d="M8 11h6"/></svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium text-foreground leading-tight">Hair Guide</div>
                            <div className="text-[10px] text-muted-foreground line-through">$19.99</div>
                          </div>
                        </div>
                        <div className="text-muted-foreground/30 text-xs">+</div>
                        {/* Free Shipping */}
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-7 h-7 rounded-md bg-[#007AFF]/8 flex items-center justify-center flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-[#007AFF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium text-foreground leading-tight">Free Shipping</div>
                            <div className="text-[10px] text-muted-foreground line-through">$9.99</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* CTA Button - full width, fully rounded, overlaps the pack bottom */}
                    <button 
                      id="main-cta-button" 
                      onClick={() => {
                        // Track AddToCart before opening checkout popup
                        try {
                          console.log('[CTA Click Sticky] selectedSize:', selectedSize, 'selectedQuantity:', selectedQuantity);
                          const size = selectedSize === 'Standard' ? 'standard' as const : 'king' as const;
                          const qty = selectedQuantity as 1 | 2 | 4;
                          const pricing = currentPricing.find(p => p.quantity === selectedQuantity);
                          console.log('[CTA Click Sticky] pricing found:', pricing);
                          if (pricing) {
                            const productData = getProductDataForTracking(size, qty, pricing.price, currencyCode);
                            console.log('[CTA Click Sticky] productData:', productData);
                            trackAddToCart(productData);
                            const ga4Item = createMainProductItem(selectedSize, qty, pricing.price, pricing.originalPrice);
                            console.log('[CTA Click Sticky] ga4Item:', ga4Item);
                            trackGA4AddToCart([ga4Item], pricing.price);
                            console.log('[CTA Click Sticky] Tracking fired successfully');
                          } else {
                            console.warn('[CTA Click Sticky] No pricing found for quantity:', selectedQuantity);
                          }
                        } catch (error) {
                          console.error('[CTA Click Sticky] Error during tracking:', error);
                        }
                        setShowOrderPopup(true);
                      }}
                      className="relative z-10 -mt-3 w-full text-white font-semibold py-4 rounded-full text-lg transition-all duration-300 animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                      style={{ 
                        backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
                        boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`,
                        ['--event-primary' as string]: isEventPage && eventColors ? eventColors.primary : '#007AFF'
                      }}
                      onMouseEnter={(e) => {
                        const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                        e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                        e.currentTarget.style.filter = 'brightness(0.95)';
                      }}
                      onMouseLeave={(e) => {
                        const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                        e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                        e.currentTarget.style.filter = 'brightness(1)';
                      }}
                    >
                      {getPageCTAWithDiscount(currentPricing.find(p => p.quantity === selectedQuantity)?.discount || 0)}
                    </button>
                  </div>
                );
              })()}

              {/* NEW TECHNOLOGY + Sold Out badges - below CTA */}
              <div className="flex flex-col gap-2 mb-2" style={{ marginTop: '-8px', marginBottom: '12px' }}>
                {/* New Technology badge */}
                <div className="flex items-center gap-2.5 bg-gradient-to-r from-[#f0f7ff] to-[#e8f4fd] border border-[#0066CC]/15 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-center w-7 h-7 bg-[#0066CC] rounded-md flex-shrink-0">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                  </div>
                  <div>
                    <span className="text-xs font-bold text-[#0066CC] uppercase tracking-wide">New Technology</span>
                    <p className="text-[11px] text-gray-600 leading-tight">4x faster absorption for visible results</p>
                  </div>
                </div>

              </div>

              {/* Compact urgency module - visually attached to CTA */}
              <div className="mt-[8px]">
                {/* Dynamic location-based shipping message - aligned left, above stock bar */}
                <div className="mb-2">
                  <ShippingMessage variant="inline" />
                </div>
                <StockInventoryBar stockLevel={stockLevel} countdown={countdown} timerExpired={timerExpired} />
              </div>

              {/* Checkmark badges - scrollable on mobile with gradient fade */}
              <div className="relative -mx-4 md:mx-0">
                {/* Left gradient fade for scroll indication */}
                <div 
                  className={`absolute left-0 top-0 bottom-1 w-8 z-10 pointer-events-none transition-opacity duration-200 ${showLeftGradient ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    background: isEventPage && eventColors 
                      ? `linear-gradient(to right, ${eventColors.background || 'hsl(var(--background))'}, transparent)`
                      : 'linear-gradient(to right, hsl(var(--background)), transparent)'
                  }}
                />
                <div 
                  ref={trustBadgesRef}
                  className="flex items-center justify-start gap-2 text-xs text-muted-foreground overflow-x-auto scrollbar-hide pb-1 px-4 md:px-0"
                >
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.guarantee_text}
                  </span>
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.shipping_text}
                  </span>
                  <span 
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border whitespace-nowrap flex-shrink-0 ${isEventPage && eventColors ? 'bg-white/80 dark:bg-white/10' : 'bg-secondary/30'}`}
                    style={{ 
                      borderColor: isEventPage && eventColors ? `${eventColors.primary}25` : undefined
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: isEventPage && eventColors ? eventColors.primary : '#16a34a' }} /> {siteSettings.origin_text}
                  </span>
                </div>
                {/* Right gradient fade for scroll indication */}
                <div 
                  className={`absolute right-0 top-0 bottom-1 w-8 pointer-events-none transition-opacity duration-200 ${showRightGradient ? 'opacity-100' : 'opacity-0'}`}
                  style={{ 
                    background: isEventPage && eventColors 
                      ? `linear-gradient(to left, ${eventColors.background || 'hsl(var(--background))'}, transparent)`
                      : 'linear-gradient(to left, hsl(var(--background)), transparent)'
                  }}
                />
              </div>

              {/* Product Tabs - How to Use / Ingredients / Clinical Results */}
              <ProductTabs />

              <div className="border-t border-border pt-4 space-y-0" style={{paddingTop: '0px'}}>
                {/* What's Included - First accordion item */}
                <div className="border-b border-border">
                  <button
                    onClick={() => setExpandedAccordion(expandedAccordion === 'whats-included' ? null : 'whats-included')}
                    className="w-full flex items-center justify-between py-3 text-sm font-medium"
                  >
                    What's Included
                    {expandedAccordion === 'whats-included' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedAccordion === 'whats-included' && (
                    <div className="pb-3 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <span className="text-sm">{selectedQuantity === 1 ? '1-Month Cure (3 serum vials + applicator)' : selectedQuantity === 2 ? '2-Month Cure (6 serum vials + applicator)' : '4-Month Cure (12 serum vials + applicator)'}</span>
                        </div>
                      </div>
                      {selectedQuantity === 4 && (
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Gift className="w-3.5 h-3.5 text-green-600" />
                          </div>
                          <div>
                            <span className="text-sm">Mystery Gift</span>
                            <span className="text-xs text-green-600 ml-1">(FREE)</span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5 text-muted-foreground">
                            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <span className="text-sm">Hair Regrowth Guide (E-Book)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="text-sm">60-Day Money-Back Guarantee</span>
                      </div>
                    </div>
                  )}
                </div>
                {[
                  { id: 'usage', title: 'Usage Instructions', content: null },
                  { id: 'formula', title: 'Formula & Ingredients', content: null },
                  { id: 'returns', title: '60-Day Returns', content: null },

                ].map((item) => (
                  <div key={item.id} className="border-b border-border">
                    <button
                      onClick={() => setExpandedAccordion(expandedAccordion === item.id ? null : item.id)}
                      className="w-full flex items-center justify-between py-3 text-sm font-medium"
                    >
                      {item.title}
                      {expandedAccordion === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {expandedAccordion === item.id && (
                      <div className="pb-3 text-sm text-muted-foreground">
                        {item.id === 'usage' && (
                          <p>Apply twice per week on clean, dry hair. Screw the micro-infusion applicator onto the serum vial and press gently on targeted scalp areas. Each session takes under 5 minutes. For best results, use consistently for 8-12 weeks.</p>
                        )}
                        {item.id === 'formula' && (
                          <div className="space-y-3">
                            <p className="mb-2">Colagea Micro-Infuse serum is a clinical-grade formula with 5 key active ingredients.</p>
                            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Acetyl Tetrapeptide-3</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Saw Palmetto Extract</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Caffeine Complex</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Menthol (Cooling Agent)</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> Copper Tripeptide-1</li>
                              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> No Parabens or Sulfates</li>
                            </ul>
                          </div>
                        )}
                        {item.id === 'returns' && (
                          <p>Not satisfied? Return within 60 days for a full refund. No questions asked. We stand behind our product with a risk-free guarantee.</p>
                        )}

                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Sold Out Badge */}
              <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                <span className="flex items-center justify-center w-5 h-5 bg-orange-500 rounded flex-shrink-0">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                </span>
                <div>
                  <span className="text-xs font-bold text-orange-700 uppercase tracking-wide">Sold Out 7 Times</span>
                  <p className="text-[10px] text-orange-600/80 leading-tight">High demand — this product sold out 7 times last year. Order now to secure yours.</p>
                </div>
              </div>

              {/* Quiz Link - Hide when user already came from quiz */}
              {quizTags.length === 0 && (
                <div className="mt-4">
                  {/* Desktop: Single line */}
                  <button
                    onClick={() => setIsQuizModalOpen(true)}
                    className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                  >
                    Not sure which cure is right for you? Find out in 60 seconds →
                  </button>
                  {/* Mobile: Two lines with emphasis */}
                  <div className="sm:hidden flex flex-col items-center gap-1">
                    <span className="text-sm text-muted-foreground">Not sure which cure is right for you?</span>
                    <button
                      onClick={() => setIsQuizModalOpen(true)}
                      className="text-sm font-medium text-foreground underline underline-offset-4 transition-colors"
                    >
                      Find out in 60 seconds →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PRESS BAR */}
      <section 
        className="py-8 border-y"
        style={isEventPage && eventColors ? {
          borderColor: `${eventColors.primary}33`,
          backgroundColor: eventColors.accentDark
        } : {
          borderColor: 'var(--border)',
          backgroundColor: '#E0E8F2'
        }}
      >
        <div className="container px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 mb-6">
            <div className="flex items-center gap-2">
              <TrustpilotStars className="h-4" />
              <span className="text-sm font-medium"><CustomerCount format="short" /> Happy Customers</span>
            </div>
            <span className="text-muted-foreground text-sm">As Seen In</span>
          </div>
          <PressLogos backgroundColor={isEventPage ? (eventColors?.accentDark || '#ffe8e8') : '#E0E8F2'} />
        </div>
      </section>

      {/* EVENT GIFT SECTION - Dynamic content based on event type */}
      {isEventPage && eventContent[config.id] && (() => {
        const content = eventContent[config.id];
        const colors = eventColors || eventColorSchemes['valentine-gift'];
        const FeatureIcon = ({ icon }: { icon: 'gift' | 'check' | 'heart' }) => {
          switch (icon) {
            case 'gift': return <Gift className="w-4 h-4" />;
            case 'check': return <Check className="w-4 h-4" />;
            case 'heart': return <Heart className="w-4 h-4" />;
          }
        };
        const CardIcon = ({ icon }: { icon: 'moon' | 'gift' | 'sparkles' }) => {
          switch (icon) {
            case 'moon': return <Moon className="w-8 h-8" style={{ color: colors.primary }} />;
            case 'gift': return <Gift className="w-8 h-8" style={{ color: colors.primary }} />;
            case 'sparkles': return <Sparkles className="w-8 h-8" style={{ color: colors.primary }} />;
          }
        };
        
        return (
          <section className="py-12 bg-white">
            <div className="container px-4">
              {/* Event Banner */}
              <div 
                className="relative overflow-hidden rounded-2xl p-8 md:p-12 text-white"
                style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
                  {/* Left: Gift Image */}
                  <div className="w-full lg:w-1/3 flex-shrink-0">
                    <div className="relative">
                      <img 
                        src={content.image} 
                        alt="Gift-Ready Packaging" 
                        className="w-full rounded-xl shadow-2xl"
                        loading="lazy"
                        decoding="async"
                      />
                      <div 
                        className="absolute -bottom-3 -right-3 bg-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                        style={{ color: colors.primary }}
                      >
                        Gift-Ready
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Content */}
                  <div className="flex-1 text-center lg:text-left">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm mb-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                      <Heart className="w-4 h-4 fill-current" />
                      <span>A Thoughtful {eventNames[config.id] || 'Holiday'} Gift</span>
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading uppercase">
                      <span className="block md:inline">{content.headline}</span>
                      <br className="hidden md:block" />
                      <span className="md:hidden"> </span>
                      <span className="block md:inline">{content.subheadline}</span>
                    </h2>
                    
                    <p className="text-white/90 text-lg mb-6 max-w-xl">
                      {content.description}
                    </p>
                    
                    <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm">
                      {content.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                          <FeatureIcon icon={feature.icon} />
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Why It's the Perfect Gift - Swipeable on mobile, grid on desktop */}
              {(() => {
                const [activeCardIndex, setActiveCardIndex] = useState(0);
                const carouselRef = useRef<HTMLDivElement>(null);
                const [isGiftDragging, setIsGiftDragging] = useState(false);
                const [giftDragStartX, setGiftDragStartX] = useState(0);
                const [giftScrollStart, setGiftScrollStart] = useState(0);
                
                const handleScroll = () => {
                  if (carouselRef.current && !isGiftDragging) {
                    const scrollLeft = carouselRef.current.scrollLeft;
                    const cardWidth = carouselRef.current.offsetWidth;
                    const newIndex = Math.round(scrollLeft / cardWidth);
                    setActiveCardIndex(newIndex);
                  }
                };
                
                const scrollToCard = (index: number) => {
                  if (carouselRef.current) {
                    const cardWidth = carouselRef.current.offsetWidth;
                    carouselRef.current.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
                  }
                };
                
                // Mouse drag handlers for desktop
                const handleGiftDragStart = (e: React.MouseEvent) => {
                  if (!carouselRef.current) return;
                  setIsGiftDragging(true);
                  setGiftDragStartX(e.clientX);
                  setGiftScrollStart(carouselRef.current.scrollLeft);
                };
                
                const handleGiftDragMove = (e: React.MouseEvent) => {
                  if (!isGiftDragging || !carouselRef.current) return;
                  e.preventDefault();
                  const diff = giftDragStartX - e.clientX;
                  carouselRef.current.scrollLeft = giftScrollStart + diff;
                };
                
                const handleGiftDragEnd = () => {
                  if (!isGiftDragging || !carouselRef.current) return;
                  setIsGiftDragging(false);
                  // Snap to nearest card
                  const cardWidth = carouselRef.current.offsetWidth;
                  const newIndex = Math.round(carouselRef.current.scrollLeft / cardWidth);
                  scrollToCard(newIndex);
                  setActiveCardIndex(newIndex);
                };
                
                return (
                  <div className="mt-12">
                    {/* Mobile: Swipeable carousel */}
                    <div className="md:hidden">
                      <div 
                        ref={carouselRef}
                        onScroll={handleScroll}
                        onMouseDown={handleGiftDragStart}
                        onMouseMove={handleGiftDragMove}
                        onMouseUp={handleGiftDragEnd}
                        onMouseLeave={handleGiftDragEnd}
                        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 select-none"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: isGiftDragging ? 'grabbing' : 'grab' }}
                      >
                        {content.cards.map((card, idx) => (
                          <div key={idx} className="flex-shrink-0 w-full snap-center px-2">
                            <div className="text-center">
                              {card.image ? (
                                <div className="w-full aspect-video mx-auto mb-4 rounded-xl overflow-hidden shadow-lg">
                                  <img 
                                    src={card.image} 
                                    alt={card.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              ) : (
                                <div 
                                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: colors.accent }}
                                >
                                  <CardIcon icon={card.icon} />
                                </div>
                              )}
                              <h3 className="font-semibold text-lg mb-2">{card.title}</h3>
                              <p className="text-muted-foreground text-sm px-4">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Dot indicators */}
                      <div className="flex justify-center gap-2 mt-6">
                        {content.cards.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => scrollToCard(idx)}
                            className={`h-2.5 rounded-full transition-all duration-300 ${activeCardIndex === idx ? 'w-6' : 'w-2.5 opacity-50'}`}
                            style={{ backgroundColor: colors.primary }}
                            aria-label={`Go to card ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Desktop: Grid layout */}
                    <div className="hidden md:grid md:grid-cols-3 gap-8">
                      {content.cards.map((card, idx) => (
                        <div key={idx} className="text-center">
                          {card.image ? (
                            <div className="w-full aspect-video mx-auto mb-4 rounded-xl overflow-hidden shadow-lg">
                              <img 
                                src={card.image} 
                                alt={card.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: colors.accent }}
                            >
                              <CardIcon icon={card.icon} />
                            </div>
                          )}
                          <h3 className="font-semibold text-lg mb-2">{card.title}</h3>
                          <p className="text-muted-foreground text-sm">
                            {card.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>
        );
      })()}

      {/* BENEFITS SECTION */}
      <section id="benefits" className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : { backgroundColor: '#EBF1F8' }}>
        <div className="container px-4">
          <div className="text-center mb-12">
            {/* Angle-based trust badge for event pages */}
            {isEventPage && currentAngleModifier.trustBadge && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ backgroundColor: `${eventColors?.primary}15` }}>
                <Check className="w-4 h-4" style={{ color: eventColors?.primary }} />
                <span className="text-sm font-medium" style={{ color: eventColors?.primary }}>
                  {currentAngleModifier.trustBadge}
                </span>
              </div>
            )}
            <h2 className="text-3xl font-semibold tracking-tight font-heading uppercase">{config.benefits.sectionTitle}</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              {config.benefits.sectionSubtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {(() => {
              const benefitImages = [
                { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-follicle-reactivation-DE9h28jiMJkWRRwK2yLTLY.webp', srcset: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-follicle-reactivation-DE9h28jiMJkWRRwK2yLTLY.webp 800w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-follicle-reactivation-Zve93D6yxHbEh7rZqGBY3f.png 2048w' },
                { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/happy-man-mirror-bS9QroVXPQ5AM4k6GSBkXx.webp', srcset: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/happy-man-mirror-bS9QroVXPQ5AM4k6GSBkXx.webp 800w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/happy-man-mirror-MoxRg4uemeRgDApEVDNwYG.png 2048w' },
                { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-YsyfBpGiRjpqdjxKzxRwdk.webp', srcset: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-YsyfBpGiRjpqdjxKzxRwdk.webp 800w, https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-iDR5aqT4n7EHmDdYYxLQDH.png 2048w' }
              ];
              return config.benefits.benefits.map((benefit, idx) => (
                <div key={idx} className="text-center">
                  <div className="aspect-square md:aspect-[3/4] bg-secondary rounded-xl mb-4 overflow-hidden">
                    <img 
                      src={benefitImages[idx]?.src || benefitImages[0].src} 
                      srcSet={benefitImages[idx]?.srcset || benefitImages[0].srcset}
                      sizes="(max-width: 768px) 400px, (max-width: 1200px) 800px, 1200px"
                      loading="lazy"
                      decoding="async"
                      alt={benefit.title} 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* HAIR TYPE COMPATIBILITY GUIDE */}
      {(() => {
        const hairTypes = [
          {
            type: 'thinning-crown',
            title: 'Thinning Crown',
            description: 'Targets vertex area where follicles weaken first',
            icon: (
              <svg viewBox="0 0 60 60" className="w-full h-full">
                <circle cx="30" cy="28" r="18" fill="#EBF1F8" stroke="#0066CC" strokeWidth="1.5" />
                <path d="M20 20c2-4 6-7 10-8s8 0 10 3" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round" />
                <path d="M18 24c1-2 4-4 7-5" stroke="#1a1a2e" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="3 3" />
                <path d="M35 17c2 1 4 3 5 5" stroke="#1a1a2e" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="3 3" />
                <circle cx="30" cy="24" r="5" fill="none" stroke="#0066CC" strokeWidth="1" strokeDasharray="2 2" />
                <text x="30" y="27" textAnchor="middle" fontSize="6" fill="#0066CC" fontWeight="bold">!</text>
                <circle cx="30" cy="50" r="3" fill="#0066CC" opacity="0.3" />
              </svg>
            ),
          },
          {
            type: 'receding-hairline',
            title: 'Receding Hairline',
            description: 'Strengthens and reactivates the frontal zone',
            icon: (
              <svg viewBox="0 0 60 60" className="w-full h-full">
                <circle cx="30" cy="28" r="18" fill="#EBF1F8" stroke="#0066CC" strokeWidth="1.5" />
                <path d="M16 28c2-8 8-14 14-15s10 1 14 8" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round" />
                <path d="M22 18c1-1 3-2 5-2" stroke="#1a1a2e" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="3 3" />
                <path d="M38 20c1 1 2 3 2 5" stroke="#1a1a2e" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="3 3" />
                <path d="M20 22l4-2" stroke="#0066CC" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M22 19l2 3" stroke="#0066CC" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="30" cy="50" r="3" fill="#0066CC" opacity="0.3" />
              </svg>
            ),
          },
          {
            type: 'overall-thinning',
            title: 'Overall Thinning',
            description: 'Full scalp coverage for diffuse hair loss',
            icon: (
              <svg viewBox="0 0 60 60" className="w-full h-full">
                <circle cx="30" cy="28" r="18" fill="#EBF1F8" stroke="#0066CC" strokeWidth="1.5" />
                <path d="M16 26c3-8 8-12 14-13s10 2 13 10" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round" />
                <line x1="22" y1="16" x2="23" y2="22" stroke="#1a1a2e" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 3" />
                <line x1="28" y1="14" x2="28" y2="20" stroke="#1a1a2e" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 3" />
                <line x1="34" y1="14" x2="33" y2="20" stroke="#1a1a2e" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 3" />
                <line x1="39" y1="17" x2="38" y2="23" stroke="#1a1a2e" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 3" />
                <circle cx="25" cy="24" r="2" fill="none" stroke="#0066CC" strokeWidth="1" strokeDasharray="1 1" />
                <circle cx="35" cy="22" r="2" fill="none" stroke="#0066CC" strokeWidth="1" strokeDasharray="1 1" />
                <circle cx="30" cy="28" r="2" fill="none" stroke="#0066CC" strokeWidth="1" strokeDasharray="1 1" />
                <circle cx="30" cy="50" r="3" fill="#0066CC" opacity="0.3" />
              </svg>
            ),
          },
        ];
        
        return (
          <section 
            className={`py-8 border-y ${isEventPage ? '' : 'border-border bg-secondary/30'}`}
            style={isEventPage ? { borderColor: `${eventColors?.primary}33`, backgroundColor: eventColors?.accentDark } : {}}
          >
            <div className="container px-4">
              <div className="flex items-center justify-center gap-2 mb-6">
                <span className="text-sm font-medium">Works for Every Hair Type</span>
                <span className="text-muted-foreground text-sm">• Adapts to Your Needs</span>
              </div>
              
              {/* Hair Types Row */}
              <div className="flex items-stretch justify-between sm:justify-center gap-2 sm:gap-6 md:gap-10 px-2 sm:px-0">
                {hairTypes.map((ht, idx) => (
                  <div key={idx} className="flex-1 sm:flex-initial flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3">
                    <div className="relative w-full sm:w-auto">
                      <div className="aspect-square sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#EBF1F8] p-2">
                        {ht.icon}
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center bg-[#0066CC]">
                        <Check className="w-3 h-3 text-background" />
                      </div>
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-xs sm:text-sm font-medium">{ht.title}</p>
                      <p className="text-xs text-muted-foreground hidden sm:block">{ht.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Trust badges below */}
              <div className="flex items-center justify-center gap-6 mt-6 text-xs">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ borderColor: '#0066CC', color: '#0066CC' }}><Heart className="w-3.5 h-3.5" style={{ fill: '#0066CC' }} /> All Hair Types</span>
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ borderColor: '#0066CC', color: '#0066CC' }}><Moon className="w-3.5 h-3.5" style={{ fill: '#0066CC' }} /> Visible Results</span>
              </div>
            </div>
          </section>
        );
      })()}

      {/* WHY WE'RE CHEAPER - Direct to Consumer Section */}
      <section 
        className="py-16 text-white"
        style={{ backgroundColor: isEventPage && eventColors ? eventColors.secondary : '#004C99' }}
      >
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Explanation */}
            <div>
              <div 
                className="text-sm font-medium mb-2"
                style={{ color: isEventPage && eventColors ? eventColors.accent : '#4da6ff' }}
              >Direct to Consumer</div>
              <h2 className="text-3xl font-semibold tracking-tight mb-6 font-heading uppercase">
                Big Pharma Charges You 10x. We Don't.
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                The hair loss industry is a $4.5 billion market dominated by pharmaceutical giants. They spend more on advertising and executive bonuses than on actual R&D. Their products cost pennies to make, yet they charge you hundreds. You're not paying for innovation. You're paying for their shareholders.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                We built Colagea differently. No distributors. No pharmacy shelf fees. No celebrity endorsement budgets. We engineer our patented formula in Switzerland, ship it directly to your door in the US, and pass the savings to you. Same clinical-grade ingredients. A fraction of the price.
              </p>
              
              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Store className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.accent : '#4da6ff' }} />
                  <span>No pharmacy markup</span>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.accent : '#4da6ff' }} />
                  <span>{isUS ? 'Free US shipping' : nonUsBadgeText}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.accent : '#4da6ff' }} />
                  <span>Lab direct pricing</span>
                </div>
              </div>
            </div>

            {/* Right: Comparison Bar Graphs */}
            <div className="space-y-8">
              {/* Big Pharma Price Breakdown */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-white/60">Big Pharma Price (per month)</span>
                  <span className="text-lg font-bold">{fp(standardPricing[0].originalPrice)}</span>
                </div>
                <div className="relative">
                  {(() => {
                    return (
                      <>
                        <div className="h-14 bg-white/10 rounded-lg overflow-hidden flex">
                          <div className="h-full bg-red-500/80 flex items-center justify-center text-[11px] sm:text-sm font-medium" style={{ width: '12%' }}>
                            <span className="px-0.5">R&D</span>
                          </div>
                          <div className="h-full bg-red-400/80 flex items-center justify-center text-[11px] sm:text-sm font-medium" style={{ width: '28%' }}>
                            <span className="px-0.5">Marketing</span>
                          </div>
                          <div className="h-full bg-red-300/80 flex items-center justify-center text-[11px] sm:text-sm font-medium text-gray-800" style={{ width: '18%' }}>
                            <span className="px-0.5">Distributor</span>
                          </div>
                          <div className="h-full bg-red-200/80 flex items-center justify-center text-[11px] sm:text-sm font-medium text-gray-800" style={{ width: '15%' }}>
                            <span className="px-0.5">Pharmacy</span>
                          </div>
                          <div className="h-full bg-red-100/80 flex items-center justify-center text-[11px] sm:text-sm font-medium text-gray-800" style={{ width: '27%' }}>
                            <span className="px-0.5">Profit</span>
                          </div>
                        </div>
                        <div className="flex mt-2 text-xs text-white/40">
                          <span style={{ width: '12%' }}>12%</span>
                          <span style={{ width: '28%' }}>28%</span>
                          <span style={{ width: '18%' }}>18%</span>
                          <span style={{ width: '15%' }}>15%</span>
                          <span style={{ width: '27%' }}>27%</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Colagea Direct Price */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-medium ${isEventPage ? 'text-white' : 'text-[#4da6ff]'}`}>Colagea Direct Price (per month)</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/40 line-through">{fp(standardPricing[0].originalPrice)}</span>
                    <span className={`text-lg font-bold ${isEventPage ? 'text-white' : 'text-[#4da6ff]'}`}>{fp((currentPricing.find(p => p.quantity === selectedQuantity)?.price || standardPricing[0].price) / selectedQuantity)}</span>
                  </div>
                </div>
                <div className="relative">
                  {(() => {
                    return (
                      <>
                        <div className="h-14 bg-white/10 rounded-lg overflow-hidden flex">
                          <div className={`h-full flex items-center justify-center text-[11px] sm:text-sm font-medium ${isEventPage ? 'bg-white/90 text-gray-800' : 'bg-[#4da6ff] text-white'}`} style={{ width: '25%' }}>
                            <span className="px-0.5">R&D</span>
                          </div>
                          <div className={`h-full flex items-center justify-center text-[11px] sm:text-sm font-medium ${isEventPage ? 'bg-white/70 text-gray-800' : 'bg-[#4da6ff]/80 text-white'}`} style={{ width: '12%' }}>
                            <span className="px-0.5">Ship</span>
                          </div>
                          <div className={`h-full flex items-center justify-center text-[11px] sm:text-sm font-medium ${isEventPage ? 'bg-white/50' : 'bg-[#4da6ff]/60 text-white'}`} style={{ width: '14%' }}>
                            <span className="px-0.5">Us</span>
                          </div>
                        </div>
                        <div className="flex mt-2 text-xs text-white/40">
                          <span style={{ width: '25%' }}>49%</span>
                          <span style={{ width: '12%' }}>24%</span>
                          <span style={{ width: '14%' }}>27%</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Savings Highlight with Quantity Switcher */}
              <div className={`rounded-xl p-6 ${isEventPage ? 'bg-white/10 border border-white/20' : 'bg-[#4da6ff]/15 border border-[#4da6ff]/30'}`}>
                {/* Quantity Switcher - Stacked on mobile */}
                <div className="flex flex-col items-center gap-2 mb-4">
                  <span className="text-xs text-white/60">Savings per cure:</span>
                  <div className="flex bg-white/10 rounded-full p-0.5">
                    {([1, 2, 4] as const).filter(qty => quantityOptions.includes(qty)).map((qty) => (
                      <button
                        key={qty}
                        onClick={() => setSavingsQuantity(qty)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                          savingsQuantity === qty
                            ? isEventPage ? 'bg-white text-gray-800' : 'bg-[#4da6ff] text-white'
                            : 'text-white/60 hover:text-white'
                        }`}
                      >
                        {qty}-Month Cure
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dynamic Savings Display */}
                <div className="text-center">
                  <div className={`text-sm mb-1 ${isEventPage ? 'text-white/80' : 'text-[#4da6ff]'}`}>Total Savings vs. Big Pharma</div>
                  <div className="text-4xl font-bold text-white mb-2">
                    {(() => {
                      const tier = standardPricing.find(p => p.quantity === savingsQuantity);
                      return tier ? fp(tier.savings) : fp(0);
                    })()}
                  </div>
                  <div className="text-sm text-white/60">
                    {(() => {
                      const tier = standardPricing.find(p => p.quantity === savingsQuantity);
                      if (!tier) return null;
                      return `You pay ${fp(tier.price)} instead of ${fp(tier.originalPrice)}`;
                    })()}
                  </div>
                  <div className={`mt-4 pt-4 border-t ${isEventPage ? 'border-white/20' : 'border-[#4da6ff]/30'}`}>
                    <div className="text-xs text-white/40 mb-1">Price per treatment</div>
                    <div className={`text-2xl font-bold ${isEventPage ? 'text-white' : 'text-green-400'}`}>
                      {(() => {
                        const tier = standardPricing.find(p => p.quantity === savingsQuantity);
                        return tier ? fp(tier.price / tier.quantity) : fp(0);
                      })()}
                      <span className="text-sm font-normal text-white/60">/month</span>
                    </div>
                    <div className="text-xs text-white/40 mt-1">vs. {fp(standardPricing[0].originalPrice)} pharmacy average</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TREAT THE ROOT CAUSE SECTION - Dark cinematic section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background image with dark overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-YsyfBpGiRjpqdjxKzxRwdk.webp" 
            alt="" 
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/90" />
        </div>
        
        <div className="container px-4 relative z-10">
          {/* Top: Main headline - centered, ALL CAPS */}
          <div className="text-center mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white/70 text-xs font-medium tracking-wider uppercase">Why most treatments fail</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] tracking-tight uppercase">
              TREAT THE CAUSE<br/>AT THE ROOT.
            </h2>
            <p className="text-white/40 text-sm md:text-base mt-4 max-w-xl mx-auto font-medium uppercase tracking-widest">
              AT THE FOLLICLE LEVEL.
            </p>
          </div>
          
          {/* Two-column content */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 max-w-5xl mx-auto">
            {/* Left: The science */}
            <div className="border-l-2 border-white/20 pl-6">
              <h3 className="text-white text-lg md:text-xl font-bold mb-4 uppercase tracking-wide">The Science</h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed mb-4">
                In men, follicle weakening results from <span className="text-white font-semibold">DHT interference</span>, oxidative stress, and nutritional deficiencies at the cellular level.
              </p>
              <p className="text-white/60 text-sm md:text-base leading-relaxed">
                Without targeted, deep follicle activation, the hair growth cycle remains incomplete — producing thinner, weaker strands until the follicle shuts down entirely.
              </p>
              {/* Visual indicator */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex -space-x-1">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#007AFF] to-[#0066CC] border-2 border-black flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4"/></svg>
                    </div>
                  ))}
                </div>
                <span className="text-white/50 text-xs">Validated by 3 independent clinical studies</span>
              </div>
            </div>
            
            {/* Right: The problem - dramatic stat */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-red-400 text-xs font-bold tracking-wider uppercase">The Problem</span>
              </div>
              <h3 className="text-5xl md:text-6xl font-heading font-bold text-white mb-2 uppercase">
                95%
              </h3>
              <p className="text-white/80 text-lg md:text-xl font-bold mb-4 uppercase tracking-wide">
                OF EXISTING SOLUTIONS FAIL.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <p className="text-white/50 text-sm"><span className="text-white/70 font-medium">Shampoos</span> — wash off before penetrating</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <p className="text-white/50 text-sm"><span className="text-white/70 font-medium">Serums</span> — sit on the surface, never reach the root</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <p className="text-white/50 text-sm"><span className="text-white/70 font-medium">Supplements</span> — months to absorb, minimal impact</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-red-400 mt-0.5">✕</span>
                  <p className="text-white/50 text-sm"><span className="text-white/70 font-medium">Prescriptions</span> — systemic side effects, temporary results</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-[#4da6ff] text-sm font-semibold">
                  Colagea bypasses the surface entirely — delivering active compounds directly to the follicle root.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE DIFFERENCE SECTION */}
      <section id="difference" className={`py-16 ${isEventPage ? '' : ''}`} style={isEventPage ? { backgroundColor: eventColors?.accentDark } : { backgroundColor: '#0A1628' }}>
        <div className="container px-4">
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-4 font-heading uppercase text-white">The Difference Is Clear</h2>
          <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto">Stop wasting money on products that don't work. See why Colagea's patented micro-infusion technology outperforms everything else on the market.</p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
            {/* LEFT: Trash image + generic products cons */}
            <div className="bg-card rounded-2xl overflow-hidden border border-border flex flex-col">
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <img 
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/trash-products-5PLioRjQpyGE2QSBMxVgdR.webp"
                  alt="Generic hair loss products in trash"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                {/* Thumbs down overlay badge */}
                <div className="absolute bottom-3 left-3 bg-red-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <span>👎</span> Don't Waste Your Money
                </div>
              </div>
              <div className="p-6 flex-1">
                <h3 className="font-semibold text-lg mb-4 text-muted-foreground">{config.difference.standardPillowTitle}</h3>
                <ul className="space-y-3">
                  {config.difference.standardPillowPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <span className="text-red-500 font-bold mt-0.5">✕</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* RIGHT: Colagea video + pros */}
            <div className="bg-card rounded-2xl overflow-hidden border-2 border-[#0066CC] relative flex flex-col">
              {/* Dermatologically Tested Badge */}
              <div className="absolute top-3 right-3 z-10">
                <DermatologicallyTestedBadge size={100} className="w-[70px] h-[70px] lg:w-[100px] lg:h-[100px] drop-shadow-lg" />
              </div>
              {/* Best choice ribbon */}
              <div className="absolute top-0 left-4 z-10 bg-[#0066CC] text-white text-[10px] font-bold px-3 py-1.5 rounded-b-lg tracking-wider uppercase">
                Best Choice
              </div>
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0a1628]">
                <video
                  src="https://colagea.com/cdn/shop/videos/c/vp/7cece6ac3e874a43924ffcb1b75f60e1/7cece6ac3e874a43924ffcb1b75f60e1.HD-1080p-3.3Mbps-62331898.mp4?v=0"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                />
                {/* Thumbs up overlay badge */}
                <div className="absolute bottom-3 left-3 bg-green-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                  <span>👍</span> Clinically Proven
                </div>
              </div>
              <div className="p-6 flex-1">
                <h3 className="font-semibold text-lg mb-4 text-[#0066CC]">{config.difference.fluffcoPillowTitle}</h3>
                <ul className="space-y-3">
                  {config.difference.fluffcoPillowPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-green-600 font-bold mt-0.5">✓</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* TECHNOLOGY SECTION - 16:9 image */}
      <section id="technology" className={`py-16 ${isEventPage ? '' : 'bg-secondary/30'}`} style={isEventPage ? { backgroundColor: eventColors?.accentDark } : {}}>
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-2">The Science</div>
              <h2 className="text-3xl font-semibold tracking-tight mb-6 font-heading uppercase">Why It Works</h2>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-5 h-5" />
                    <h3 className="font-semibold">Patented Micro-Needle Delivery</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Our 0.5mm micro-needle cap creates painless micro-channels in the scalp, allowing active ingredients to penetrate 10x deeper than topical serums. This bypasses the skin barrier that blocks 95% of conventional treatments.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["10x Deeper Absorption", "Painless Application", "Activates Follicles", "Patented Design"].map((tag) => (
                      <span 
                        key={tag} 
                        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full ${isEventPage ? '' : ''}`}
                        style={isEventPage ? { backgroundColor: `${eventColors?.primary}15`, color: eventColors?.primary } : { backgroundColor: '#D0C1B4', color: '#4A3F35' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5" />
                    <h3 className="font-semibold">Concentrated Growth Complex</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    A proprietary blend of Biotin, Collagen Peptides, Saw Palmetto, and Caffeine works synergistically to block DHT (the hormone that kills follicles), stimulate blood flow, and nourish dormant hair roots back to life.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["DHT Blocker", "Collagen-Rich", "Clinically Dosed", "No Side Effects"].map((tag) => (
                      <span 
                        key={tag} 
                        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full ${isEventPage ? '' : ''}`}
                        style={isEventPage ? { backgroundColor: `${eventColors?.primary}15`, color: eventColors?.primary } : { backgroundColor: '#D0C1B4', color: '#4A3F35' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="aspect-video bg-secondary rounded-2xl overflow-hidden">
              <img src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/lab-research-colagea-hG2ScjduDDqHse7hJEWwxY.webp" alt="Colagea lab research" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* NECK PAIN ANGLE SECTION - Only for neck pain relief config */}
      {config.id === 'neck-pain-relief' && (
        <section className="py-16 bg-gradient-to-b from-[#e8f4fc] to-[#f0f8ff]">
          <div className="container px-4">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-[#2563eb]/10 text-[#2563eb] text-sm font-medium rounded-full mb-4">Designed for Pain Relief</span>
              <h2 className="text-3xl font-semibold tracking-tight mb-4 font-heading uppercase">Why Your Current Pillow Causes Neck Pain</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">Most pillows fail to support your cervical spine properly, leading to strain, stiffness, and chronic discomfort. Here's how we engineered a solution.</p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Cervical Alignment */}
              <div className="bg-white rounded-2xl overflow-hidden border border-[#2563eb]/20 shadow-sm">
                <div className="aspect-[4/3] bg-[#f0f8ff]">
                  <img src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/neck-pain-cervical-alignment_0e1a3c2b.png")} alt="Proper cervical alignment" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#2563eb]/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#2563eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <h3 className="font-semibold text-lg">Proper Cervical Alignment</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">Our pillow maintains your neck's natural C-curve throughout the night, preventing the strain that causes morning stiffness.</p>
                  <ul className="space-y-2">
                    {["Supports natural spine curvature", "Reduces muscle tension", "Prevents nerve compression"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-[#2563eb]">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Morning Relief */}
              <div className="bg-white rounded-2xl overflow-hidden border border-[#2563eb]/20 shadow-sm">
                <div className="aspect-[4/3] bg-[#f0f8ff]">
                  <img src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/neck-pain-morning-relief_71601df5.png")} alt="Wake up without pain" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#2563eb]/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#2563eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" /></svg>
                    </div>
                    <h3 className="font-semibold text-lg">Wake Up Pain-Free</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">Customers report significant reduction in morning neck stiffness within the first week of switching to our pillow.</p>
                  <ul className="space-y-2">
                    {["No more morning stiffness", "Reduced headaches", "Better range of motion"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-[#2563eb]">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Support Zones */}
              <div className="bg-white rounded-2xl overflow-hidden border border-[#2563eb]/20 shadow-sm">
                <div className="aspect-[4/3] bg-[#f0f8ff]">
                  <img src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/neck-pain-support-diagram_eb147f2f.png")} alt="Support zones diagram" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#2563eb]/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#2563eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    </div>
                    <h3 className="font-semibold text-lg">3-Zone Support System</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">Engineered with distinct support zones for your head, neck, and shoulders, each optimized for its specific function.</p>
                  <ul className="space-y-2">
                    {["Head cradle zone", "Cervical support core", "Shoulder transition zone"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-[#2563eb]">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Therapist Recommendation Banner */}
            <div className="mt-12 bg-white rounded-2xl border border-[#2563eb]/20 p-6 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-8 h-8 text-[#2563eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                </div>
                <div className="text-center md:text-left">
                  <h3 className="font-semibold text-lg mb-1">Recommended by Physical Therapists & Chiropractors</h3>
                  <p className="text-muted-foreground text-sm">Healthcare professionals recommend our pillow for patients with chronic neck issues. The consistent support and proper alignment help complement treatment plans.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FLUFFCO SECRET SECTION - Hidden for neck pain relief config */}
      {config.id !== 'neck-pain-relief' && (
      <section className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : { backgroundColor: '#EBF1F8' }}>
        <div className="container px-4">
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-12 font-heading uppercase">What Makes Colagea Different?</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl xl:max-w-5xl mx-auto">
            {/* Micro-Needle Technology */}
            <div className="bg-card rounded-2xl overflow-hidden border border-border">
              <div className="aspect-video bg-secondary">
                <img 
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/man-applying-colagea-YsyfBpGiRjpqdjxKzxRwdk.webp" 
                  alt="Colagea micro-needle application on scalp" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async" 
                />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-xl mb-3">Patented Micro-Needle Cap</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Most hair serums just sit on top of your scalp. Colagea's patented micro-needle cap creates painless micro-channels that deliver active ingredients directly to the follicle root, where hair growth actually happens.
                </p>
                <ul className="space-y-2">
                  {["10x Deeper Penetration", "Painless 0.5mm Needles", "Activates Dormant Follicles", "Engineered in Switzerland", "Dermatologist Approved"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span style={isEventPage ? { color: eventColors?.primary } : {}} className={isEventPage ? '' : 'text-green-600'}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Clinical-Grade Formula */}
            <div className="bg-card rounded-2xl overflow-hidden border border-border">
              <div className="aspect-video bg-secondary">
                <img src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/clinical-grade-formula-eibpPiaxJ5FVN4miUCkwjv.webp" alt="Colagea clinical-grade growth complex" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-xl mb-3">Clinical-Grade Growth Complex</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Each vial contains a concentrated blend of 5 clinically proven ingredients, dosed at therapeutic levels. Unlike drugstore products that use trace amounts, Colagea delivers the full clinical dose in every application.
                </p>
                <ul className="space-y-2">
                  {["Biotin + Collagen Peptides", "Saw Palmetto (DHT Blocker)", "Caffeine Complex", "Zero Side Effects", "93% Efficacy in Trials"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span style={isEventPage ? { color: eventColors?.primary } : {}} className={isEventPage ? '' : 'text-green-600'}>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* BUILT FOR THE LONG RUN - Cards with images */}
      <section className={`py-16 ${isEventPage ? '' : 'bg-secondary/30'}`} style={isEventPage ? { backgroundColor: eventColors?.accentDark } : {}}>
        <div className="container px-4">
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-12 font-heading uppercase">Built for Real Results</h2>
          
          {/* Desktop: Show all cards in grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-full xl:max-w-6xl mx-auto">
            {benefitCards.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-card rounded-xl border border-border overflow-hidden flex min-w-0">
                    <div className="w-20 lg:w-28 flex-shrink-0 bg-secondary">
                      <img 
                        src={benefit.image} 
                        alt={benefit.title} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                  </div>
                  <div className="p-4 flex flex-col justify-center flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <h3 className="font-semibold text-sm">{benefit.title}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Mobile: Show 3 cards + partial 4th with overlay button */}
          <div className="md:hidden relative">
            <div className="flex flex-col gap-2.5">
              {benefitCards.map((benefit, idx) => {
                const Icon = benefit.icon;
                // Show first 3 fully, 4th partially (for overlay effect), hide rest when collapsed
                const isHidden = !showAllBenefitCards && idx >= 4;
                const isPartial = !showAllBenefitCards && idx === 3;
                
                if (isHidden) return null;
                
                return (
                  <div 
                    key={idx} 
                    className={`bg-card rounded-xl border border-border overflow-hidden flex min-w-0 transition-all duration-300 relative ${
                      isPartial ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="w-20 flex-shrink-0 bg-secondary">
                      <img 
                        src={benefit.image} 
                        alt={benefit.title} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-4 flex flex-col justify-center flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <h3 className="font-semibold text-sm">{benefit.title}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{benefit.desc}</p>
                    </div>
                    
                    {/* Overlay button on 4th card with gradient blur */}
                    {isPartial && (
                      <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px]">
                        {/* Gradient overlay for smooth fade effect */}
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/50" />
                        <button 
                          onClick={() => setShowAllBenefitCards(true)}
                          className="relative z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium bg-card shadow-lg hover:bg-secondary transition-colors"
                        >
                          See All {benefitCards.length} Benefits
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERT TESTIMONIAL - Marc Reymond, Independent Hair Specialist */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: '#1a1f2e' }}>
        <div className="container px-4">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-[#4d9fff] font-semibold mb-6 lg:mb-8">Developed with Scalp Experts</p>
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center gap-6 lg:gap-10">
            {/* Expert Photo */}
            <div className="flex-shrink-0">
              <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-full overflow-hidden border-2 border-white/10 shadow-xl">
                <img 
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/specialist-marc-reymond_b4340370.png"
                  alt="Marc Reymond - Independent Hair Specialist"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            {/* Quote */}
            <div className="text-center md:text-left">
              <blockquote className="text-xl lg:text-2xl text-white leading-relaxed mb-4 font-medium">
                &ldquo;Traditional routines often deliver only superficial results. Colagea takes a rigorous, targeted approach to scalp care.&rdquo;
              </blockquote>
              <div>
                <p className="text-white font-semibold text-sm lg:text-base">Marc Reymond</p>
                <p className="text-gray-400 text-xs lg:text-sm">Independent Hair Specialist</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - Emotional with human touches */}
      <section id="reviews-section" className="py-16" style={isEventPage && eventColors ? { backgroundColor: eventColors.accentDark } : { backgroundColor: '#E0E8F2' }}>
        <div className="container px-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrustpilotStars className="h-5" />
            <span className="font-semibold text-lg">{config.testimonials.sectionTitle}</span>
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-12 font-heading uppercase">{config.testimonials.sectionSubtitle}</h2>
          
          {/* Desktop: Show 4 testimonials in grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleTestimonials.map((t, idx) => (
              <div key={idx} className="bg-card p-5 rounded-xl border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary flex items-center justify-center">
                    {t.image ? (
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span className="text-sm font-semibold text-muted-foreground">{t.name.split(' ').map(n => n[0]).join('').slice(0,2)}</span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">{t.name}</span>
                      {t.verified && (
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                            <Check className="w-2 h-2 text-white stroke-[3]" />
                          </span>
                          <span className="text-[10px] text-[#1d9bf0] font-medium">Verified</span>
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.location}</div>
                  </div>
                </div>
                <div className="flex mb-2">
                  <TrustpilotStars count={t.rating} className="h-3.5" />
                </div>
                <p className="text-sm leading-relaxed">"{t.text}"</p>
              </div>
            ))}
          </div>
          
          {/* Desktop: Show More/Less button */}
          <div className="hidden md:block text-center mt-8">
            <button 
              onClick={() => setShowAllTestimonials(!showAllTestimonials)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors"
            >
              {showAllTestimonials ? 'Show Less' : 'See More Testimonials'}
              {showAllTestimonials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Mobile: Show 3 testimonials + 4th with overlay button */}
          <div className="md:hidden relative">
            <div className="flex flex-col gap-2.5">
              {testimonials.map((t, idx) => {
                // Show first 3 fully, 4th partially (for overlay effect), hide rest when collapsed
                const isHidden = !showAllTestimonials && idx >= 4;
                const isPartial = !showAllTestimonials && idx === 3;
                
                if (isHidden) return null;
                
                return (
                  <div 
                    key={idx} 
                    className={`bg-card p-4 rounded-lg border border-border transition-all duration-300 relative ${
                      isPartial ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary flex-shrink-0 flex items-center justify-center">
                        {t.image ? (
                          <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <span className="text-xs font-semibold text-muted-foreground">{t.name.split(' ').map(n => n[0]).join('').slice(0,2)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium">{t.name}</span>
                          {t.verified && (
                            <span className="w-3.5 h-3.5 rounded-full bg-[#1d9bf0] flex items-center justify-center flex-shrink-0">
                              <Check className="w-2 h-2 text-white stroke-[3]" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <TrustpilotStars count={t.rating} className="h-3" />
                          <span className="text-[10px] text-muted-foreground">{t.location}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs leading-relaxed line-clamp-3">"{t.text}"</p>
                    
                    {/* Overlay button on 4th card with gradient blur */}
                    {isPartial && (
                      <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-lg">
                        {/* Gradient overlay for smooth fade effect */}
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/50 rounded-lg" />
                        <button 
                          onClick={() => setShowAllTestimonials(true)}
                          className="relative z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium bg-card shadow-lg hover:bg-secondary transition-colors"
                        >
                          See All {testimonials.length} Reviews
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION with Size Selector */}
      <section 
        className="py-16"
        style={isEventPage && eventColors ? {
          background: `linear-gradient(to bottom, ${eventColors.accent}, ${eventColors.accentDark})`
        } : {
          backgroundColor: '#EBF1F8'
        }}
      >
        <div className="container px-4 xl:max-w-4xl">
          <div className="text-center mb-8">
            {isEventPage && eventColors && (
              <span 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4"
                style={{ backgroundColor: `${eventColors.primary}15`, color: eventColors.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                A Thoughtful {eventNames[config.id] || 'Holiday'} Gift
              </span>
            )}
            <h2 className="text-3xl font-semibold tracking-tight font-heading uppercase">{isEventPage ? `${eventNames[config.id] || 'Holiday'} Pricing` : 'Choose Your Cure'}</h2>
            <p className="mt-3 text-muted-foreground">{isEventPage ? 'Start your hair regrowth journey at exclusive seasonal pricing.' : (isUS ? 'Online-only pricing. Free shipping on all orders.' : `Online-only pricing. ${nonUsBadgeText}.`)}  </p>
          </div>


          
          <div className={`grid gap-6 relative ${quantityOptions.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'}`}>
            {pricingSectionPricing.filter(option => quantityOptions.includes(option.quantity)).map((option) => {
              const setupImage = option.quantity === 1 
                ? 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/cure-1month-3vials_e6113e35.png' 
                : option.quantity === 2 
                  ? 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/cure-2month-6vials_b01fccec.png' 
                  : 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/cure-4month-12vials_fab19dbb.png';
              return (
                <div
                  key={option.quantity}
                  className={`rounded-2xl border-2 relative overflow-visible flex flex-col ${
                    option.quantity === 4 ? 'border-foreground bg-card' : 'border-border bg-card'
                  }`}
                  style={option.quantity === 4 && isEventPage && eventColors ? {
                    borderColor: eventColors.primary
                  } : {}}
                >
                  {/* Badges container - flex layout for consistent spacing */}
                  <div className="absolute -top-3 right-4 z-20 flex items-center gap-1.5">
                    {option.badge && !(config.hideMostPopularBadge && option.badge === 'Most Popular') && (
                      <span 
                        className="text-xs font-medium px-3 py-1 rounded-full shadow-md"
                        style={isEventPage && eventColors ? {
                          backgroundColor: eventColors.primary,
                          color: 'white'
                        } : {
                          backgroundColor: '#004C99',
                          color: 'white'
                        }}
                      >
                        {option.badge}
                      </span>
                    )}
                    {/* Discount badge - always shown */}
                    <span 
                      className="text-xs font-medium px-3 py-1 rounded-full shadow-md"
                      style={{
                        backgroundColor: '#16a34a',
                        color: 'white'
                      }}
                    >
                      -{option.discount}%
                    </span>
                  </div>
                  {/* 16:9 Image at top, touching edges - zoomed to focus on pillows */}
                  <div className="relative aspect-video w-full bg-secondary rounded-t-[14px] overflow-hidden">
                    <img 
                      src={setupImage} 
                      alt={`${option.quantity === 1 ? '1-Month' : option.quantity === 2 ? '2-Month' : '4-Month'} Cure`} 
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  {/* Content area with flex-grow to push button to bottom */}
                  <div className="p-6 flex flex-col flex-grow text-left">
                    <div className="text-xl font-semibold mb-1">{option.quantity === 1 ? '1-Month Cure' : option.quantity === 2 ? '2-Month Cure' : '4-Month Cure'}</div>
                    <p className="text-xs text-muted-foreground mb-2">{option.quantity === 1 ? '3 vials · 30 days of treatment' : option.quantity === 2 ? '6 vials · 60 days of treatment' : '12 vials · 120 days of treatment'}</p>
                    
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold tabular-nums overflow-hidden">
                        <span 
                          key={`${pricingSectionSize}-${option.price}`}
                          className="inline-block animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                          style={{ animationDelay: '0ms' }}
                        >
                          {fp(option.price)}
                        </span>
                      </span>
                      <span className="text-sm text-muted-foreground tabular-nums overflow-hidden">
                        <span 
                          key={`${pricingSectionSize}-${option.originalPrice}`}
                          className="inline-block line-through animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                          style={{ animationDelay: '50ms' }}
                        >
                          {fp(option.originalPrice)}
                        </span>
                      </span>
                    </div>
                    <div className="text-sm text-green-600 font-medium mb-4 tabular-nums overflow-hidden">
                      <span 
                        key={`${pricingSectionSize}-${option.savings}`}
                        className="inline-block animate-[slideIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)_both]"
                        style={{ animationDelay: '100ms' }}
                      >
                        Save {fp(option.savings)}
                      </span>
                    </div>
                    
                    {/* Pillowcase bonus - horizontal bar style matching hero */}
                    {option.bonus && (
                      <div className="border-t border-b border-border py-2 my-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Gift className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">+ Exclusive Mystery Gift</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground line-through">$10</span>
                          <span className="text-xs font-medium text-green-600">FREE</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Spacer to push button to bottom */}
                    <div className="flex-grow"></div>
                    
                    <button 
                      onClick={() => {
                        setSelectedQuantity(option.quantity);
                        setSelectedSize(pricingSectionSize);
                        // Track AddToCart before opening checkout popup
                        const size = pricingSectionSize === 'Standard' ? 'standard' as const : 'king' as const;
                        const qty = option.quantity as 1 | 2 | 4;
                        const pricing = currentPricing.find(p => p.quantity === option.quantity);
                        if (pricing) {
                          const productData = getProductDataForTracking(size, qty, pricing.price, currencyCode);
                          trackAddToCart(productData);
                          const ga4Item = createMainProductItem(pricingSectionSize, qty, pricing.price, pricing.originalPrice);
                          trackGA4AddToCart([ga4Item], pricing.price);
                        }
                        setShowOrderPopup(true);
                      }}
                      className={`w-full py-3 rounded-full font-medium transition-all duration-300 hover:-translate-y-px active:translate-y-0 ${
                        option.quantity === 4 
                          ? 'text-white' 
                          : ''
                      }`}
                      style={option.quantity === 4 ? { 
                        backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
                        boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`
                      } : {
                        backgroundColor: 'transparent',
                        border: `2px solid ${isEventPage && eventColors ? eventColors.primary : '#0066CC'}`,
                        color: isEventPage && eventColors ? eventColors.primary : '#0066CC'
                      }}
                      onMouseEnter={(e) => {
                        if (option.quantity === 4) {
                          const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                          e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                          e.currentTarget.style.filter = 'brightness(0.95)';
                        } else {
                          const color = isEventPage && eventColors ? eventColors.primary : '#0066CC';
                          e.currentTarget.style.backgroundColor = color;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${color}30`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (option.quantity === 4) {
                          const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                          e.currentTarget.style.filter = 'brightness(1)';
                        } else {
                          const color = isEventPage && eventColors ? eventColors.primary : '#0066CC';
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = color;
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {getPageCTAWithDiscount(option.discount)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> 60-Day Guarantee</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> {isUS ? 'Free Shipping' : nonUsBadgeText}</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Free Returns</span>
          </div>
          
          {/* Free Welcome Pack Highlight */}
          <div className="mt-10 max-w-lg mx-auto">
            <div 
              className="relative overflow-hidden rounded-2xl border-2 p-5 sm:p-6"
              style={isEventPage && eventColors ? {
                borderColor: eventColors.primary,
                backgroundColor: `${eventColors.primary}08`
              } : {
                borderColor: '#007AFF',
                backgroundColor: '#007AFF08'
              }}
            >
              {/* Corner ribbon */}
              <div 
                className="absolute -top-px -right-px px-4 py-1 rounded-bl-xl text-[10px] font-bold uppercase tracking-wider text-white"
                style={{ backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF' }}
              >
                Included Free
              </div>
              
              {/* Header */}
              <div className="flex items-center gap-2.5 mb-4">
                <div 
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: isEventPage && eventColors ? `${eventColors.primary}15` : '#007AFF15' }}
                >
                  <Gift className="w-4.5 h-4.5" style={{ color: isEventPage && eventColors ? eventColors.primary : '#007AFF' }} />
                </div>
                <div>
                  <p className="text-base font-bold text-foreground">Free Welcome Pack</p>
                  <p className="text-xs text-muted-foreground">$59.97 value — yours at no extra cost with every order</p>
                </div>
              </div>
              
              {/* Items grid */}
              <div className="grid grid-cols-3 gap-3">
                {/* Derma+ Brush */}
                <div className="bg-white rounded-xl p-3 text-center border border-border/50">
                  <div 
                    className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: isEventPage && eventColors ? `${eventColors.primary}10` : '#007AFF10' }}
                  >
                    <svg className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.primary : '#007AFF' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="1" width="12" height="10" rx="2"/><line x1="9" y1="4" x2="9" y2="8"/><line x1="12" y1="4" x2="12" y2="8"/><line x1="15" y1="4" x2="15" y2="8"/><path d="M8 11v4a4 4 0 008 0v-4"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
                  </div>
                  <p className="text-xs font-semibold text-foreground">Derma+ Brush</p>
                  <p className="text-[10px] text-muted-foreground">Scalp stimulator</p>
                  <p className="text-[10px] text-muted-foreground line-through mt-0.5">$29.99</p>
                </div>
                {/* Hair Recovery Guide */}
                <div className="bg-white rounded-xl p-3 text-center border border-border/50">
                  <div 
                    className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: isEventPage && eventColors ? `${eventColors.primary}10` : '#007AFF10' }}
                  >
                    <svg className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.primary : '#007AFF' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><path d="M8 7h8"/><path d="M8 11h6"/></svg>
                  </div>
                  <p className="text-xs font-semibold text-foreground">Hair Guide</p>
                  <p className="text-[10px] text-muted-foreground">Expert protocols</p>
                  <p className="text-[10px] text-muted-foreground line-through mt-0.5">$19.99</p>
                </div>
                {/* Free Express Shipping */}
                <div className="bg-white rounded-xl p-3 text-center border border-border/50">
                  <div 
                    className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: isEventPage && eventColors ? `${eventColors.primary}10` : '#007AFF10' }}
                  >
                    <svg className="w-5 h-5" style={{ color: isEventPage && eventColors ? eventColors.primary : '#007AFF' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  </div>
                  <p className="text-xs font-semibold text-foreground">Free Shipping</p>
                  <p className="text-[10px] text-muted-foreground">Express delivery</p>
                  <p className="text-[10px] text-muted-foreground line-through mt-0.5">$9.99</p>
                </div>
              </div>
              
              {/* Urgency line */}
              <p className="text-center text-[11px] text-muted-foreground mt-3">
                Limited availability — Welcome Pack included while supplies last
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION - Closed by default, openable/closable */}
      <section id="faq" className="py-16" style={isEventPage ? { backgroundColor: eventColors?.accent } : { backgroundColor: '#E0E8F2' }}>
        <div className="container px-4 xl:max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight text-center mb-12 font-heading uppercase">{config.faq.sectionTitle}</h2>
          
          <div 
            className="rounded-2xl border overflow-hidden"
            style={isEventPage && eventColors ? {
              backgroundColor: 'white',
              borderColor: `${eventColors.primary}33`
            } : {
              backgroundColor: 'var(--card)',
              borderColor: 'var(--border)'
            }}
          >
            {faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="last:border-b-0 transition-colors border-b"
                style={isEventPage && eventColors ? {
                  borderColor: `${eventColors.primary}1a`,
                  backgroundColor: expandedFaq === idx ? eventColors.background : undefined
                } : {
                  borderColor: 'var(--border)'
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
                >
                  <h3 className="font-medium pr-4 text-sm sm:text-base">{faq.q}</h3>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4 sm:px-6 sm:pb-6 -mt-2">
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* FOOTER */}
      <footer 
        className="py-12 border-t"
        style={isEventPage && eventColors ? {
          borderColor: `${eventColors.primary}33`,
          backgroundColor: 'white'
        } : {
          borderColor: 'var(--border)'
        }}
      >
        <div className="container px-4 text-center">
          <FluffLogo className="h-9 w-auto mx-auto mb-4" color="currentColor" />
          <p className="text-sm text-muted-foreground mb-4">Science-backed hair regrowth. Visible in 5 weeks.</p>
          <div className="flex flex-wrap justify-center gap-6 mb-4">
            <a href="https://colagea.co" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Shop</a>
            <a href="https://colagea.co/pages/contact" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <a href="https://colagea.co/pages/gtc" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms & Conditions</a>
            <a href="https://colagea.co/pages/terms-of-use" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms of Use</a>
            <a href="https://colagea.co/pages/privacypolicy" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="https://colagea.co/pages/refund" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Refund Policy</a>
            <a href="https://colagea.co/pages/legal" target="_blank" rel="noopener" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Legal Notice</a>
          </div>
          <div className="text-xs text-muted-foreground">
            © 2026 Colagea. All rights reserved. Engineered in Switzerland.
          </div>
        </div>
      </footer>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSizeGuide(false)}
          />
          {/* Modal */}
          <div className="relative bg-card rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto animate-[fadeSlideIn_0.3s_ease-out]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">Pillow Size Guide</h3>
              <button 
                onClick={() => setShowSizeGuide(false)}
                className="p-1 hover:bg-secondary rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">Choose the right pillow size based on your bed:</p>
              
              {/* Standard Recommendation */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedSize === 'Standard' ? 'border-foreground bg-secondary/50' : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => { setSelectedSize('Standard'); setShowSizeGuide(false); }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-muted-foreground">
                      <rect x="2" y="6" width="20" height="12" rx="2"/>
                      <line x1="8" y1="6" x2="8" y2="18"/>
                      <line x1="16" y1="6" x2="16" y2="18"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">Standard</span>
                      <span className="text-xs text-muted-foreground">20" × 28"</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Best for Twin, Twin XL, Full, and Queen beds</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Twin</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Twin XL</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Full</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Queen</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* King Recommendation */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedSize === 'King' ? 'border-foreground bg-secondary/50' : 'border-border hover:border-muted-foreground'
                }`}
                onClick={() => { setSelectedSize('King'); setShowSizeGuide(false); }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7 text-muted-foreground">
                      <rect x="1" y="6" width="22" height="12" rx="2"/>
                      <line x1="6" y1="6" x2="6" y2="18"/>
                      <line x1="12" y1="6" x2="12" y2="18"/>
                      <line x1="18" y1="6" x2="18" y2="18"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">King</span>
                      <span className="text-xs text-muted-foreground">20" × 36"</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#004C99', color: '#ffffff' }}>Chosen by 81%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Best for King and California King beds</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">King</span>
                      <span className="text-[10px] bg-secondary px-2 py-0.5 rounded-full">Cal King</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pro Tip - Hidden as per requirements */}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Add to Cart Bar */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-40 bg-card border-t shadow-lg transform transition-all duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={isEventPage && eventColors ? {
          borderColor: `${eventColors.primary}33`
        } : {
          borderColor: 'var(--border)'
        }}
      >
          {/* Desktop/Tablet Layout */}
          <div className="hidden sm:block container px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Product info */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <img 
                  src={toWebP("https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-box-product_08474614.jpg")} 
                  alt="Colagea Micro-Infusion" 
                  className="w-12 h-12 object-contain flex-shrink-0"
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold">{fp(currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0)}</p>
                    <p className="text-xs text-muted-foreground line-through">{fp(currentPricing.find(p => p.quantity === selectedQuantity)?.originalPrice || 0)}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ backgroundColor: '#16a34a20', color: '#16a34a' }}>-{currentPricing.find(p => p.quantity === selectedQuantity)?.discount}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedQuantity === 1 ? '1-Month Cure' : selectedQuantity === 2 ? '2-Month Cure' : '4-Month Cure'} · {selectedQuantity === 1 ? '3' : selectedQuantity === 2 ? '6' : '12'} treatments
                  </p>
                </div>
              </div>
              
              {/* Cure selector - Pills on large desktop, dropdown on tablet */}
              <div className="flex items-center gap-2">
                {/* Large desktop: pill buttons */}
                <div 
                  className="hidden lg:flex items-center gap-1 rounded-full p-1"
                  style={isEventPage && eventColors ? {
                    backgroundColor: eventColors.secondaryLight,
                    border: `1px solid ${eventColors.primary}30`
                  } : {
                    backgroundColor: 'white',
                    border: '1px solid var(--border)'
                  }}
                >
                  {currentPricing.filter(option => quantityOptions.includes(option.quantity)).map((option) => (
                    <button
                      key={option.quantity}
                      onClick={() => setSelectedQuantity(option.quantity)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-150 whitespace-nowrap ${
                        selectedQuantity !== option.quantity ? 'hover:bg-black/8 dark:hover:bg-white/15' : ''
                      }`}
                      style={selectedQuantity === option.quantity
                        ? (isEventPage && eventColors ? {
                            backgroundColor: eventColors.primary,
                            color: 'white'
                          } : {
                            backgroundColor: '#0066CC',
                            color: 'white'
                          })
                        : (isEventPage && eventColors ? {
                            color: eventColors.primary
                          } : {
                            color: 'var(--muted-foreground)'
                          })
                      }
                    >
                      {option.quantity === 1 ? '1-Month' : option.quantity === 2 ? '2-Month' : '4-Month'}{option.quantity === 4 && ' 🎁'}
                    </button>
                  ))}
                </div>
                
                {/* Tablet: compact dropdown */}
                <div className="lg:hidden relative">
                  <select
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(Number(e.target.value))}
                    className="appearance-none rounded-lg px-3 py-2 pr-8 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1"
                    style={isEventPage && eventColors ? {
                      backgroundColor: eventColors.secondaryLight,
                      border: `1px solid ${eventColors.primary}40`,
                      color: eventColors.primary
                    } : {
                      backgroundColor: 'white',
                      border: '1px solid var(--border)',
                      color: 'var(--foreground)'
                    }}
                  >
                    {currentPricing.filter(option => quantityOptions.includes(option.quantity)).map((option) => (
                      <option key={option.quantity} value={option.quantity}>
                        {option.quantity === 1 ? '1-Month Cure' : option.quantity === 2 ? '2-Month Cure' : '4-Month Cure'} — {fp(option.price)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              
              {/* CTA button */}
              <button 
                onClick={() => {
                  // Track AddToCart before opening checkout popup
                  const size = selectedSize === 'Standard' ? 'standard' as const : 'king' as const;
                  const qty = selectedQuantity as 1 | 2 | 4;
                  const pricing = currentPricing.find(p => p.quantity === selectedQuantity);
                  if (pricing) {
                    const productData = getProductDataForTracking(size, qty, pricing.price, currencyCode);
                    trackAddToCart(productData);
                    const ga4Item = createMainProductItem(selectedSize, qty, pricing.price, pricing.originalPrice);
                    trackGA4AddToCart([ga4Item], pricing.price);
                  }
                  setShowOrderPopup(true);
                }}
                className="text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-300 whitespace-nowrap animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                style={{ 
                  backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
                  boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`
                }}
                onMouseEnter={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                  e.currentTarget.style.filter = 'brightness(0.95)';
                }}
                onMouseLeave={(e) => {
                  const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                {getPageCTA()}
              </button>
            </div>
          </div>
          
          {/* Mobile Layout */}
          <div className="sm:hidden">
            {/* Main bar - variant toggle adjacent to CTA on right */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-3">
                {/* Left: Price info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">{fp(currentPricing.find(p => p.quantity === selectedQuantity)?.price || 0)}</p>
                    <p className="text-xs text-muted-foreground line-through">{fp(currentPricing.find(p => p.quantity === selectedQuantity)?.originalPrice || 0)}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#16a34a20', color: '#16a34a' }}>-{currentPricing.find(p => p.quantity === selectedQuantity)?.discount}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedQuantity === 1 ? '1-Month Cure' : selectedQuantity === 2 ? '2-Month Cure' : '4-Month Cure'} · {selectedQuantity === 1 ? '3' : selectedQuantity === 2 ? '6' : '12'} treatments{selectedQuantity === 4 && ' + 🎁'}
                  </p>
                </div>
                
                {/* Right: Compact variant pills + CTA button side by side */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Compact cure toggle */}
                  <div 
                    className="flex items-center rounded-full p-0.5"
                    style={isEventPage && eventColors ? {
                      backgroundColor: eventColors.secondaryLight,
                      border: `1px solid ${eventColors.primary}30`
                    } : {
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {currentPricing.filter(option => quantityOptions.includes(option.quantity)).map((option) => (
                      <button
                        key={option.quantity}
                        onClick={() => setSelectedQuantity(option.quantity)}
                        className={`px-2 py-1 text-[10px] font-semibold rounded-full transition-colors duration-150 whitespace-nowrap ${
                          selectedQuantity !== option.quantity ? 'active:opacity-80' : ''
                        }`}
                        style={selectedQuantity === option.quantity
                          ? (isEventPage && eventColors ? {
                              backgroundColor: eventColors.primary,
                              color: 'white'
                            } : {
                              backgroundColor: '#0066CC',
                              color: 'white'
                            })
                          : (isEventPage && eventColors ? {
                              color: eventColors.primary
                            } : {
                              color: '#6b7280'
                            })
                        }
                      >
                        {option.quantity === 1 ? '1M' : option.quantity === 2 ? '2M' : '4M'}
                      </button>
                    ))}
                  </div>
                
                {/* CTA button */}
                <button 
                  onClick={() => {
                    // Track AddToCart before opening checkout popup
                    const size = selectedSize === 'Standard' ? 'standard' as const : 'king' as const;
                    const qty = selectedQuantity as 1 | 2 | 4;
                    const pricing = currentPricing.find(p => p.quantity === selectedQuantity);
                    if (pricing) {
                      const productData = getProductDataForTracking(size, qty, pricing.price, currencyCode);
                      trackAddToCart(productData);
                      const ga4Item = createMainProductItem(selectedSize, qty, pricing.price, pricing.originalPrice);
                      trackGA4AddToCart([ga4Item], pricing.price);
                    }
                    setShowOrderPopup(true);
                  }}
                  className="text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-all duration-300 whitespace-nowrap animate-cta-shimmer hover:-translate-y-px active:translate-y-0"
                  style={{ 
                    backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF',
                    boxShadow: `0 2px 8px ${isEventPage && eventColors ? eventColors.primary : '#007AFF'}40`
                  }}
                  onMouseEnter={(e) => {
                    const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${color}70`;
                    e.currentTarget.style.filter = 'brightness(0.95)';
                  }}
                  onMouseLeave={(e) => {
                    const color = isEventPage && eventColors ? eventColors.primary : '#007AFF';
                    e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`;
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  {getPageCTA(true)}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Just Purchased Toast Notification */}
      {showToast && currentToast !== null && (
        <div className={`fixed left-4 z-50 animate-toast-in-out transition-all duration-300 ${showStickyBar ? (stickyBarExpanded ? 'hidden sm:block sm:bottom-20' : 'bottom-20') : 'bottom-4'}`}>
          <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">{purchaseToasts[currentToast].name}</span>
                  <span className="text-[10px]">{purchaseToasts[currentToast].flag}</span>
                  <span className="text-[10px] text-muted-foreground">{purchaseToasts[currentToast].location}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[9px] text-muted-foreground">Just purchased</span>
                  <span className="text-[9px] bg-secondary px-1.5 py-0.5 rounded font-medium">
                    {purchaseToasts[currentToast].quantity}-Month Cure
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
                aria-label="Close notification"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Popup */}
      <OrderPopup
        isOpen={showOrderPopup}
        onClose={() => setShowOrderPopup(false)}
        // Exit recovery disabled - no longer showing unlock 10% popup on close
        selectedSize={selectedSize}
        selectedQuantity={selectedQuantity}
        stockLevel={stockLevel}
        countdown={countdown}
        pageSlug={config.slug || ''}
        hideFirstUpsell={config.hideFirstUpsell}
        hideSecondUpsell={config.hideSecondUpsell}
      />

      {/* Quiz Modal */}
      <QuizModal
        isOpen={isQuizModalOpen}
        onClose={() => setIsQuizModalOpen(false)}
        onStartCure={(quantity) => {
          // Pre-select the quantity and close modal, scroll to pricing
          setSelectedQuantity(quantity);
          setIsQuizModalOpen(false);
          setTimeout(() => {
            const el = document.getElementById('hero-pricing');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }}
        onShowResults={(profileSlug, tags, labels, cureQuantity) => {
          // Set quiz results state to show ProfileResultsBanner at top of PDP
          setQuizResultProfileId(profileSlug);
          setQuizResultTags(tags);
          setQuizResultLabels(labels);
          setQuizResultCureQuantity(cureQuantity);
          setSelectedQuantity(cureQuantity);
          // Scroll to top to see the banner expand
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* FAQ Micro-Modal - Appears when FAQ section is in view, hidden for quiz users */}
      {/* Positioned directly below announcement bar - mobile: ~38px, desktop: ~48px */}
      {showFaqMicroModal && quizTags.length === 0 && (
        <div 
          className="fixed left-0 right-0 z-[55] animate-[slideDown_0.3s_ease-out]"
          style={{ 
            top: typeof window !== 'undefined' 
              ? `${document.querySelector('[data-announcement-bar]')?.getBoundingClientRect().height || 44}px`
              : '44px'
          }}
        >
          <div className="bg-background border-b border-border shadow-sm">
            <div className="container px-4 py-3 flex items-center justify-between">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="text-sm text-muted-foreground">Not sure which cure is right for you?</span>
                <button
                  onClick={() => {
                    setIsQuizModalOpen(true);
                    setShowFaqMicroModal(false);
                    setFaqMicroModalDismissed(true);
                  }}
                  className="text-sm font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors"
                >
                  Find out in 60 seconds →
                </button>
              </div>
              <button
                onClick={() => {
                  setShowFaqMicroModal(false);
                  setFaqMicroModalDismissed(true);
                }}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Exit Recovery Modal */}
      {showExitRecoveryModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowExitRecoveryModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-background rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 animate-[fadeSlideIn_0.3s_ease-out]">
            <button
              onClick={() => setShowExitRecoveryModal(false)}
              className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            
            {!exitRecoverySubmitted ? (
              <>
                <h3 className="text-xl font-semibold mb-2">Not sure yet?</h3>
                <p className="text-muted-foreground mb-4">
                 Get an extra <span className="font-semibold text-foreground">10% off</span>, only available right now, in exchange for your email.
                </p>
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={exitRecoveryEmail}
                    onChange={(e) => setExitRecoveryEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                  <button
                    onClick={() => {
                      if (exitRecoveryEmail) {
                        // TODO: Save email to backend
                        setExitRecoverySubmitted(true);
                      }
                    }}
                    disabled={!exitRecoveryEmail}
                    className="w-full text-white font-semibold py-3 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: isEventPage && eventColors ? eventColors.primary : '#007AFF'
                    }}
                  >
                    Unlock 10% Off
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  No spam, just your exclusive discount code.
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Check your inbox!</h3>
                <p className="text-muted-foreground">
                  Your exclusive 10% discount code is on its way.
                </p>
                <button
                  onClick={() => setShowExitRecoveryModal(false)}
                  className="mt-4 text-sm text-primary hover:underline"
                >
                  Continue shopping
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
